import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Users,
  Pill,
  ReceiptText,
  DollarSign,
  Download,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  CalendarDays,
  Banknote,
  PackageOpen,
  Printer,
  ChevronDown,
  ChevronRight,
  Check,
  ShieldCheck,
  Search,
  Upload,
  X as XIcon,
  UserMinus,
} from "lucide-react";
import { exportOdbPaymentPdf, exportNihbPaymentPdf } from "@/lib/payment-pdf";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  LabelList,
} from "recharts";
// Tabs imports removed — Product Scope toggle now lives inside the Filters dialog.
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { exportToCsv } from "@/lib/csv";
import { uploadBirthdayPdf, fetchPatientPreferences, savePatientPreference, fetchCyclePatientTags, saveCyclePatientTag } from "@/lib/api";
import { exportPatientCohortPdf } from "@/lib/patient-cohort-pdf";
import { JarvisAssistant } from "@/components/JarvisAssistant";
import { recomputeAnalysis, canonicalDrugName, hcvRebateForRow, aggregateHcvRebate, aggregateNihbDrugSpread, hcvConsultantsForRow, aggregateConsultantFees, aggregateConsultantFeesPerCycle, buildPatientPayerMap, aggregateMilestoneIncentives, HCV_MILESTONE_PER_CLIENT, hcvDrugKey, HCV_REBATE_PCT, HCV_COST_PER_UNIT, HCV_DRUG_KEYWORDS, classifyPayer, unitsPerClaimForPayer, combinedAmountForRow, isPspRow, pspAmountForRow, isHcvDrug } from "@/lib/hcv";
import GenericRebatesCard from "@/components/GenericRebatesCard";
import {
  DISPENSING_FEE,
  COPAY_WAIVED_PER_DISPENSING,
  ODB_MONTH_SHORT,
  formatCurrency,
  clampIso,
  todayIso,
  lastDayOfMonth,
  rebateAmountFor,
  addMonths,
  isoFromYmd,
  monthShort,
  odbCyclesInRange,
  nihbCyclesInRange,
  formatPayDate,
  formatShortDate,
} from "@/lib/dashboard-utils";
import { CollapsibleBlock } from "@/components/dashboard/CollapsibleBlock";
import { RangePicker } from "@/components/dashboard/RangePicker";
import { PLPeriodBlock, OperatingCostEditor } from "@/components/dashboard/ProfitLossBlock";
import { KpiCard, KpiBreakdownList, KpiTransactionsList, PatientRxDetail, DailyClaimsDetail } from "@/components/dashboard/KpiPrimitives";
import { RevenueBlock } from "@/components/dashboard/RevenueBlock";
import { TooltipBox, CurrencyTooltip, DrugTick } from "@/components/dashboard/ChartTooltips";
import { PatientVolumeChartCard } from "@/components/dashboard/PatientVolumeChartCard";

// Compact download button that sits next to a cohort's count on the
// Patient Search block. Uses the same tinted-outline treatment as the
// mockup so it feels like a subtle affordance, not a call-to-action —
// stopPropagation prevents the parent <summary> row from expanding
// when the user taps the button.
function CohortDownloadButton({ label, tint, onClick, testId }) {
  const palette = COHORT_TINTS[tint] || COHORT_TINTS.default;
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.();
      }}
      className={`inline-flex items-center justify-center h-6 w-6 rounded-md border transition-colors ${palette.border} ${palette.bg} ${palette.text} ${palette.hover}`}
      title={`Download ${label} PDF`}
      aria-label={`Download ${label} PDF`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    </button>
  );
}

const COHORT_TINTS = {
  lost:        { border: "border-[#D36C56]/40", bg: "bg-white", text: "text-[#8B3A24]", hover: "hover:bg-[#F7EBE2]" },
  discharged:  { border: "border-[#B48A55]/40", bg: "bg-white", text: "text-[#7A5A2E]", hover: "hover:bg-[#F0E4CB]" },
  reactivated: { border: "border-[#2B5E4A]/40", bg: "bg-white", text: "text-[#1B3B2E]", hover: "hover:bg-[#DCEBE2]" },
  new:         { border: "border-[#C7DBCF]",   bg: "bg-white", text: "text-[#1B3B2E]", hover: "hover:bg-[#F0F4F2]" },
  active:      { border: "border-[#1B3B2E]/50", bg: "bg-white", text: "text-[#1B3B2E]", hover: "hover:bg-[#F0F4F2]" },
  default:     { border: "border-[#C7DBCF]",   bg: "bg-white", text: "text-[#1B3B2E]", hover: "hover:bg-[#F0F4F2]" },
};

export default function DashboardView({ analysis: originalAnalysis, onReset }) {
  const [viewMode, setViewMode] = useState("non-hcv"); // "non-hcv" | "hcv"
  const analysis = useMemo(
    () => recomputeAnalysis(originalAnalysis, viewMode),
    [originalAnalysis, viewMode]
  );

  const {
    filename,
    date_range_start,
    date_range_end,
    num_days,
    total_dispensings,
    total_fees,
    unique_patients: analysisUniquePatients,
    avg_daily_fees,
    monthly_revenue_est,
    annual_revenue_est,
    per_day,
    zero_fee_entries,
    drug_breakdown_per_day,
    drug_breakdown_total,
    per_month_summary,
    transactions,
  } = analysis;

  // Stable Patient → "ODB" | "NIHB" map built from the patient's COMPLETE
  // HCV history (the full transactions array, before any date filtering).
  // Used by every consultant fee aggregation so a patient's payer class is
  // identical whether you're viewing one ODB cycle or all of them — eliminates
  // grouped vs single-cycle drift caused by per-slice avg-plan1 classification.
  const patientPayerMap = useMemo(
    () => buildPatientPayerMap(transactions),
    [transactions]
  );

  const isMultiDay = num_days > 1;
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedDate, setSelectedDate] = useState(
    isMultiDay ? "all" : per_day[0]?.service_date
  );
  const [drillDown, setDrillDown] = useState(null);
  // Patient drill — set of `service_date` strings whose Rx group is open.
  // Each date group collapses by default so a long course (e.g. 12 weekly
  // EPCLUSA fills) renders as a tappable per-day summary first.
  const [drillExpandedDates, setDrillExpandedDates] = useState(() => new Set());
  // Patient drill — set of `rx_id`s whose per-row financial breakdown is
  // open (markup, plan 1 / plan 2 paid, total paid).
  const [drillExpandedRxIds, setDrillExpandedRxIds] = useState(() => new Set());
  // Top Line Sales drill-down — null | "plan1" | "plan2" | "combined" | "gross-profit".
  // Opens a dialog listing every row contributing to the selected card so
  // the user can validate where the numbers come from.
  const [topLineDrill, setTopLineDrill] = useState(null);
  // For the Gross Profit drill view: which patient card is currently expanded
  // to show all of their individual claim rows. Resets when the dialog closes.
  const [gpExpandedPatient, setGpExpandedPatient] = useState(null);
  // ODB cycle drill-down — null OR the full cycle object so the dialog can
  // list every dispensing that contributed to the deposit for that cycle.
  const [odbDrill, setOdbDrill] = useState(null);
  // Which patient is currently expanded inside the ODB drill dialog.
  // Resets when the dialog closes.
  const [odbExpandedPatient, setOdbExpandedPatient] = useState(null);
  // How to sort the patient list inside the ODB / NIHB audit modal.
  // "total"  = highest deposit contribution first (default; matches
  //            the reconciliation "biggest cheque first" intuition)
  // "alpha"  = alphabetical by surname — auditor-friendly for
  //            cross-referencing a signed patient roster
  const [odbSortMode, setOdbSortMode] = useState("total");
  // Top Line drill — which patient row is currently expanded to show
  // its individual dispensings. null = all collapsed (overview mode).
  const [topLineExpandedPatient, setTopLineExpandedPatient] = useState(null);
  // PSP Reimbursement drill — which patient row is expanded in the PSP
  // section (mirrors the Top Line drill UX).
  const [pspExpandedPatient, setPspExpandedPatient] = useState(null);
  const [expandedKpi, setExpandedKpi] = useState(null);
  // Per-Day breakdown — tapping a row expands inline to show every patient
  // and their claims for that day (mirrors the patient drill behaviour).
  const [dailyExpandedDate, setDailyExpandedDate] = useState(null);
  // Unique Patients KPI — which patient row inside the expanded list is
  // currently drilled open to show its prescription-level detail. Resets
  // whenever the KPI card collapses.
  const [patientsKpiExpandedRow, setPatientsKpiExpandedRow] = useState(null);
  // Consultant fee drill-down — null OR { type: 'medical' | 'sales' }. Opens
  // a dialog listing every client and the consultant fee attributed to them
  // in the currently visible period (for audit/reconciliation).
  const [consultantDrill, setConsultantDrill] = useState(null);
  // Cost drill-down — null OR { type: 'op-cost' | 'pharmacy-ops' }. Opens a
  // simple per-HCV-patient list explaining how the flat per-patient charge
  // rolls up to the visible window total.
  const [costDrill, setCostDrill] = useState(null);
  // Operating-cost inline category breakdown — when true, the per-category
  // sub-rows (Hotel / Cepheid / Flights / …) render directly below the
  // Operating cost line in the HCV P&L block.
  const [opCostBreakdownOpen, setOpCostBreakdownOpen] = useState(false);
  // Bumped once when the analysis becomes available — JarvisAssistant listens
  // and triggers its auto-greet voice flow on each new signal value.
  // Note: auto-greet on report load is DISABLED per user request — JARVIS
  // now stays minimized as the floating mic button until the user taps it.
  // The state is preserved so we can still trigger a programmatic open from
  // a CTA elsewhere without re-wiring the prop contract.
  // eslint-disable-next-line no-unused-vars
  const [jarvisAutoOpenSignal, setJarvisAutoOpenSignal] = useState(0);
  // JARVIS is hidden by default — the user reveals it with a hidden gesture
  // (4 taps on the Charts section within 4s). Once revealed in a session
  // the floating mic FAB stays visible until the next reload. iOS Safari
  // can eat rapid clicks (double-tap-to-zoom), so we bind to both
  // pointerdown (fires on touch + mouse) AND onClick, deduped by a
  // 200ms-window timestamp so a single tap only counts once.
  const [jarvisRevealed, setJarvisRevealed] = useState(false);
  const chartsTapCountRef = useRef(0);
  const chartsTapResetRef = useRef(null);
  const chartsLastTapTsRef = useRef(0);
  const handleChartsTap = () => {
    const now = Date.now();
    if (now - chartsLastTapTsRef.current < 200) return; // dedupe pointer+click
    chartsLastTapTsRef.current = now;
    chartsTapCountRef.current += 1;
    if (chartsTapResetRef.current) clearTimeout(chartsTapResetRef.current);
    chartsTapResetRef.current = setTimeout(() => {
      chartsTapCountRef.current = 0;
    }, 4000);
    if (chartsTapCountRef.current >= 4) {
      chartsTapCountRef.current = 0;
      if (chartsTapResetRef.current) {
        clearTimeout(chartsTapResetRef.current);
        chartsTapResetRef.current = null;
      }
      toast.success("JARVIS engaged, sir.");
      setJarvisRevealed(true);
      setJarvisAutoOpenSignal((s) => s + 1);
    }
  };
  // Captures dashboard state changes made during the current JARVIS intent
  // dispatch so report_metric can read the most-recently-applied range/view
  // synchronously (React state from earlier actions in the same loop won't
  // have propagated yet).
  const jarvisIntentRef = useRef({ start: null, end: null, viewMode: null });
  // Refs mirroring the latest computed cycle deposits + raw transactions.
  // The JARVIS show_chart handler reads from these refs (not the closure)
  // so a stale onAction callback captured before the dashboard finished
  // loading data never blocks the chart from rendering. This is the
  // canonical fix for the "JARVIS says no cycles even though the cards
  // clearly show 7 cycles" bug.
  const odbCycleDepositsRef = useRef([]);
  const allTransactionsRef = useRef([]);
  // Which patient row is currently expanded in the consultant drill dialog.
  // Key shape: `${patient}||${drugKey}`. Resets when the dialog closes.
  const [consultantExpandedRow, setConsultantExpandedRow] = useState(null);
  // Whether to redact patient names in the PDF export of the consultant
  // drill (replaces each name with "Patient 1", "Patient 2" etc.). Off by
  // default — the user toggles it per export when they're sending the
  // statement outside the pharmacy.
  const [consultantMaskNames, setConsultantMaskNames] = useState(false);
  // Per-patient program override for the ODB/NIHB payment ledgers.
  // Maps { patientName: "420" | "HepCURE" }. When at least one patient
  // has an override, the export can switch its supplier-split (Gilead
  // vs Sentrex) over to a program-split (420 vs HepCURE). Persisted to
  // localStorage so overrides survive PDF re-uploads and page refresh.
  const [patientProgramMap, setPatientProgramMap] = useState(() => {
    try {
      const raw = localStorage.getItem("haven.patientProgram.v1");
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        "haven.patientProgram.v1",
        JSON.stringify(patientProgramMap),
      );
    } catch {
      /* localStorage quota / privacy mode — ignore */
    }
  }, [patientProgramMap]);
  const setPatientProgram = (patient, program) => {
    setPatientProgramMap((prev) => {
      const next = { ...prev };
      if (!program) delete next[patient];
      else next[patient] = program;
      return next;
    });
    // Program lives per-patient (drives PDF export splits + commission
    // math). Sent as its own field so other fields on the doc are
    // preserved by the patch-semantic backend.
    savePatientPreference({ patient, program: program || "" }).catch(() => {});
  };
  const clearAllPatientPrograms = () => setPatientProgramMap({});

  // Cohort PDF download — one entry point handles all six banner rows
  // on the Patient Search block. Returns a click handler pre-bound to
  // the cohort's row set, column set, and human label so the tiny
  // <CohortDownloadButton> next to each count is a thin wrapper.
  // Note: date range + row lists are read INSIDE the arrow function so
  // the closure doesn't trip the temporal-dead-zone on state consts
  // declared further down in this same component body.
  const downloadCohortPdf = (kind) => {
    const rangeStart = visibleDays?.[0]?.service_date || null;
    const rangeEnd = visibleDays?.[visibleDays.length - 1]?.service_date || null;
    const scopeLabel =
      viewMode === "hcv" ? "HCV drugs only" :
      viewMode === "non-hcv" ? "Non-HCV pharmacy only" :
      "All drugs";
    const configs = {
      lost: {
        cohortName: "Lost Patients",
        subtitle: `No dispense in 7 days · ${scopeLabel}`,
        rows: activeLostPatients,
        columns: [
          { header: "Patient", accessor: "patient", halign: "left", width: 84 },
          { header: "Last dispense", accessor: "lastDate", format: "date", halign: "left", width: 40 },
          { header: "Days since", accessor: "daysSince", halign: "center", width: 28 },
          { header: "Lifetime days", accessor: "historyDays", halign: "center", width: 30 },
        ],
      },
      discharged: {
        cohortName: "Discharged Patients",
        subtitle: `One-off scripts · fewer than 7 lifetime dispense days · ${scopeLabel}`,
        rows: dischargedPatients,
        columns: [
          { header: "Patient", accessor: "patient", halign: "left", width: 84 },
          { header: "Last dispense", accessor: "lastDate", format: "date", halign: "left", width: 40 },
          { header: "Days since", accessor: "daysSince", halign: "center", width: 28 },
          { header: "Lifetime days", accessor: "historyDays", halign: "center", width: 30 },
        ],
      },
      reactivated: {
        cohortName: "Reactivated Patients",
        subtitle: `Returned after a 7+ day gap · ${scopeLabel}`,
        rows: reactivatedPatients,
        columns: [
          { header: "Patient", accessor: "patient", halign: "left", width: 76 },
          { header: "Last dispense", accessor: "lastDate", format: "date", halign: "left", width: 36 },
          { header: "Previous dispense", accessor: "prevDate", format: "date", halign: "left", width: 40 },
          { header: "Gap (days)", accessor: "gapDays", halign: "center", width: 30 },
        ],
      },
      new: {
        cohortName: "New Clients",
        subtitle: `First dispense within the visible window · ${scopeLabel}`,
        rows: newClients,
        columns: [
          { header: "Patient", accessor: "patient", halign: "left", width: 84 },
          { header: "First dispense", accessor: "firstDate", format: "date", halign: "left", width: 40 },
          { header: "Days since first", accessor: "daysSinceFirst", halign: "center", width: 30 },
          { header: "Dispense days", accessor: "dispenseDays", halign: "center", width: 28 },
        ],
      },
      active: {
        cohortName: viewMode === "hcv" ? "HCV Patients" : "Active Patients",
        subtitle:
          viewMode === "hcv"
            ? "Patients on an HCV course · every patient with an HCV row"
            : `Dispensed within last 7 days · ${scopeLabel}`,
        rows: (() => {
          // Cohort scope — Non-HCV excludes anyone already flagged
          // Lost/Discharged so the "Active" download reflects exactly
          // what the on-screen roster shows.
          const activeCohort =
            viewMode === "hcv"
              ? patientsBreakdown
              : patientsBreakdown.filter((p) => {
                  const excluded = new Set(lostPatients.map((lp) => lp.patient));
                  return !excluded.has(p.patient);
                });

          // Bucket every visible transaction by (patient, service_date)
          // so we can emit one PDF row per dispensing date. Drug names
          // on the same date are joined with ", " (canonicalised so
          // "epclusa 400mg" and "EPCLUSA" don't double-count).
          const byPatient = new Map();
          for (const t of visibleTransactions) {
            if (!t.patient || !t.service_date || !t.drug_name) continue;
            let dm = byPatient.get(t.patient);
            if (!dm) {
              dm = new Map();
              byPatient.set(t.patient, dm);
            }
            let ds = dm.get(t.service_date);
            if (!ds) {
              ds = new Set();
              dm.set(t.service_date, ds);
            }
            ds.add(canonicalDrugName(t.drug_name) || t.drug_name);
          }

          // Alphabetical by patient, then within each patient DESC by
          // date. Fills is patient-level (total prescriptions) so it
          // repeats across all rows for that patient — same value the
          // user sees in the on-screen roster.
          const expanded = [];
          const sortedPatients = [...activeCohort].sort((a, b) =>
            a.patient.localeCompare(b.patient),
          );
          for (const p of sortedPatients) {
            const dm = byPatient.get(p.patient);
            if (!dm) continue;
            const dates = [...dm.entries()].sort((a, b) =>
              b[0].localeCompare(a[0]),
            );
            for (const [date, drugsSet] of dates) {
              expanded.push({
                patient: p.patient,
                service_date: date,
                drugs: [...drugsSet].sort().join(", "),
                count: p.count,
              });
            }
          }
          return expanded;
        })(),
        columns: [
          { header: "Patient", accessor: "patient", halign: "left", width: 56 },
          { header: "Dispensed", accessor: "service_date", format: "date", halign: "left", width: 32 },
          { header: "Medication", accessor: "drugs", halign: "left", width: 74 },
          { header: "Fills", accessor: "count", halign: "center", width: 20 },
        ],
      },
    };
    const cfg = configs[kind];
    if (!cfg) return;
    exportPatientCohortPdf({
      ...cfg,
      rangeStart,
      rangeEnd,
    });
  };
  // / Recovered are NOT stored here anymore — those live in the
  // per-cycle `cyclePatientTags` map below so every cycle card starts
  // blank instead of inheriting a previous cycle's values.
  const [patientPrefs, setPatientPrefs] = useState({}); // { patientName: {} } — kept for future patient-scoped extensions
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await fetchPatientPreferences();
        if (cancelled) return;
        setPatientPrefs({}); // no per-patient audit fields anymore

        // Program reconciliation with the server (same as before —
        // program stays per-patient because it drives export splits).
        const serverProgramMap = {};
        for (const it of items) {
          if (it.program) serverProgramMap[it.patient] = it.program;
        }
        setPatientProgramMap((localMap) => {
          const merged = { ...serverProgramMap };
          const toMigrate = [];
          for (const [pt, prog] of Object.entries(localMap || {})) {
            if (!serverProgramMap[pt] && prog) {
              merged[pt] = prog;
              toMigrate.push({ pt, prog });
            }
          }
          for (const { pt, prog } of toMigrate) {
            savePatientPreference({ patient: pt, program: prog }).catch(() => {});
          }
          return merged;
        });
      } catch {
        // Silent — un-authed or offline; localStorage remains the fallback.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Per-cycle patient audit tags — keyed by `${scope}||${cycle_key}||${patient}`.
  // Type / Bridging / Recovered. Every cycle is independent so a patient
  // tagged in one cycle starts blank in the next. Persisted via
  // /api/cycle-patient-tags.
  const [cyclePatientTags, setCyclePatientTags] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await fetchCyclePatientTags();
        if (cancelled) return;
        const map = {};
        for (const it of items) {
          const k = `${it.scope}||${it.cycle_key}||${it.patient}`;
          map[k] = {
            type: it.type || "",
            bridging: it.bridging || "",
            recovered: it.recovered || "",
          };
        }
        setCyclePatientTags(map);
      } catch {
        // Silent
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Update ONE tag field for ONE (scope, cycle_key, patient) triple.
  // Only sends the changed field so other tags on the doc are preserved.
  const updateCyclePatientTag = (scope, cycleKey, patient, field, value) => {
    const k = `${scope}||${cycleKey}||${patient}`;
    setCyclePatientTags((prev) => {
      const prevEntry = prev[k] || { type: "", bridging: "", recovered: "" };
      const nextEntry = { ...prevEntry, [field]: value };
      saveCyclePatientTag({
        scope,
        cycle_key: cycleKey,
        patient,
        [field]: value,
      }).catch(() => {});
      return { ...prev, [k]: nextEntry };
    });
  };
  // Export options for the ODB / NIHB payment PDFs. `splitMode` controls
  // how the ledger splits into sheets: `supplier` (default — Gilead /
  // Sentrex), or `program` (uses `patientProgramMap` to split 420 vs
  // HepCURE). `hidePatientNames` redacts names to "Patient 1..N".
  // `maskAcqCost` removes the Acquisition cost column entirely.
  const [exportSplitMode, setExportSplitMode] = useState("supplier");
  const [exportHidePatientNames, setExportHidePatientNames] = useState(false);
  const [exportMaskAcqCost, setExportMaskAcqCost] = useState(false);
  const [patientSearch, setPatientSearch] = useState("");
  // Birthday lookup — session-only (not persisted). Once a PDF is parsed
  // we keep the {patient, dob} array in memory until the user clears it
  // or refreshes the page.
  const [birthdayList, setBirthdayList] = useState(null); // null | [{patient, dob}]
  const [birthdayUploading, setBirthdayUploading] = useState(false);
  const [birthdayError, setBirthdayError] = useState("");
  const [birthdayMonth, setBirthdayMonth] = useState(""); // "YYYY-MM" or "" (all visible months)
  // Custom date range filter — overrides month/day filters when active.
  // Shape: { start: "YYYY-MM-DD", end: "YYYY-MM-DD" } | null
  const [customRange, setCustomRange] = useState(null);
  const [rangeDialogOpen, setRangeDialogOpen] = useState(false);

  // Per-month purchasing-rebate map { "YYYY-MM": dollarAmount }. Kept in
  // component state only — fresh sales-report uploads should always start
  // with an empty rebate list (a sales report has no persistent session).
  const [rebates, setRebates] = useState({});
  useEffect(() => {
    // Migration: clean up any stale rebates persisted by an earlier build so
    // users no longer see ghost entries on a fresh sales-report upload.
    try {
      localStorage.removeItem("haven.rebates.v1");
    } catch {
      /* non-fatal */
    }
  }, []);

  // Editable monthly operating cost — persisted per product scope so the
  // user's Non-HCV (default $20,000/month flat) and HCV (default $2,000
  // per patient) numbers don't collide. In HCV scope the value below is
  // a PER-PATIENT rate, multiplied by the count of distinct HCV patients
  // in the visible window. Pencil-icon next to the Operating cost row
  // opens an inline editor.
  const [opCostNonHcv, setOpCostNonHcv] = useState(() => {
    try {
      const v = localStorage.getItem("haven.opcost.nonhcv");
      return v != null ? Number(v) : 25000;
    } catch {
      return 25000;
    }
  });
  const [opCostHcv, setOpCostHcv] = useState(() => {
    try {
      const v = localStorage.getItem("haven.opcost.hcv");
      // Default: $600 per HCV patient. Pharmacy operations and Staffing
      // costs are tracked on separate lines below (see HCV_PHARMACY_OPS_PER_PATIENT
      // and HCV_STAFFING_PER_PATIENT).
      if (v == null) return 600;
      const n = Number(v);
      // Auto-migrate legacy defaults (0 / 1760 / 2000) so existing users
      // land on the corrected $600 figure.
      if (!Number.isFinite(n) || n <= 0 || n === 1760 || n === 2000) return 600;
      return n;
    } catch {
      return 600;
    }
  });
  // Additional pharmacy-operations cost layered on top of the per-patient
  // operating cost above. $350 per HCV patient — fixed for now (not
  // user-editable; promote to state if that ever changes).
  const HCV_PHARMACY_OPS_PER_PATIENT = 350;
  // Staffing cost layered on top of the operating and pharmacy-operations
  // costs above. $500 per HCV patient — also fixed for now.
  const HCV_STAFFING_PER_PATIENT = 500;
  // Per-patient breakdown of the $600 HCV Operating cost. Shown inside the
  // Operating cost drill so the line-item budget is visible alongside the
  // per-patient roll-up. Numbers MUST sum to opCostHcv's default ($600).
  const HCV_OP_COST_BREAKDOWN = [
    { label: "Hotel", amount: 200 },
    { label: "Cepheid", amount: 140 },
    { label: "Flights", amount: 40 },
    { label: "Gas", amount: 35 },
    { label: "Insurance", amount: 35 },
    { label: "Screening", amount: 35 },
    { label: "Maintenance", amount: 35 },
    { label: "Rent", amount: 25 },
    { label: "Accounting", amount: 25 },
    { label: "Technology", amount: 10 },
    { label: "Supplies", amount: 10 },
    { label: "Delivery", amount: 10 },
  ];
  const setMonthlyOperatingCost = (val) => {
    const n = Number.isFinite(+val) ? Math.max(0, +val) : 0;
    if (viewMode === "hcv") {
      setOpCostHcv(n);
      try { localStorage.setItem("haven.opcost.hcv", String(n)); } catch { /* non-fatal */ }
    } else {
      setOpCostNonHcv(n);
      try { localStorage.setItem("haven.opcost.nonhcv", String(n)); } catch { /* non-fatal */ }
    }
  };
  const [opCostEditing, setOpCostEditing] = useState(false);

  // Discover months present in the report (YYYY-MM, most recent first).
  // Declared early so the auto-default useEffect below can read it.
  const monthsAvailable = useMemo(() => {
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ];
    const set = new Set();
    per_day.forEach((d) => set.add(d.service_date.slice(0, 7)));
    return Array.from(set)
      .sort((a, b) => (a < b ? 1 : a > b ? -1 : 0))
      .map((ym) => {
        const [y, m] = ym.split("-");
        return { ym, label: `${monthNames[parseInt(m, 10) - 1]} ${y}` };
      });
  }, [per_day]);

  const isMultiMonth = monthsAvailable.length > 1;

  // When the user toggles between HCV and Non-HCV, clear month/day filters
  // and any open drills so the new scope renders cleanly. NOTE: we
  // intentionally do NOT reset customRange here — JARVIS often issues
  // set_date_range + set_view_mode together, and clobbering the range
  // would defeat the user's request. If the user wants the full visible
  // window back, the Reset pill in the header does that explicitly.
  useEffect(() => {
    setSelectedMonth("all");
    setSelectedDate("all");
    setDrillDown(null);
    setExpandedKpi(null);
  }, [viewMode]);
  // drillDown shape: { patient: string, service_date: "YYYY-MM-DD", pretty_date: string }

  // Transactions filtered by the currently-visible scope (custom range > month + day)
  const visibleTransactions = useMemo(() => {
    if (!transactions) return [];
    return transactions.filter((t) => {
      if (customRange) {
        if (t.service_date < customRange.start) return false;
        if (t.service_date > customRange.end) return false;
        return true;
      }
      if (selectedMonth !== "all" && !t.service_date.startsWith(selectedMonth)) {
        return false;
      }
      if (selectedDate !== "all" && t.service_date !== selectedDate) {
        return false;
      }
      return true;
    });
  }, [transactions, selectedMonth, selectedDate, customRange]);

  // Same date-range filter applied to the ORIGINAL (unfiltered) transactions —
  // bypasses the HCV/Non-HCV view filter so the PSP Reimbursement section
  // can surface HCV+PSP rows even while the user is in the Non-HCV view
  // (the default). PSP is a separate revenue stream and should be visible
  // in either scope.
  const allVisibleTransactions = useMemo(() => {
    const src = originalAnalysis?.transactions || [];
    return src.filter((t) => {
      if (customRange) {
        if (t.service_date < customRange.start) return false;
        if (t.service_date > customRange.end) return false;
        return true;
      }
      if (selectedMonth !== "all" && !t.service_date.startsWith(selectedMonth)) {
        return false;
      }
      if (selectedDate !== "all" && t.service_date !== selectedDate) {
        return false;
      }
      return true;
    });
  }, [originalAnalysis, selectedMonth, selectedDate, customRange]);

  // HCV one-fee-per-course safety rule:
  //   Pharmacy is paid a SINGLE dispensing fee for the entire HCV treatment
  //   course of any one patient. The source PDF typically shows this with
  //   only one non-zero fee row per HCV patient, but if the report happens
  //   to surface multiple fee rows per HCV patient (data error, mid-course
  //   adjustment, etc.) we'd over-count revenue. So for HCV rows only,
  //   collapse each patient's fees to a single row (the largest) and zero
  //   the rest. Non-HCV rows are untouched.
  const visibleTransactionsForFees = useMemo(() => {
    if (viewMode !== "hcv") return visibleTransactions;
    // Find max fee per patient across their HCV rows.
    const maxFee = new Map();
    for (const t of visibleTransactions) {
      const cur = maxFee.get(t.patient) || 0;
      if ((t.fees || 0) > cur) maxFee.set(t.patient, t.fees || 0);
    }
    const usedPatient = new Set();
    return visibleTransactions.map((t) => {
      const top = maxFee.get(t.patient) || 0;
      if (top === 0) return t; // no fee to dedup
      if (!usedPatient.has(t.patient) && (t.fees || 0) === top) {
        usedPatient.add(t.patient);
        return t;
      }
      return { ...t, fees: 0 };
    });
  }, [visibleTransactions, viewMode]);

  // Distinct HCV patient count in the visible window — used as the multiplier
  // for the HCV per-patient operating cost (default $2,000 per HCV patient).
  // In Non-HCV view this is unused.
  const hcvPatientCountInWindow = useMemo(() => {
    if (viewMode !== "hcv") return 0;
    const set = new Set();
    for (const t of visibleTransactionsForFees) {
      if (t.patient) set.add(t.patient);
    }
    return set.size;
  }, [visibleTransactionsForFees, viewMode]);

  // Effective monthly operating cost shown across the dashboard.
  //   • Non-HCV → flat editable $/mo (default $20,000)
  //   • HCV     → opCostHcv (per-patient rate, default $2,000) × distinct
  //               HCV patients in the visible window. Because HCV is a
  //               one-time-per-patient cost the consumers below skip the
  //               days-in-window proration that Non-HCV uses.
  const monthlyOperatingCost = viewMode === "hcv"
    ? opCostHcv * hcvPatientCountInWindow
    : opCostNonHcv;

  // Per-row reconciliation sanity check for the per-day breakdown banner.
  //
  // Identity: plan1_paid (submitted total) = drug_cost + markup + gross_fees
  // Any row whose plan1_paid is more than $1 SHORT of `markup + gross_fees`
  // implies negative implied drug cost — a red flag for parser drift,
  // reversal/credit rows, or unusual compound scripts. Surface them so the
  // auditor can drill in immediately instead of quietly clamping to $0 in
  // the drug-cost derivation.
  const reconciliationFlags = useMemo(() => {
    const byDate = new Map();
    for (const t of visibleTransactions) {
      const p1 = (t.plan1_paid || 0) + (t.plan2_paid || 0);
      const mk = t.markup || 0;
      const f = t.fees || 0;
      // Zero-fee / zero-plan rows aren't part of the reconciliation.
      if (p1 <= 0 && f <= 0 && mk <= 0) continue;
      const derivedDrugCost = p1 - mk - f;
      if (derivedDrugCost < -1) {
        const list = byDate.get(t.service_date) || [];
        list.push({
          patient: t.patient,
          drug: t.drug_name,
          plan_paid: p1,
          markup: mk,
          fees: f,
          shortfall: +Math.abs(derivedDrugCost).toFixed(2),
        });
        byDate.set(t.service_date, list);
      }
    }
    return [...byDate.entries()]
      .map(([date, items]) => ({ date, items }))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [visibleTransactions]);

  // ODB cycles that cover the currently-visible HCV claims. Used by all
  // consultant fee aggregations so the NIHB $100/bottle cap is applied
  // PER CYCLE (matching the per-deposit audit reality the user expects)
  // rather than globally across the visible window.
  const visibleConsultantCycles = useMemo(() => {
    if (!visibleTransactionsForFees.length) return [];
    let earliest = null;
    let latest = null;
    for (const t of visibleTransactionsForFees) {
      if (!t.service_date) continue;
      if (!earliest || t.service_date < earliest) earliest = t.service_date;
      if (!latest || t.service_date > latest) latest = t.service_date;
    }
    if (!earliest || !latest) return [];
    // Widen by ~30 days on either side so cycles whose deposit pays just
    // outside the visible window (but contain in-window dispensings) are
    // still resolved correctly.
    const widen = (iso, days) => {
      const d = new Date(`${iso}T00:00:00Z`);
      d.setUTCDate(d.getUTCDate() + days);
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
    };
    return odbCyclesInRange(widen(earliest, -30), widen(latest, 30));
  }, [visibleTransactionsForFees]);

  // Aggregations for the expandable KPI cards
  const patientsBreakdown = useMemo(() => {
    // First pass — compute the total unique dispense-day count PER
    // PATIENT across the ENTIRE unfiltered ORIGINAL transactions array
    // (regardless of current viewMode + date filters). Surfaces "this
    // person only has 1 dispense in their whole history" vs. "this
    // person has 50 dispense days" — information that's lost when we
    // scope to a narrow date filter or HCV/Non-HCV view. Also feeds
    // the adherence and annual-revenue projections below so those
    // reflect the patient's true lifetime cadence and value.
    const sourceAllRaw = originalAnalysis?.transactions || [];
    // Scope the "history" maps to the same drug slice the user is viewing.
    // In HCV mode, a patient's "last dispense date" / "meds" / "history days"
    // should reflect ONLY their HCV rows — otherwise an HCV patient who
    // happens to be on a bunch of non-HCV maintenance drugs looks like a
    // "62 days · Daily · 4 meds" pharmacy patient rather than an HCV
    // patient with a 3-fill course. Filtering here also means patients
    // whose ONLY HCV row was zero-fee still show up correctly.
    const sourceAll = viewMode === "hcv"
      ? sourceAllRaw.filter((t) => isHcvDrug(t.drug_name))
      : sourceAllRaw;
    const totalHistoryDaysByPatient = new Map(); // patient -> Set<serviceDate>
    const historyStatsByPatient = new Map();     // patient -> { fees, markup, hcvRebate, paidCount, zero }
    const firstDispenseByPatient = new Map();    // patient -> earliest service_date ISO
    // Per-patient per-date net-fee map ("patient|YYYY-MM-DD" -> $).
    // Used by the recent-compliance annualization below.
    const feesByPatientDate = new Map();
    // Set of DISTINCT drug names dispensed WITH a fee on the patient's
    // MOST-RECENT dispense date. This is the exact "how many meds did
    // we bill on their last visit" number — no windowing, no lifetime
    // union. If the patient filled 3 meds with a fee on their last
    // visit, this is 3.
    const paidDrugsByPatient = new Map(); // patient -> Set<drug>
    // Companion set: EVERY drug dispensed on that same LAST date. Used
    // to derive the zero-fee-only med count (unpaid = all − paid).
    const allDrugsByPatient = new Map(); // patient -> Set<drug>
    // Track each patient's latest service_date so the second pass can
    // filter rows to that exact date only.
    const lastDispenseByPatient = new Map(); // patient -> latest ISO
    for (const t of sourceAll) {
      if (!t.patient || !t.service_date) continue;
      const prev = lastDispenseByPatient.get(t.patient);
      if (!prev || t.service_date > prev) lastDispenseByPatient.set(t.patient, t.service_date);
    }
    for (const t of sourceAll) {
      if (!t.patient || !t.service_date) continue;
      let s = totalHistoryDaysByPatient.get(t.patient);
      if (!s) {
        s = new Set();
        totalHistoryDaysByPatient.set(t.patient, s);
      }
      s.add(t.service_date);
      // Per-date net-fee accumulation per patient. Used by the
      // recent-compliance annualization helper below to sum fees
      // within a rolling recent window.
      const feeKey = `${t.patient}|${t.service_date}`;
      const rowNetFee = Math.max(0, (t.fees || 0) - ((t.fees || 0) > 0 ? COPAY_WAIVED_PER_DISPENSING : 0));
      feesByPatientDate.set(feeKey, (feesByPatientDate.get(feeKey) || 0) + rowNetFee);
      // Track the patient's first-ever dispense date so adherence
      // calculations can use their TENURE-ADJUSTED denominator (they
      // shouldn't be penalized for days before they became a client).
      const cur = firstDispenseByPatient.get(t.patient);
      if (!cur || t.service_date < cur) firstDispenseByPatient.set(t.patient, t.service_date);
      // Fee/markup stats accumulate across the entire history.
      let stats = historyStatsByPatient.get(t.patient);
      if (!stats) {
        stats = { fees: 0, markup: 0, hcvRebate: 0, paidCount: 0, zero: 0 };
        historyStatsByPatient.set(t.patient, stats);
      }
      stats.fees += t.fees || 0;
      stats.markup += t.markup || 0;
      stats.hcvRebate += hcvRebateForRow(t);
      if ((t.fees || 0) === 0) stats.zero += 1;
      else stats.paidCount += 1;
    }
    // SECOND pass: populate `paidDrugsByPatient` and `allDrugsByPatient`
    // by keeping ONLY rows whose service_date === the patient's most-
    // recent dispense date. Exactly "meds billed on their last visit"
    // — no lifetime union, no rolling window.
    for (const t of sourceAll) {
      if (!t.patient || !t.service_date || !t.drug_name) continue;
      const last = lastDispenseByPatient.get(t.patient);
      if (!last || t.service_date !== last) continue;
      let ad = allDrugsByPatient.get(t.patient);
      if (!ad) {
        ad = new Set();
        allDrugsByPatient.set(t.patient, ad);
      }
      ad.add(t.drug_name);
      if ((t.fees || 0) > 0) {
        let ds = paidDrugsByPatient.get(t.patient);
        if (!ds) {
          ds = new Set();
          paidDrugsByPatient.set(t.patient, ds);
        }
        ds.add(t.drug_name);
      }
    }
    // Calendar span of the ENTIRE original report (first dispense date
    // → last dispense date). Same for every patient — anchors the
    // annual projection on the report's actual duration.
    let historySpanDays = 0;
    let historyMinIso = "";
    let historyMaxIso = "";
    for (const t of sourceAll) {
      if (!t.service_date) continue;
      if (!historyMinIso || t.service_date < historyMinIso) historyMinIso = t.service_date;
      if (!historyMaxIso || t.service_date > historyMaxIso) historyMaxIso = t.service_date;
    }
    if (historyMinIso && historyMaxIso) {
      const a = new Date(`${historyMinIso}T00:00:00Z`);
      const b = new Date(`${historyMaxIso}T00:00:00Z`);
      historySpanDays = Math.round((b - a) / (1000 * 60 * 60 * 24)) + 1;
    }
    const historyTotalDays = new Set(
      sourceAll.map((t) => t.service_date).filter(Boolean)
    ).size || 1;
    const historySpanForProjection = Math.max(1, historySpanDays || historyTotalDays);

    const map = new Map();
    const allDays = new Set();
    for (const t of visibleTransactionsForFees) {
      allDays.add(t.service_date);
      const entry = map.get(t.patient) || {
        patient: t.patient,
        count: 0,
        fees: 0,
        markup: 0,
        // HCV-only: sum of auto-computed rebate ($drug cost × 3% / 0.5%) per
        // row. For Non-HCV rows this stays 0 — rebates are still apportioned
        // from the manual uploads downstream.
        hcvRebate: 0,
        zero: 0,
        zeroFeeWithMarkup: 0,
        days: new Set(),
      };
      entry.count += 1;
      entry.fees += t.fees;
      entry.markup += t.markup || 0;
      entry.hcvRebate += hcvRebateForRow(t);
      if (t.fees === 0) {
        entry.zero += 1;
        if ((t.markup || 0) > 0) entry.zeroFeeWithMarkup += 1;
      }
      entry.days.add(t.service_date);
      map.set(t.patient, entry);
    }
    const totalDays = Math.max(1, allDays.size);
    // Sorted list of pharmacy-active days in the visible range — used
    // to compute per-patient tenure-adjusted denominators via binary
    // search below. A patient who joined mid-window shouldn't be
    // penalized against days the pharmacy dispensed before they were
    // ever a client.
    const sortedAllDays = Array.from(allDays).sort();
    const totalDispensings = visibleTransactionsForFees.length || 1;
    // Total zero-fee items across the visible scope — excluded from revenue
    // apportionment so each patient's share is based on paid dispensings only.
    const allZeroCount = visibleTransactionsForFees.reduce(
      (s, t) => s + (t.fees === 0 ? 1 : 0),
      0
    );
    // Calendar span of the visible range (e.g. Jan 1 -> Jun 17 = 168 days).
    // Using active-days as the denominator wildly inflated projections for
    // patients who came on consecutive days inside a longer report window.
    let spanDays = 0;
    if (allDays.size > 0) {
      const sortedAll = Array.from(allDays).sort();
      const aFirst = new Date(sortedAll[0]);
      const aLast = new Date(sortedAll[sortedAll.length - 1]);
      spanDays = Math.round((aLast - aFirst) / (1000 * 60 * 60 * 24)) + 1;
    }
    const spanForProjection = Math.max(1, spanDays || totalDays);
    // Pool-level extras to apportion per patient (sum across visible days only).
    const perDayByDate = new Map(per_day.map((d) => [d.service_date, d]));
    let totalMarkupVisible = 0;
    for (const d of allDays) {
      const row = perDayByDate.get(d);
      if (row) totalMarkupVisible += row.total_markup || 0;
    }
    const visibleMonthSet = new Set();
    for (const d of allDays) visibleMonthSet.add(d.slice(0, 7));
    let totalRebatesVisible = 0;
    for (const ym of visibleMonthSet) {
      totalRebatesVisible += rebateAmountFor(rebates, ym, viewMode);
    }

    const classifyCadence = (avgGap) => {
      if (!avgGap || avgGap <= 1.5) return "Daily";
      if (avgGap <= 4) return "Most days";
      if (avgGap <= 9) return "Weekly";
      if (avgGap <= 17) return "Bi-weekly";
      if (avgGap <= 35) return "Monthly";
      return "Occasional";
    };

    return Array.from(map.values())
      .map((p) => {
        const visitDays = p.days.size;
        // Average gap between consecutive dispense days (in days)
        const sortedDays = Array.from(p.days).sort();
        let avgGap = 0;
        if (sortedDays.length > 1) {
          let totalGap = 0;
          for (let i = 1; i < sortedDays.length; i++) {
            const a = new Date(sortedDays[i - 1]);
            const b = new Date(sortedDays[i]);
            totalGap += (b - a) / (1000 * 60 * 60 * 24);
          }
          avgGap = totalGap / (sortedDays.length - 1);
        }
        // Only paid dispensings count as "revenue dispensings" — zero-fee items
        // (compounds with no separate fee, supplements without billing, etc.)
        // are tracked separately, but their MARKUP is still counted as revenue.
        const paidCount = p.count - p.zero;
        // Net fees actually collected = submitted fees − $2 patient co-pay
        // waived on each paid dispensing.
        const netFees = +(
          p.fees - paidCount * COPAY_WAIVED_PER_DISPENSING
        ).toFixed(2);
        // Actual markup attributable to this patient (sum of t.markup across all
        // their rows — paid AND zero-fee). Falls back to the dispensing-share
        // apportionment if the report didn't carry per-row markup.
        const markupShare = +(p.markup || 0).toFixed(2);
        // Rebates:
        //   HCV  → auto-computed per row from drug cost × manufacturer % (3% /
        //          0.5%). Exact, not apportioned.
        //   Non-HCV → uploaded monthly cheques, apportioned by paid-dispensing
        //          share across the visible window.
        let rebateShare;
        if (viewMode === "hcv") {
          rebateShare = +(p.hcvRebate || 0).toFixed(2);
        } else {
          const share =
            (totalDispensings - allZeroCount) > 0
              ? paidCount / (totalDispensings - allZeroCount)
              : 0;
          rebateShare = +(share * totalRebatesVisible).toFixed(2);
        }
        const totalValueVisible = +(
          netFees + markupShare + rebateShare
        ).toFixed(2);
        // Full-history stats for this patient — used to drive the
        // adherence percentage and the annual revenue projection so
        // both reflect the patient's TRUE lifetime cadence + value
        // rather than just what's inside the current filter window.
        const histDays = (totalHistoryDaysByPatient.get(p.patient) || new Set()).size;
        const histStats = historyStatsByPatient.get(p.patient) || {
          fees: 0, markup: 0, hcvRebate: 0, paidCount: 0, zero: 0,
        };
        // Full-history net fees (submitted − $2 co-pay per paid).
        const histNetFees = +(
          histStats.fees - histStats.paidCount * COPAY_WAIVED_PER_DISPENSING
        ).toFixed(2);
        // Full-history rebate: HCV uses exact per-row rebate; Non-HCV
        // apportions total rebates uploaded across all months by the
        // patient's paid-dispensing share. Approximate for Non-HCV
        // but consistent with the visible-window logic above.
        let histRebateShare;
        if (viewMode === "hcv") {
          histRebateShare = +(histStats.hcvRebate || 0).toFixed(2);
        } else {
          // Sum every Non-HCV rebate cheque across every month in the
          // report so the share reflects the patient's lifetime pool.
          const allMonths = new Set(sourceAll.map((t) => t.service_date?.slice(0, 7)).filter(Boolean));
          let totalHistoryRebates = 0;
          for (const ym of allMonths) totalHistoryRebates += rebateAmountFor(rebates, ym, viewMode);
          const totalHistoryPaid = sourceAll.reduce(
            (s, t) => s + ((t.fees || 0) > 0 ? 1 : 0),
            0
          );
          const share = totalHistoryPaid > 0 ? histStats.paidCount / totalHistoryPaid : 0;
          histRebateShare = +(share * totalHistoryRebates).toFixed(2);
        }
        const histTotalValue = +(
          histNetFees + (histStats.markup || 0) + histRebateShare
        ).toFixed(2);
        // Project to annual using the PATIENT'S TENURE span — from
        // their first dispense in the PDF to the report's most recent
        // date, NOT the whole-PDF span. A mid-report joiner (e.g. Ian
        // joined April against a Sep→Jul report) shouldn't have his
        // annual cratered by dividing his lifetime value across the
        // 305 days most of which he wasn't even a client for.
        // HCV exception — pharmacy is paid only ONE dispensing fee
        // per entire course of treatment per client. In HCV scope,
        // annualValue == the patient's actual lifetime course value.
        const firstIsoForAnnual = firstDispenseByPatient.get(p.patient);
        let tenureSpanDays = historySpanForProjection;
        if (firstIsoForAnnual && historyMaxIso) {
          const aStart = new Date(`${firstIsoForAnnual}T00:00:00Z`);
          const aEnd = new Date(`${historyMaxIso}T00:00:00Z`);
          tenureSpanDays = Math.max(
            1,
            Math.round((aEnd - aStart) / (1000 * 60 * 60 * 24)) + 1
          );
        }
        // Recent-compliance annualization — if the patient has been
        // dispensing regularly for the last 3+ weeks (adherence ≥ 60%
        // in a contiguous window ending at their latest dispense),
        // extrapolate annual value from THAT window's rate instead of
        // the full lifetime. Otherwise a patient who was previously
        // non-compliant but has been steady for a month gets a
        // realistic ceiling instead of a lowball anchored to their
        // ancient absent months.
        let recentAnnual = null;
        let recentWindowDays = 0;
        let recentAdherencePct = null;
        let recentVisitDays = 0;
        if (viewMode !== "hcv") {
          const patientDates = Array.from(totalHistoryDaysByPatient.get(p.patient) || []).sort();
          if (patientDates.length >= 2 && historyMaxIso) {
            const latestIso = patientDates[patientDates.length - 1];
            const latestMs = new Date(`${latestIso}T00:00:00Z`).getTime();
            const refMs = new Date(`${historyMaxIso}T00:00:00Z`).getTime();
            const daysSinceLatest = Math.floor((refMs - latestMs) / 86400000);
            // Only consider currently-active patients (last dispense
            // within 7 days of report_max). Others go through the
            // full-history fallback below.
            if (daysSinceLatest <= 7) {
              const ADH_THRESHOLD = 0.6;
              const MIN_WINDOW_DAYS = 21;
              // Walk backwards from the latest date, expanding the
              // window. Track the longest window whose adherence
              // (dispense days ÷ window days) stays ≥ threshold.
              let bestStart = null;
              let bestSpanDays = 0;
              let bestDispenseDays = 0;
              for (let i = patientDates.length - 1; i >= 0; i--) {
                const startMs = new Date(`${patientDates[i]}T00:00:00Z`).getTime();
                const spanDays = Math.floor((latestMs - startMs) / 86400000) + 1;
                const dispenseDaysInWin = patientDates.length - i;
                const adherence = dispenseDaysInWin / spanDays;
                if (adherence >= ADH_THRESHOLD) {
                  bestStart = patientDates[i];
                  bestSpanDays = spanDays;
                  bestDispenseDays = dispenseDaysInWin;
                } else {
                  break;
                }
              }
              if (bestSpanDays >= MIN_WINDOW_DAYS && bestStart) {
                let feesInWindow = 0;
                for (let i = 0; i < patientDates.length; i++) {
                  const d = patientDates[i];
                  if (d < bestStart) continue;
                  feesInWindow += feesByPatientDate.get(`${p.patient}|${d}`) || 0;
                }
                recentAnnual = +((feesInWindow / bestSpanDays) * 365).toFixed(2);
                recentWindowDays = bestSpanDays;
                // Also expose the adherence within THAT window so the
                // patient card reflects "she's been compliant lately"
                // instead of the lifetime 10%.
                recentAdherencePct = Math.round((bestDispenseDays / bestSpanDays) * 100);
                recentVisitDays = bestDispenseDays;
              }
            }
          }
        }
        const annualValue =
          viewMode === "hcv"
            ? histTotalValue
            : recentAnnual != null
              // Prefer the recent-compliance rate when we have a
              // ≥ 21-day stable streak — reflects the patient's
              // current trajectory, not their historical worst.
              ? recentAnnual
              : tenureSpanDays > 0
                ? +((histNetFees / tenureSpanDays) * 365).toFixed(2)
                : 0;
        return {
          patient: p.patient,
          count: p.count,
          paidCount,
          fees: p.fees,           // gross submitted
          netFees,                // after $2 co-pay waived
          copayWaived: +(
            paidCount * COPAY_WAIVED_PER_DISPENSING
          ).toFixed(2),
          zero: p.zero,
          visitDays,
          // Total unique dispense days across the ENTIRE unfiltered
          // history for this patient (across every viewMode + every
          // date). Lets the auditor tell "1 fill ever" from "50 fills
          // across the full report" without changing filters.
          historyDays: (
            // In HCV mode, prefer the frontend-computed HCV-only day count
            // so a patient with 3 HCV fills doesn't display "62 days" from
            // their non-HCV maintenance history. Non-HCV falls back to the
            // authoritative backend count (survives when `transactions` is
            // dropped for large reports).
            viewMode === "hcv"
              ? (totalHistoryDaysByPatient.get(p.patient) || new Set()).size
              : (originalAnalysis?.patient_history_days?.[p.patient] ??
                (totalHistoryDaysByPatient.get(p.patient) || new Set()).size)
          ),
          totalDays,
          // Adherence — TENURE-ADJUSTED so patients who joined mid-
          // window aren't penalized against days that occurred before
          // they became a client of the pharmacy. Denominator = # of
          // pharmacy-active days in the visible range that fall on or
          // AFTER the patient's first-ever dispense date (from the
          // full unfiltered history). Numerator = the patient's own
          // dispense days in the visible range. So Ian starting in
          // April with 70 fills out of ~85 possible days after his
          // first dispense = ~82% adherence rather than 23% against
          // the full 309-day report timeline.
          adherence: (() => {
            // If a recent-compliance window was found (≥ 21 days at
            // ≥ 60% adherence), use ITS adherence — reflects the
            // patient's current trajectory, not their historical worst.
            if (recentAdherencePct != null) return recentAdherencePct;
            const firstIso = firstDispenseByPatient.get(p.patient);
            let denom = totalDays;
            if (firstIso && sortedAllDays.length > 0) {
              // Binary search for insertion index — how many active
              // days precede the patient's first dispense.
              let lo = 0;
              let hi = sortedAllDays.length;
              while (lo < hi) {
                const mid = (lo + hi) >>> 1;
                if (sortedAllDays[mid] < firstIso) lo = mid + 1;
                else hi = mid;
              }
              const tenureDays = sortedAllDays.length - lo;
              denom = Math.max(1, tenureDays);
            }
            return Math.round((visitDays / denom) * 100);
          })(),
          // Denominator that was used for the adherence % above — surfaced
          // so the drill dialog can show "X of Y days seen" with the same
          // tenure-adjusted denominator instead of the flat report total.
          adherenceDenom: (() => {
            // Recent-compliance window drove the %, so its span drives the
            // "X of Y" label too — otherwise the numerator (visit days
            // within window) would look inconsistent against a lifetime
            // denominator.
            if (recentAdherencePct != null) return recentWindowDays;
            const firstIso = firstDispenseByPatient.get(p.patient);
            if (!firstIso || sortedAllDays.length === 0) return totalDays;
            let lo = 0;
            let hi = sortedAllDays.length;
            while (lo < hi) {
              const mid = (lo + hi) >>> 1;
              if (sortedAllDays[mid] < firstIso) lo = mid + 1;
              else hi = mid;
            }
            return Math.max(1, sortedAllDays.length - lo);
          })(),
          // Numerator that matches the adherence % — dispense days within
          // the recent-compliance window when it drove the %, otherwise
          // the standard visit day count.
          adherenceNum: recentAdherencePct != null ? recentVisitDays : visitDays,
          markupShare,
          rebateShare,
          totalValueVisible,
          annualValue,
          // Non-null when the annualValue was computed from a recent-
          // compliance window (≥ 21 days at ≥ 60% adherence) rather
          // than lifetime. Surfaced in the "/yr" tooltip on hover so
          // the user understands why the number reflects the patient's
          // current trajectory instead of their historical worst.
          annualWindowDays: recentAnnual != null ? recentWindowDays : null,
          // Patient's most-recent service_date across the full history.
          // Surfaced in the middle column so the "N meds" number is
          // anchored to a visible date the auditor can verify.
          lastDispenseDate: lastDispenseByPatient.get(p.patient) || "",
          // Drugs billed on the patient's most-recent visit — used by
          // the "Medication" column in the cohort PDF exports. Sorted
          // for stable output; empty string if the patient has never
          // had a paid dispense.
          lastVisitDrugs: (() => {
            const paid = paidDrugsByPatient.get(p.patient) || new Set();
            const all = allDrugsByPatient.get(p.patient) || new Set();
            // Prefer paid drugs; fall back to any dispensed drug so
            // patients whose last visit was all zero-fee still show
            // the medication name.
            const source = paid.size > 0 ? paid : all;
            return [...source].map((d) => canonicalDrugName(d) || d).sort().join(", ");
          })(),
          // Number of DISTINCT medications this patient has EVER
          // collected a dispensing fee for (across the full unfiltered
          // history). This is what the auditor thinks of when asking
          // "how many meds is this patient on that we get a fee for".
          paidDrugCount: (paidDrugsByPatient.get(p.patient) || new Set()).size,
          // Companion count — drugs the patient has been dispensed but
          // NEVER charged a fee for. Surfaced in red so the auditor
          // sees "3 meds bill · 1 med never bills" at a glance.
          zeroFeeDrugCount: (() => {
            const all = allDrugsByPatient.get(p.patient) || new Set();
            const paid = paidDrugsByPatient.get(p.patient) || new Set();
            let n = 0;
            for (const d of all) if (!paid.has(d)) n += 1;
            return n;
          })(),
          avgGap: +avgGap.toFixed(1),
          cadence: classifyCadence(avgGap),
        };
      })
      .sort((a, b) => {
        // Highest estimated annual revenue first — that's what the user
        // surface as the primary signal in the patient list.
        if (b.annualValue !== a.annualValue) return b.annualValue - a.annualValue;
        // Tie-break: visible-range value, then dispensings, then name.
        if (b.totalValueVisible !== a.totalValueVisible)
          return b.totalValueVisible - a.totalValueVisible;
        if (b.count !== a.count) return b.count - a.count;
        return a.patient.localeCompare(b.patient);
      });
  }, [visibleTransactionsForFees, per_day, rebates, viewMode, originalAnalysis]);

  // Top-line sales totals — sum the per-row Plan paid columns across the
  // currently-visible transactions. Lets us see what was billed to each
  // payer regardless of whether the dispensing earned a fee.
  //
  // HCV/PSP RULE: For HCV drugs where Plan 2 > 0, that Plan 2 amount is the
  // patient's deductible being paid by the Patient Support Program (PSP) —
  // NOT additional pharmacy revenue. So `combined` subtracts it from Plan 1
  // for those rows instead of adding it. Non-HCV rows continue to use
  // Plan 1 + Plan 2 as before. (See `combinedAmountForRow` in lib/hcv.js)
  const topLineSales = useMemo(() => {
    let plan1 = 0, plan2 = 0, plan3 = 0, plan4 = 0, totalPaid = 0;
    let combined = 0;
    let pspOffset = 0; // sum of HCV Plan 2 amounts that get subtracted
    let visibleDispensings = 0;
    for (const t of visibleTransactions) {
      plan1 += t.plan1_paid || 0;
      plan2 += t.plan2_paid || 0;
      plan3 += t.plan3_paid || 0;
      plan4 += t.plan4_paid || 0;
      totalPaid += t.total_paid || 0;
      combined += combinedAmountForRow(t);
      if (isPspRow(t)) pspOffset += pspAmountForRow(t);
      visibleDispensings += 1;
    }
    const ourRevenue = patientsBreakdown.reduce(
      (s, p) => s + p.totalValueVisible,
      0
    );
    // NIHB drug-cost spread — cash the pharmacy keeps from billing NIHB
    // at its negotiated ingredient cost (~$25K+/bottle) vs paying the
    // supplier $20K wholesale. patientsBreakdown.totalValueVisible only
    // counts fees + markup + rebate per row, so this spread (which is
    // an HCV-only revenue line) would otherwise be missing from the
    // Gross Profit total. Adding it here makes the Gross Profit card
    // reconcile with the HCV P&L's "Total revenue" below.
    const nihbDrugSpread = aggregateNihbDrugSpread(visibleTransactions);
    // Drug cost (COGS) is what's billed to the plans MINUS the pharmacy's
    // markup and the GROSS dispensing fees that were billed. `combined`
    // is the submitted total (plan1_paid stores the raw billed amount,
    // BEFORE the $2 patient co-pay waiver is deducted by ODB), so we
    // subtract gross fees — not net — to recover the raw ingredient
    // cost. Matches the raw pharmacy report exactly (e.g. Aaron Brown
    // Kadian: 55.08 − 3.62 − 8.83 = 42.63).
    const grossFeesTotal = patientsBreakdown.reduce(
      (s, p) => s + (p.fees || 0),
      0
    );
    const markupTotal = patientsBreakdown.reduce(
      (s, p) => s + (p.markupShare || 0),
      0
    );
    const rebateTotal = patientsBreakdown.reduce(
      (s, p) => s + (p.rebateShare || 0),
      0
    );
    const drugCost = +(combined - markupTotal - grossFeesTotal).toFixed(2);
    const margin =
      totalPaid > 0 ? +((ourRevenue / totalPaid) * 100).toFixed(1) : 0;
    // Calendar span used everywhere else for monthly/annual projection.
    const dates = new Set(visibleTransactions.map((t) => t.service_date));
    let spanDays = 0;
    if (dates.size > 0) {
      const sorted = Array.from(dates).sort();
      const first = new Date(sorted[0]);
      const last = new Date(sorted[sorted.length - 1]);
      spanDays = Math.round((last - first) / (1000 * 60 * 60 * 24)) + 1;
    }
    const monthlyEst = spanDays > 0 ? +((combined / spanDays) * 30).toFixed(2) : 0;
    const annualEst = spanDays > 0 ? +((combined / spanDays) * 365).toFixed(2) : 0;
    return {
      plan1: +plan1.toFixed(2),
      plan2: +plan2.toFixed(2),
      plan3: +plan3.toFixed(2),
      plan4: +plan4.toFixed(2),
      combined: +combined.toFixed(2),
      pspOffset: +pspOffset.toFixed(2),
      totalPaid: +totalPaid.toFixed(2),
      ourRevenue: +(ourRevenue + nihbDrugSpread).toFixed(2),
      drugCost,
      markupTotal: +markupTotal.toFixed(2),
      rebateTotal: +rebateTotal.toFixed(2),
      nihbDrugSpread: +nihbDrugSpread.toFixed(2),
      grossFeesTotal: +grossFeesTotal.toFixed(2),
      margin,
      monthlyEst,
      annualEst,
      spanDays,
      visibleDispensings,
    };
  }, [visibleTransactions, patientsBreakdown]);

  // ODB (Ontario Drug Benefit) cycle deposits — follows the currently
  // selected date-range filter so the deposit amounts reflect ONLY the
  // timeframe shown. Government pays twice a month:
  //   Cycle A (9–22)  → deposit (M+1)-15
  //   Cycle B (23→8)  → deposit (M+1)-30
  // Deposit amount ≈ Plan 1 paid for dispensings inside the cycle window
  // AND inside the active selection.
  // ================================================================
  // 🔒 LOCKED — ODB cycle deposit math + ref mirrors
  // ----------------------------------------------------------------
  // The `odbCycleDeposits` memo below is the SINGLE source of truth
  // for cycle deposit amounts. The dashboard cycle cards AND the
  // JARVIS odb_cycles chart both read from it (the chart reads via
  // odbCycleDepositsRef.current to avoid stale-closure bugs).
  //
  // Before changing ANYTHING here, run:
  //   cd /app/backend && python -m pytest tests/test_cycle_reporting_lock.py -v
  //
  // See /app/CYCLE_REPORTING_CONTRACT.md for the locked contract.
  // ================================================================
  const odbCycleDeposits = useMemo(() => {
    // Effective selection bounds — mirror how visibleTransactions is filtered.
    let effStart = date_range_start;
    let effEnd = date_range_end;
    if (customRange) {
      effStart = customRange.start;
      effEnd = customRange.end;
    } else if (selectedDate !== "all") {
      effStart = selectedDate;
      effEnd = selectedDate;
    } else if (selectedMonth !== "all") {
      effStart = `${selectedMonth}-01`;
      effEnd = lastDayOfMonth(selectedMonth);
    }
    if (!effStart || !effEnd) return [];
    let cycles = odbCyclesInRange(effStart, effEnd);
    // If the user explicitly picked specific ODB cycles from the
    // filter dropdown, restrict rendering to JUST those — otherwise
    // every ODB cycle overlapping the encompassing range would render
    // even if the user only picked two non-adjacent cycles.
    const pickedOdbKeys = customRange?.odbCycleKeys;
    if (Array.isArray(pickedOdbKeys) && pickedOdbKeys.length > 0) {
      const keySet = new Set(pickedOdbKeys);
      cycles = cycles.filter((c) => keySet.has(c.key));
    }
    const today = todayIso();
    // Pre-classify every visible transaction by payer so cycle sums can
    // be split between ODB and NIHB. Patients are bucketed across the whole
    // visible window — not per cycle — so a patient with 3 NIHB-pattern claims
    // is consistently identified even if those claims straddle two cycles.
    const txByPatient = new Map();
    for (const t of visibleTransactionsForFees) {
      const arr = txByPatient.get(t.patient) || [];
      arr.push(t);
      txByPatient.set(t.patient, arr);
    }
    const payerByPatient = new Map();
    for (const [pt, arr] of txByPatient) {
      payerByPatient.set(pt, classifyPayer(arr));
    }
    // Under the user's accounting rule ("all transaction amounts for a
    // patient on the same date sum to the cost of 1 bottle"), every NIHB
    // row's plan1_paid is summed and each unique
    // (patient, drug, service_date) group counts as exactly 1 bottle.
    // Bottle tracking is per-cycle (declared inside the cycles.map below).
    const byDate = new Map();
    for (const t of visibleTransactionsForFees) {
      const d = t.service_date;
      const cur = byDate.get(d) || { txs: [] };
      cur.txs.push(t);
      byDate.set(d, cur);
    }
    return cycles.map((c) => {
      let odbPlan1 = 0;
      let nihbPlan1 = 0;
      let odbCount = 0;
      let nihbCount = 0;
      let coveredDays = 0;
      let odbProfit = 0;
      let nihbProfit = 0;
      // Track claim counts per drug for ODB (4 claims = 1 unit) and
      // bottle counts per drug for NIHB. Under the user's accounting
      // rule, 1 bottle = 1 (patient, drug, service_date) group — so a
      // patient with two dispense dates in the same cycle registers as
      // 2 bottles. `nihbBottlesByDrug` increments once per NIHB rep tx.
      const odbClaimsByDrug = new Map();
      const nihbBottlesByDrug = new Map(); // Map<drugKey, bottleCount>
      const nihbPatientsByDrug = new Map(); // Map<drugKey, Set<patient>> — retained for audit drill
      const nihbBottlesSeen = new Set(); // "patient||drug||date" → bottle already counted this cycle
      const [sy, sm, sd] = c.dispenseStart.split("-").map(Number);
      const [ey, em, ed] = c.dispenseEnd.split("-").map(Number);
      const start = new Date(Date.UTC(sy, sm - 1, sd));
      const end = new Date(Date.UTC(ey, em - 1, ed));
      const day = new Date(start);
      while (day <= end) {
        const iso = `${day.getUTCFullYear()}-${String(day.getUTCMonth() + 1).padStart(2, "0")}-${String(day.getUTCDate()).padStart(2, "0")}`;
        if (iso >= effStart && iso <= effEnd) {
          coveredDays += 1;
          const hit = byDate.get(iso);
          if (hit) {
            for (const t of hit.txs) {
              const isNihb = payerByPatient.get(t.patient) === "NIHB";
              const key = hcvDrugKey(t.drug_name);
              if (isNihb) {
                // Sum EVERY raw NIHB row's plan1_paid — the sum of same-
                // date rows for a (patient, drug) equals that bottle's
                // deposit. No co-pay applies to NIHB → full fees + markup
                // contribute to profit.
                const feeNet = (t.fees || 0) > 0 ? (t.fees || 0) : 0;
                const profit = feeNet + (t.markup || 0);
                nihbPlan1 += t.plan1_paid || 0;
                nihbCount += 1; // raw claim count (each row is 1 dispensing)
                nihbProfit += profit;
                // Count 1 bottle per unique (patient, drug, service_date)
                // group encountered in this cycle window.
                const drugKeyLbl = key || (t.drug_name || "").toUpperCase();
                const bottleKey = `${t.patient}||${drugKeyLbl}||${t.service_date || ""}`;
                if (!nihbBottlesSeen.has(bottleKey)) {
                  nihbBottlesSeen.add(bottleKey);
                  if (key) {
                    nihbBottlesByDrug.set(key, (nihbBottlesByDrug.get(key) || 0) + 1);
                    if (!nihbPatientsByDrug.has(key)) nihbPatientsByDrug.set(key, new Set());
                    nihbPatientsByDrug.get(key).add(t.patient);
                  }
                }
              } else {
                // $2 co-pay applies to ODB only.
                const feeNet =
                  (t.fees || 0) > 0
                    ? Math.max(0, (t.fees || 0) - COPAY_WAIVED_PER_DISPENSING)
                    : 0;
                const profit = feeNet + (t.markup || 0);
                odbPlan1 += t.plan1_paid || 0;
                odbCount += 1;
                odbProfit += profit;
                if (key) odbClaimsByDrug.set(key, (odbClaimsByDrug.get(key) || 0) + 1);
              }
            }
          }
        }
        day.setUTCDate(day.getUTCDate() + 1);
      }
      // Number of days in the cycle window (total possible coverage).
      const totalCycleDays = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
      let status = "paid";
      if (today < c.dispenseEnd) status = "in-progress";
      else if (today < c.payDate) status = "upcoming";
      // Compute drug cost and rebate in a single pass per slice using the
      // unit-anchored math ($20,000 / bottle × units). Accepts a
      // pre-computed unitsByDrug map so each payer can derive units in its
      // own way (ODB = claims × 1/4, NIHB = unique patients per drug).
      const sliceTotals = (unitsByDrug) => {
        let drugCost = 0;
        let rebate = 0;
        const breakdown = [];
        for (const [k, units] of unitsByDrug) {
          const cost = units * HCV_COST_PER_UNIT;
          const drugRebate = cost * (HCV_REBATE_PCT[k] || 0);
          drugCost += cost;
          rebate += drugRebate;
          breakdown.push({
            drug: k,
            units: +units.toFixed(2),
            cost: +cost.toFixed(2),
            rebatePct: (HCV_REBATE_PCT[k] || 0) * 100,
            amount: +drugRebate.toFixed(2),
          });
        }
        // Sort by amount descending so the dominant drug appears first
        breakdown.sort((a, b) => b.amount - a.amount);
        return {
          drugCost: +drugCost.toFixed(2),
          rebate: +rebate.toFixed(2),
          breakdown,
        };
      };
      // ODB: derive units = claims × (1/4) per the per-claim ratio.
      const odbUnitsByDrug = new Map();
      const odbUpc = unitsPerClaimForPayer("ODB");
      for (const [k, claims] of odbClaimsByDrug) {
        odbUnitsByDrug.set(k, claims * odbUpc);
      }
      // NIHB: each bottle-group (patient, drug, service_date) = 1 bottle.
      const nihbUnitsByDrug = new Map();
      for (const [k, bottles] of nihbBottlesByDrug) {
        nihbUnitsByDrug.set(k, bottles);
      }
      const odbTotals = sliceTotals(odbUnitsByDrug);
      const nihbTotals = sliceTotals(nihbUnitsByDrug);
      // Derive NIHB profit from (actual receivable − anchored drug cost) so
      // the three figures reconcile exactly: receivable − drug cost = profit.
      // This matches how the pharmacy thinks about NIHB margin and absorbs
      // sub-dollar PDF rounding (e.g., $0.18) into the profit line cleanly
      // instead of leaving an unexplained gap on the cycle card.
      if (nihbPlan1 > 0.005) {
        nihbProfit = nihbPlan1 - nihbTotals.drugCost;
      }
      // Percentages — GP-as-percent-of-drug-cost so the user can see the
      // pharmacy's effective margin (target ~5% from spec).
      const pct = (num, denom) =>
        denom > 0 ? +((num / denom) * 100).toFixed(1) : 0;
      return {
        ...c,
        // ODB-only fields drive the existing cycle cards.
        plan1: +odbPlan1.toFixed(2),
        odbProfit: +odbProfit.toFixed(2),
        rebateProfit: odbTotals.rebate,
        odbRebateBreakdown: odbTotals.breakdown,
        odbDrugCost: odbTotals.drugCost,
        odbMarkupPct: pct(odbProfit, odbTotals.drugCost),
        odbRebatePct: pct(odbTotals.rebate, odbTotals.drugCost),
        odbGpPct: pct(odbProfit + odbTotals.rebate, odbTotals.drugCost),
        grossProfit: +(odbProfit + odbTotals.rebate).toFixed(2),
        count: odbCount,
        // NIHB amounts in this same window — surfaced below the ODB cards.
        // Receivable = exact amount NIHB pays (plan1_paid). Do not derive from
        // calculated components — drug cost is anchored to $20k/unit so the
        // sum of components can be slightly less than the actual deposit by a
        // few cents (PDF rounding). The deposit shown must match the real
        // amount the pharmacy receives.
        nihbPlan1: +nihbPlan1.toFixed(2),
        nihbCount,
        nihbProfit: +nihbProfit.toFixed(2),
        nihbRebate: nihbTotals.rebate,
        nihbRebateBreakdown: nihbTotals.breakdown,
        nihbDrugCost: nihbTotals.drugCost,
        nihbMarkupPct: pct(nihbProfit, nihbTotals.drugCost),
        nihbRebatePct: pct(nihbTotals.rebate, nihbTotals.drugCost),
        nihbGpPct: pct(nihbProfit + nihbTotals.rebate, nihbTotals.drugCost),
        coveredDays,
        totalCycleDays,
        status,
      };
    });
  }, [
    visibleTransactionsForFees,
    date_range_start,
    date_range_end,
    customRange,
    selectedMonth,
    selectedDate,
  ]);

  // Keep refs in sync with the latest cycle-deposit + transaction data so
  // the JARVIS onAction closure can read fresh values even when it was
  // captured before the data loaded. See odbCycleDepositsRef declaration
  // above for why this matters.
  useEffect(() => {
    odbCycleDepositsRef.current = odbCycleDeposits;
  }, [odbCycleDeposits]);
  useEffect(() => {
    allTransactionsRef.current = (originalAnalysis?.transactions) || transactions || [];
  }, [originalAnalysis, transactions]);

  // NIHB cycles run on a DIFFERENT cadence than ODB — bi-weekly windows
  // ending Saturday, paid the following Wednesday. Anchored to the
  // published NIHB payment calendar and reused here to bucket the
  // pharmacy's NIHB receivables into their real deposit windows. Same
  // per-payer aggregation as odbCycleDeposits but scoped strictly to
  // NIHB-classified patients (ODB patients are ignored).
  const nihbCycleDeposits = useMemo(() => {
    let effStart = date_range_start;
    let effEnd = date_range_end;
    if (customRange) {
      effStart = customRange.start;
      effEnd = customRange.end;
    } else if (selectedDate !== "all") {
      effStart = selectedDate;
      effEnd = selectedDate;
    } else if (selectedMonth !== "all") {
      effStart = `${selectedMonth}-01`;
      effEnd = lastDayOfMonth(selectedMonth);
    }
    if (!effStart || !effEnd) return [];
    let cycles = nihbCyclesInRange(effStart, effEnd);
    // Restrict to user-picked NIHB cycle keys when present, so
    // picking a single NIHB cycle doesn't also surface adjacent ones
    // that happen to overlap the encompassing range.
    const pickedNihbKeys = customRange?.nihbCycleKeys;
    if (Array.isArray(pickedNihbKeys) && pickedNihbKeys.length > 0) {
      const keySet = new Set(pickedNihbKeys);
      cycles = cycles.filter((c) => keySet.has(c.key));
    }
    const today = todayIso();
    // Same payer-classification approach as odbCycleDeposits so a
    // patient's payer is stable across the whole visible window rather
    // than fluctuating cycle-by-cycle.
    const txByPatient = new Map();
    for (const t of visibleTransactionsForFees) {
      const arr = txByPatient.get(t.patient) || [];
      arr.push(t);
      txByPatient.set(t.patient, arr);
    }
    const payerByPatient = new Map();
    for (const [pt, arr] of txByPatient) {
      payerByPatient.set(pt, classifyPayer(arr));
    }
    // Per user's accounting rule: every NIHB row's plan1_paid is summed
    // and each unique (patient, drug, service_date) group counts as
    // exactly 1 bottle. Bottle tracking is per-cycle (in the loop below).
    const byDate = new Map();
    for (const t of visibleTransactionsForFees) {
      const d = t.service_date;
      const cur = byDate.get(d) || { txs: [] };
      cur.txs.push(t);
      byDate.set(d, cur);
    }
    return cycles.map((c) => {
      let nihbPlan1 = 0;
      let nihbCount = 0;
      let nihbProfit = 0;
      const nihbBottlesByDrug = new Map(); // Map<drugKey, bottleCount>
      const nihbPatientsByDrug = new Map(); // retained for audit drill (unique clients)
      const nihbBottlesSeen = new Set();
      const [sy, sm, sd] = c.dispenseStart.split("-").map(Number);
      const [ey, em, ed] = c.dispenseEnd.split("-").map(Number);
      const start = new Date(Date.UTC(sy, sm - 1, sd));
      const end = new Date(Date.UTC(ey, em - 1, ed));
      const day = new Date(start);
      let coveredDays = 0;
      while (day <= end) {
        const iso = `${day.getUTCFullYear()}-${String(day.getUTCMonth() + 1).padStart(2, "0")}-${String(day.getUTCDate()).padStart(2, "0")}`;
        if (iso >= effStart && iso <= effEnd) {
          coveredDays += 1;
          const hit = byDate.get(iso);
          if (hit) {
            for (const t of hit.txs) {
              if (payerByPatient.get(t.patient) !== "NIHB") continue;
              // NIHB has no ODB co-pay — full raw fees count toward profit.
              const feeNet = (t.fees || 0) > 0 ? (t.fees || 0) : 0;
              const profit = feeNet + (t.markup || 0);
              nihbPlan1 += t.plan1_paid || 0;
              nihbCount += 1; // raw claim count
              nihbProfit += profit;
              const key = hcvDrugKey(t.drug_name);
              const drugKeyLbl = key || (t.drug_name || "").toUpperCase();
              const bottleKey = `${t.patient}||${drugKeyLbl}||${t.service_date || ""}`;
              if (!nihbBottlesSeen.has(bottleKey)) {
                nihbBottlesSeen.add(bottleKey);
                if (key) {
                  nihbBottlesByDrug.set(key, (nihbBottlesByDrug.get(key) || 0) + 1);
                  if (!nihbPatientsByDrug.has(key)) nihbPatientsByDrug.set(key, new Set());
                  nihbPatientsByDrug.get(key).add(t.patient);
                }
              }
            }
          }
        }
        day.setUTCDate(day.getUTCDate() + 1);
      }
      let status = "paid";
      if (today < c.dispenseEnd) status = "in-progress";
      else if (today < c.payDate) status = "upcoming";
      // Drug-cost + rebate rollup — same $20K anchor math as ODB. Each
      // NIHB bottle group (patient × drug × service_date) = 1 unit.
      let drugCost = 0;
      let rebate = 0;
      const breakdown = [];
      for (const [k, bottles] of nihbBottlesByDrug) {
        const units = bottles;
        const cost = units * HCV_COST_PER_UNIT;
        const drugRebate = cost * (HCV_REBATE_PCT[k] || 0);
        drugCost += cost;
        rebate += drugRebate;
        breakdown.push({
          drug: k,
          units,
          cost: +cost.toFixed(2),
          rebatePct: (HCV_REBATE_PCT[k] || 0) * 100,
          amount: +drugRebate.toFixed(2),
        });
      }
      breakdown.sort((a, b) => b.amount - a.amount);
      // Reconcile profit to (actual receivable − anchored drug cost)
      // so the three figures always tie out on the card. Same rule as
      // odbCycleDeposits — absorbs sub-dollar PDF rounding cleanly.
      if (nihbPlan1 > 0.005) {
        nihbProfit = nihbPlan1 - drugCost;
      }
      const pct = (num, denom) =>
        denom > 0 ? +((num / denom) * 100).toFixed(1) : 0;
      return {
        ...c,
        nihbPlan1: +nihbPlan1.toFixed(2),
        nihbCount,
        nihbProfit: +nihbProfit.toFixed(2),
        nihbRebate: +rebate.toFixed(2),
        nihbRebateBreakdown: breakdown,
        nihbDrugCost: +drugCost.toFixed(2),
        nihbMarkupPct: pct(nihbProfit, drugCost),
        nihbRebatePct: pct(rebate, drugCost),
        nihbGpPct: pct(nihbProfit + rebate, drugCost),
        coveredDays,
        status,
      };
    });
  }, [
    visibleTransactionsForFees,
    date_range_start,
    date_range_end,
    customRange,
    selectedMonth,
    selectedDate,
  ]);

  const drugsBreakdown = useMemo(() => {
    const map = new Map();
    // For HCV drugs we apply a course-level rule for "zero-fee" counting:
    //   • 1 course = 12 weekly claims for Epclusa / Vosevi / Harvoni
    //   • 1 course = 8 weekly claims for Maviret
    // Only ONE professional fee is allowable per course. So if ANY of the
    // 12 (or 8) weekly dispensings has a fee collected, the entire course
    // is considered fee-covered (contributes 0 to zero-fee). If all of them
    // are $0, every row in that course is flagged. Non-HCV drugs continue
    // to use per-row zero-fee counting.
    const COURSE_LEN = { EPCLUSA: 12, VOSEVI: 12, HARVONI: 12, MAVIRET: 8 };
    const hcvRowsByPatientDrug = new Map();
    for (const t of visibleTransactionsForFees) {
      const drugName = canonicalDrugName(t.drug_name);
      const din = t.din || "";
      // Each (drug name, DIN) pair is its own row — so the same drug
      // from two different manufacturers (different DINs) shows as
      // separate line items rather than a single collapsed row.
      const key = `${drugName}||${din}`;
      const entry = map.get(key) || {
        drug_name: drugName,
        din,
        count: 0,
        fees: 0,
        zero: 0,
        patients: new Set(),
      };
      entry.count += 1;
      entry.fees += t.fees;
      if (t.patient) entry.patients.add(t.patient);
      const isHcv = !!hcvDrugKey(t.drug_name);
      if (isHcv) {
        // Defer HCV zero-counting until we've grouped by patient × drug.
        const hcvKey = `${t.patient}||${drugName}`;
        const arr = hcvRowsByPatientDrug.get(hcvKey) || [];
        arr.push(t);
        hcvRowsByPatientDrug.set(hcvKey, arr);
      } else if (t.fees === 0) {
        entry.zero += 1;
      }
      map.set(key, entry);
    }
    // Walk each HCV (patient × drug) group, chunk into courses sized by
    // the drug's regimen length, and credit only courses with no
    // fee-bearing dispensing as zero-fee. HCV drugs typically map 1:1
    // to a single DIN, but iterate every entry that matches drugName
    // to be safe if two DINs share a canonical name.
    for (const [hcvKey, hcvRows] of hcvRowsByPatientDrug) {
      const drugName = hcvKey.split("||")[1];
      const drugKey = hcvDrugKey(drugName);
      const chunk = COURSE_LEN[drugKey] || 12;
      const sorted = hcvRows
        .slice()
        .sort((a, b) => (a.service_date || "").localeCompare(b.service_date || ""));
      let missing = 0;
      for (let i = 0; i < sorted.length; i += chunk) {
        const course = sorted.slice(i, i + chunk);
        const anyFee = course.some((r) => (r.fees || 0) > 0);
        if (!anyFee) {
          // Entire course missed — every row in it counts as a zero-fee miss.
          missing += course.length;
        }
      }
      for (const e of map.values()) {
        if (e.drug_name === drugName) e.zero += missing;
      }
    }
    return Array.from(map.values())
      .map((d) => ({
        ...d,
        unique_patients: d.patients.size,
        patients: undefined,
        // Aliases for the legacy markup that expected the analysis-level shape.
        total_fees: d.fees,
        zero_fee_count: d.zero,
      }))
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        const nameCmp = a.drug_name.localeCompare(b.drug_name);
        if (nameCmp !== 0) return nameCmp;
        return (a.din || "").localeCompare(b.din || "");
      });
  }, [visibleTransactionsForFees]);

  const drillDownRows = useMemo(() => {
    if (!drillDown) return [];
    // A patient drill is a per-patient audit view. When the user is
    // reviewing an individual (especially from Lost Patients), they
    // want the COMPLETE dispensing history — not just what falls in
    // the currently-filtered date window. Pull from the unfiltered
    // transactions dataset so every prior dispensing date shows.
    // Day-specific drills (drillDown.service_date set) still honor
    // the exact date filter.
    const source = transactions || [];
    if (source.length === 0) return [];
    return source
      .filter((t) => {
        if (t.patient !== drillDown.patient) return false;
        if (drillDown.service_date && t.service_date !== drillDown.service_date)
          return false;
        return true;
      })
      .sort((a, b) => {
        // Sort by date desc, then time asc within the same day.
        if (a.service_date !== b.service_date)
          return a.service_date < b.service_date ? 1 : -1;
        return a.time < b.time ? -1 : a.time > b.time ? 1 : 0;
      });
  }, [drillDown, transactions]);

  const drillDownTotals = useMemo(() => {
    if (!drillDownRows.length)
      return { count: 0, paidCount: 0, fees: 0, zero: 0, markup: 0 };
    const count = drillDownRows.length;
    const fees = +drillDownRows.reduce((s, r) => s + r.fees, 0).toFixed(2);
    const zero = drillDownRows.filter((r) => r.fees === 0).length;
    const paidCount = count - zero;
    const markup = +drillDownRows
      .reduce((s, r) => s + (r.markup || 0), 0)
      .toFixed(2);
    return { count, paidCount, fees, zero, markup };
  }, [drillDownRows]);

  // Per-patient profile used by the drill-down dialog (adherence, cadence,
  // estimated annual value). Falls back to null when no patient is drilled in.
  const drillDownProfile = useMemo(() => {
    if (!drillDown) return null;
    return patientsBreakdown.find((p) => p.patient === drillDown.patient) || null;
  }, [drillDown, patientsBreakdown]);

  // Legacy adherence accessor kept for the existing JSX.
  const drillDownAdherence = useMemo(() => {
    if (!drillDownProfile || drillDown?.service_date) return null;
    return {
      pct: drillDownProfile.adherence,
      visitDays: drillDownProfile.adherenceNum ?? drillDownProfile.visitDays,
      // Show the tenure-adjusted denominator (days since patient's
      // first dispense) so "X of Y days seen" reflects the same
      // calculation as the adherence %.
      totalDays: drillDownProfile.adherenceDenom ?? drillDownProfile.totalDays,
    };
  }, [drillDownProfile, drillDown]);

  // Lost patients — anyone whose LIFETIME most recent dispensing is
  // more than 7 days before the report's LIFETIME most recent
  // dispense date. Uses the full unfiltered PDF history so the
  // classification is a stable lifetime signal regardless of the
  // user's current date filter.
  //
  // HCV drugs are excluded: HCV patients dispense on 8/12-week courses,
  // so a 7-day silence isn't a meaningful adherence signal for them.
  const lostPatients = useMemo(() => {
    // Prefer the unfiltered original transactions so patients whose
    // latest dispense falls outside the current filter still get
    // correctly flagged (e.g., FLETCHER, JEFF last dispensed Apr 15
    // still shows as Lost when the user's filter is May 4 → Jun 25).
    const sourceAll = originalAnalysis?.transactions || visibleTransactions || [];
    if (sourceAll.length === 0) return [];
    const pharmacyTxs = sourceAll.filter((t) => !isHcvDrug(t.drug_name));
    if (pharmacyTxs.length === 0) return [];
    // Reference date = latest LIFETIME service_date across the whole
    // report. Anchors the "no dispense in 7 days" question against
    // the true current state of the pharmacy, not a filter slice.
    let refIso = "";
    for (const t of pharmacyTxs) {
      if (t.service_date && t.service_date > refIso) refIso = t.service_date;
    }
    if (!refIso) return [];
    // 7-day lookback cutoff (exclusive). A patient is "lost" when their
    // last dispense is strictly before this cutoff.
    const refDate = new Date(`${refIso}T00:00:00Z`);
    const cutoffDate = new Date(refDate.getTime() - 7 * 24 * 3600 * 1000);
    const cutoffIso = `${cutoffDate.getUTCFullYear()}-${String(cutoffDate.getUTCMonth() + 1).padStart(2, "0")}-${String(cutoffDate.getUTCDate()).padStart(2, "0")}`;
    // Track last dispense per patient across the full history.
    const byPatient = new Map();
    for (const t of pharmacyTxs) {
      const cur = byPatient.get(t.patient);
      if (!cur || t.service_date > cur.lastDate) {
        byPatient.set(t.patient, {
          patient: t.patient,
          lastDate: t.service_date,
          drugsOnLastDay: new Map(), // drug canonical -> count
        });
      }
    }
    // Populate the drug list for each patient's last dispense day.
    for (const t of pharmacyTxs) {
      const entry = byPatient.get(t.patient);
      if (!entry) continue;
      if (t.service_date !== entry.lastDate) continue;
      const drug = canonicalDrugName(t.drug_name) || t.drug_name || "";
      if (!drug) continue;
      entry.drugsOnLastDay.set(drug, (entry.drugsOnLastDay.get(drug) || 0) + 1);
    }
    const lost = [];
    for (const entry of byPatient.values()) {
      if (!entry.lastDate) continue;
      if (entry.lastDate >= cutoffIso) continue; // Recently seen; not lost.
      const daysSince = Math.floor(
        (refDate.getTime() - new Date(`${entry.lastDate}T00:00:00Z`).getTime()) /
          (24 * 3600 * 1000)
      );
      lost.push({
        patient: entry.patient,
        lastDate: entry.lastDate,
        daysSince,
        // Total unique dispense days for this patient across the ENTIRE
        // report — from the backend-authoritative map when present,
        // else fall back to the local per-patient day set. Lets the
        // auditor see "1 fill ever" vs. "50 fills over the report"
        // right in the Lost Patients callout without opening the drill.
        historyDays:
          originalAnalysis?.patient_history_days?.[entry.patient] ?? 1,
      });
    }
    // Sort MOST-RECENT last-dispense first (newest at top) so the
    // auditor sees patients who just went silent before the ones who
    // dropped off months ago. `lastDate` is ISO — direct string
    // compare works.
    lost.sort((a, b) => (a.lastDate < b.lastDate ? 1 : a.lastDate > b.lastDate ? -1 : 0));
    return lost;
  }, [visibleTransactions, originalAnalysis]);

  // Split lost-patient roster into two cohorts based on lifetime
  // engagement:
  //   • Lost (≥ 7 total dispense days) — established patients with a
  //     real relationship who've now gone silent. Highest-value
  //     follow-up opportunity.
  //   • Discharged (< 7 total dispense days) — likely one-off scripts
  //     (visitor, urgent-care refill, single trial). Filed separately
  //     so the Lost list stays focused on genuine adherence risk.
  const DISCHARGED_THRESHOLD = 7;
  const activeLostPatients = useMemo(
    () => lostPatients.filter((lp) => lp.historyDays >= DISCHARGED_THRESHOLD),
    [lostPatients]
  );
  const dischargedPatients = useMemo(
    () => lostPatients.filter((lp) => lp.historyDays < DISCHARGED_THRESHOLD),
    [lostPatients]
  );

  // Reactivated patients — clients who previously went silent for
  // more than 7 days (would have been Lost or Discharged) AND have
  // now dispensed again within the last 7 days. Called out separately
  // so the auditor sees "who just came back" without hunting through
  // the full Patients list. Auto-expires: once the return dispense is
  // more than 7 days behind the report's max date, they naturally
  // drop off this banner (and continue to appear in the standard
  // Patients cohort). Non-HCV only — HCV courses don't fit the
  // "returned after a gap" pattern.
  const reactivatedPatients = useMemo(() => {
    if (viewMode === "hcv") return [];
    const sourceAll = originalAnalysis?.transactions || [];
    if (sourceAll.length === 0) return [];
    // Bucket dispense dates per patient (ISO strings).
    const datesByPatient = new Map(); // patient -> Set<isoDate>
    let refIso = "";
    for (const t of sourceAll) {
      if (!t.patient || !t.service_date) continue;
      if (isHcvDrug(t.drug_name)) continue;
      if (t.service_date > refIso) refIso = t.service_date;
      let s = datesByPatient.get(t.patient);
      if (!s) {
        s = new Set();
        datesByPatient.set(t.patient, s);
      }
      s.add(t.service_date);
    }
    if (!refIso) return [];
    const refMs = new Date(`${refIso}T00:00:00Z`).getTime();
    const DAY = 24 * 3600 * 1000;
    const WINDOW_DAYS = 7; // "back within last 7 days"
    const GAP_DAYS = 7;    // "was silent > 7 days"
    const out = [];
    for (const [patient, dates] of datesByPatient) {
      const sorted = Array.from(dates).sort();
      if (sorted.length < 2) continue;
      const latest = sorted[sorted.length - 1];
      const prev = sorted[sorted.length - 2];
      const daysSinceLatest = Math.floor(
        (refMs - new Date(`${latest}T00:00:00Z`).getTime()) / DAY
      );
      if (daysSinceLatest > WINDOW_DAYS) continue;
      const gapDays = Math.floor(
        (new Date(`${latest}T00:00:00Z`).getTime() -
          new Date(`${prev}T00:00:00Z`).getTime()) /
          DAY
      );
      if (gapDays <= GAP_DAYS) continue;
      out.push({
        patient,
        lastDate: latest,
        prevDate: prev,
        gapDays,
        daysSinceLatest,
        // Was this reactivation from the "Discharged" cohort (< 7 lifetime
        // dispense days) or the "Lost" cohort (≥ 7)? Colors the badge.
        wasDischarged:
          (originalAnalysis?.patient_history_days?.[patient] ?? sorted.length) <
          DISCHARGED_THRESHOLD,
      });
    }
    // Newest reactivation at the top.
    out.sort((a, b) => (a.lastDate < b.lastDate ? 1 : a.lastDate > b.lastDate ? -1 : 0));
    return out;
  }, [viewMode, originalAnalysis]);

  // New Clients — patients whose FIRST-EVER dispense across the report
  // is within the last 7 days of the report's max date. Surfaces first-
  // time customers so the pharmacist can see roster growth. Auto-expires
  // after 7 days (same window as Reactivated). Non-HCV only — HCV courses
  // are course-based, not "new customer" signals. A patient can appear in
  // this cohort OR Reactivated, but not both — genuine first-timers only.
  const newClients = useMemo(() => {
    if (viewMode === "hcv") return [];
    const sourceAll = originalAnalysis?.transactions || [];
    if (sourceAll.length === 0) return [];
    // Bucket ALL dispense dates per patient (non-HCV only).
    const datesByPatient = new Map(); // patient -> Set<isoDate>
    let refIso = "";
    for (const t of sourceAll) {
      if (!t.patient || !t.service_date) continue;
      if (isHcvDrug(t.drug_name)) continue;
      if (t.service_date > refIso) refIso = t.service_date;
      let s = datesByPatient.get(t.patient);
      if (!s) {
        s = new Set();
        datesByPatient.set(t.patient, s);
      }
      s.add(t.service_date);
    }
    if (!refIso) return [];
    const refMs = new Date(`${refIso}T00:00:00Z`).getTime();
    const DAY = 24 * 3600 * 1000;
    const WINDOW_DAYS = 7;
    const out = [];
    for (const [patient, dates] of datesByPatient) {
      const sorted = Array.from(dates).sort();
      if (sorted.length === 0) continue;
      const firstDate = sorted[0];
      // "First ever" within the last 7 days of the report.
      const daysSinceFirst = Math.floor(
        (refMs - new Date(`${firstDate}T00:00:00Z`).getTime()) / DAY
      );
      if (daysSinceFirst > WINDOW_DAYS) continue;
      out.push({
        patient,
        firstDate,
        daysSinceFirst,
        dispenseDays: sorted.length,
        lastDate: sorted[sorted.length - 1],
      });
    }
    // Newest first-dispense at the top.
    out.sort((a, b) => (a.firstDate < b.firstDate ? 1 : a.firstDate > b.firstDate ? -1 : 0));
    return out;
  }, [viewMode, originalAnalysis]);

  // Cohort mix percentages — shown next to each banner count so the
  // auditor can eyeball the health of the roster at a glance
  // (e.g., "24 lost · 36%" tells them more than "24" alone). Uses the
  // same activePool logic the search IIFE uses below (excludes Lost
  // from the Patients cohort in Non-HCV) so the three percentages sum
  // to ~100% for the visible cohort mix.
  const cohortPct = useMemo(() => {
    const excludedForActive = new Set(lostPatients.map((lp) => lp.patient));
    const cohortActive = viewMode === "hcv"
      ? patientsBreakdown.length
      : patientsBreakdown.filter((p) => !excludedForActive.has(p.patient)).length;
    const cohortLost = viewMode !== "hcv" ? activeLostPatients.length : 0;
    const cohortDischarged = viewMode !== "hcv" ? dischargedPatients.length : 0;
    const cohortReactivated = viewMode !== "hcv" ? reactivatedPatients.length : 0;
    const cohortNew = viewMode !== "hcv" ? newClients.length : 0;
    // Denominator excludes reactivated and new so the three exclusive cohorts
    // (Lost / Discharged / Active) sum to ~100%. Reactivated + New Clients
    // are subsets of Active — their % is a share of the same roster total.
    const cohortTotal = cohortLost + cohortDischarged + cohortActive;
    return {
      total: cohortTotal,
      lost: cohortTotal ? Math.round((cohortLost / cohortTotal) * 100) : 0,
      discharged: cohortTotal ? Math.round((cohortDischarged / cohortTotal) * 100) : 0,
      active: cohortTotal ? Math.round((cohortActive / cohortTotal) * 100) : 0,
      // Reactivated share — clamped to a minimum of 1% when non-zero
      // so a lone reactivation on a large roster still surfaces
      // visually (0.4% rounded would show as "0%" and read as broken).
      reactivated:
        cohortTotal && cohortReactivated > 0
          ? Math.max(1, Math.round((cohortReactivated / cohortTotal) * 100))
          : 0,
      newClients:
        cohortTotal && cohortNew > 0
          ? Math.max(1, Math.round((cohortNew / cohortTotal) * 100))
          : 0,
    };
  }, [viewMode, patientsBreakdown, lostPatients, activeLostPatients, dischargedPatients, reactivatedPatients, newClients]);

  // Patient drill dialog: day groups start COLLAPSED. The auditor
  // wants a clean summary list of dispense dates first, then can tap
  // any date to expand its Rx rows. (Earlier iteration auto-expanded
  // every date — too visually busy on a long history.)
  // Intentionally NO auto-expand effect here.


  // Days currently visible given the month / custom-range filter
  const visibleDays = useMemo(() => {
    if (customRange) {
      return per_day.filter(
        (d) =>
          d.service_date >= customRange.start &&
          d.service_date <= customRange.end
      );
    }
    if (selectedMonth === "all") return per_day;
    return per_day.filter((d) => d.service_date.startsWith(selectedMonth));
  }, [per_day, selectedMonth, customRange]);

  // When the month filter changes, fall back to "all days within that month"
  // unless the previously-selected date is still inside the new month.
  const handleMonthChange = (val) => {
    // Clicking a month pill always returns us to the "whole month" view —
    // including re-clicking the currently active pill, which acts as an
    // "escape hatch" back from a drilled-into specific day.
    setSelectedMonth(val);
    setSelectedDate("all");
  };

  const summary = useMemo(() => {
    if (selectedDate === "all") {
      const days = visibleDays;
      const drugSet = new Set();
      days.forEach((d) => {
        (drug_breakdown_per_day[d.service_date] || []).forEach((x) =>
          drugSet.add(x.drug_name)
        );
      });
      // Distinct unique patients across the visible date range (deduped by name,
      // not sum-of-daily-uniques). Falls back to summing daily uniques if the
      // full transactions list isn't available for some reason.
      const visibleSet = new Set(days.map((d) => d.service_date));
      let unique_patients_distinct = 0;
      if (transactions && transactions.length) {
        const ptSet = new Set();
        for (const t of transactions) {
          if (visibleSet.has(t.service_date)) ptSet.add(t.patient);
        }
        unique_patients_distinct = ptSet.size;
      } else if (selectedMonth !== "all") {
        // Pull the precise distinct count from the server-computed month bundle.
        const ms = per_month_summary.find((m) => m.year_month === selectedMonth);
        unique_patients_distinct = ms?.unique_patients
          ?? days.reduce((s, d) => s + d.unique_patients, 0);
      } else {
        // "All months" with no inlined transactions — use the report-wide
        // distinct count emitted by the backend (or fall back to summed daily
        // uniques as a last resort).
        unique_patients_distinct =
          analysisUniquePatients ||
          days.reduce((s, d) => s + d.unique_patients, 0);
      }
      const sumFees = +(days.reduce((s, d) => s + d.total_fees, 0)).toFixed(2);
      const sumDispensings = days.reduce((s, d) => s + d.total_dispensings, 0);
      const monthLabel =
        selectedMonth === "all"
          ? `${date_range_start || ""} → ${date_range_end || ""}`
          : monthsAvailable.find((m) => m.ym === selectedMonth)?.label ||
            selectedMonth;
      return {
        unique_patients: unique_patients_distinct,
        patients_label: "Unique patients",
        unique_drugs: drugSet.size,
        total_dispensings: sumDispensings,
        total_fees: sumFees,
        zero_fees: days.reduce((s, d) => s + d.zero_fee_count, 0),
        paid_fees: days.reduce((s, d) => s + d.paid_fee_count, 0),
        pretty: monthLabel,
      };
    }
    const d = per_day.find((x) => x.service_date === selectedDate);
    return {
      unique_patients: d.unique_patients,
      patients_label: "Unique patients seen",
      unique_drugs: d.unique_drugs,
      total_dispensings: d.total_dispensings,
      total_fees: d.total_fees,
      zero_fees: d.zero_fee_count,
      paid_fees: d.paid_fee_count,
      pretty: d.pretty_date,
    };
  }, [
    selectedDate,
    visibleDays,
    per_day,
    per_month_summary,
    transactions,
    drug_breakdown_per_day,
    date_range_start,
    date_range_end,
    selectedMonth,
    monthsAvailable,
    analysisUniquePatients,
  ]);

  const chartData = useMemo(
    () =>
      visibleDays.map((d) => ({
        date: formatShortDate(d.service_date),
        Patients: d.unique_patients,
        Dispensings: d.total_dispensings,
      })),
    [visibleDays]
  );

  // For the Monthly Revenue Variance table: count, per YYYY-MM, how many
  // patients had their FIRST EVER prescription in that month (i.e. the
  // earliest service_date for the patient — across the whole report — lands
  // in this month). Lets the user see new-client acquisition per month.
  // Computed from the current view's transactions, so in non-HCV scope it
  // counts patients new to non-HCV; in HCV scope, new to HCV.
  const newPatientsByMonth = useMemo(() => {
    const firstSeen = new Map();
    for (const t of transactions || []) {
      if (!t.patient || !t.service_date) continue;
      const prev = firstSeen.get(t.patient);
      if (!prev || t.service_date < prev) firstSeen.set(t.patient, t.service_date);
    }
    const counts = new Map();
    for (const iso of firstSeen.values()) {
      const ym = iso.slice(0, 7);
      counts.set(ym, (counts.get(ym) || 0) + 1);
    }
    return counts;
  }, [transactions]);

  // Group days by YYYY-MM, return list of { ym, monthLabel, days, avg, data }
  // sorted with most-recent month first. Used when "All months" is selected
  // on a multi-month report.
  const perMonthCharts = useMemo(() => {
    const groups = new Map();
    visibleDays.forEach((d) => {
      const ym = d.service_date.slice(0, 7);
      if (!groups.has(ym)) groups.set(ym, []);
      groups.get(ym).push(d);
    });
    // Distinct patient count per month — derive from transactions so the
    // same patient seen on multiple days within the month counts once.
    const uniquePatientsByMonth = new Map();
    for (const t of visibleTransactionsForFees) {
      if (!t.patient || !t.service_date) continue;
      const ym = t.service_date.slice(0, 7);
      let set = uniquePatientsByMonth.get(ym);
      if (!set) {
        set = new Set();
        uniquePatientsByMonth.set(ym, set);
      }
      set.add(t.patient);
    }
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ];
    const list = Array.from(groups.entries()).map(([ym, days]) => {
      const [y, m] = ym.split("-");
      const monthLabel = `${monthNames[parseInt(m, 10) - 1]} ${y}`;
      const sumDailyPatients = days.reduce((s, d) => s + d.unique_patients, 0);
      const avg = days.length ? sumDailyPatients / days.length : 0;
      const totalFees = +(days.reduce((s, d) => s + d.total_fees, 0)).toFixed(2);
      const uniquePatients = (uniquePatientsByMonth.get(ym) || new Set()).size;
      return {
        ym,
        monthLabel,
        days,
        avg,
        totalFees,
        uniquePatients,
        data: days.map((d) => ({
          date: formatShortDate(d.service_date),
          Patients: d.unique_patients,
          Dispensings: d.total_dispensings,
        })),
      };
    });
    list.sort((a, b) => (a.ym < b.ym ? 1 : a.ym > b.ym ? -1 : 0));
    return list;
  }, [visibleDays, visibleTransactionsForFees]);

  // ================================================================
  // Top-of-dashboard "Charts" section.
  //
  // Scope-aware quick view of full-report trends:
  //   • HCV view  → "HCV Sales" (monthly) + "HCV ODB Cycles" (per-cycle)
  //   • Non-HCV   → "Pharmacy Sales" (monthly) + "Pharmacy ODB Cycles" (per-cycle)
  //
  // Sales totals use Σ(plan1_paid + plan2_paid) so the tile reflects
  // actual receivable (~$7M for HCV) rather than just dispensing margin.
  // ODB Cycles use real ODB cadence (Cycle A: 9–22, Cycle B: 23→8) so
  // the bars line up with how the pharmacy is actually paid.
  // ================================================================
  const [chartsSelectedKey, setChartsSelectedKey] = useState(null);
  const chartsMonthly = useMemo(() => {
    const all = transactions || [];
    if (!all.length) return null;
    // ODB-payer classification needs ALL of a patient's claims to be stable.
    const byPatient = new Map();
    for (const t of all) {
      if (!t.patient) continue;
      const arr = byPatient.get(t.patient) || [];
      arr.push(t);
      byPatient.set(t.patient, arr);
    }
    const payerByPatient = new Map();
    for (const [pt, rows] of byPatient.entries()) {
      payerByPatient.set(pt, classifyPayer(rows));
    }
    // Monthly Sales buckets — full receivable per month, plus plan2 (PSP
    // copay) so the user can see how much of "Sales" is patient-side.
    const buckets = new Map();
    for (const t of all) {
      const ym = (t.service_date || "").slice(0, 7);
      if (!ym) continue;
      const slot = buckets.get(ym) || {
        ym,
        hcvSales: 0,
        nonHcvSales: 0,
        hcvPsp: 0,
        nonHcvPsp: 0,
      };
      const isHcv = !!hcvDrugKey(t.drug_name);
      const sale = (t.plan1_paid || 0) + (t.plan2_paid || 0);
      const psp = t.plan2_paid || 0;
      if (isHcv) {
        slot.hcvSales += sale;
        slot.hcvPsp += psp;
      } else {
        slot.nonHcvSales += sale;
        slot.nonHcvPsp += psp;
      }
      buckets.set(ym, slot);
    }
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    const sortedMonths = Array.from(buckets.values()).sort((a, b) =>
      a.ym.localeCompare(b.ym)
    );
    const labelFor = (ym) => {
      const [y, m] = ym.split("-");
      return `${monthNames[parseInt(m, 10) - 1]} ${y.slice(2)}`;
    };
    // Per-cycle ODB series across the full report span.
    const txByDate = new Map();
    for (const t of all) {
      if (!t.service_date) continue;
      const arr = txByDate.get(t.service_date) || [];
      arr.push(t);
      txByDate.set(t.service_date, arr);
    }
    const cycles = (date_range_start && date_range_end)
      ? odbCyclesInRange(date_range_start, date_range_end)
          .slice()
          .sort((a, b) => a.dispenseStart.localeCompare(b.dispenseStart))
      : [];
    const hcvOdbSeries = [];
    const nonHcvOdbSeries = [];
    const hcvNihbSeries = [];
    const nonHcvNihbSeries = [];
    for (const c of cycles) {
      let hcvOdb = 0;
      let nonHcvOdb = 0;
      let hcvNihb = 0;
      let nonHcvNihb = 0;
      const [sy, sm, sd] = c.dispenseStart.split("-").map(Number);
      const [ey, em, ed] = c.dispenseEnd.split("-").map(Number);
      const start = new Date(Date.UTC(sy, sm - 1, sd));
      const end = new Date(Date.UTC(ey, em - 1, ed));
      for (let day = new Date(start); day <= end; day.setUTCDate(day.getUTCDate() + 1)) {
        const iso = `${day.getUTCFullYear()}-${String(day.getUTCMonth() + 1).padStart(2, "0")}-${String(day.getUTCDate()).padStart(2, "0")}`;
        const txs = txByDate.get(iso);
        if (!txs) continue;
        for (const t of txs) {
          const payer = payerByPatient.get(t.patient);
          if (payer !== "ODB" && payer !== "NIHB") continue;
          const plan1 = t.plan1_paid || 0;
          const isHcv = !!hcvDrugKey(t.drug_name);
          if (payer === "ODB") {
            if (isHcv) hcvOdb += plan1;
            else nonHcvOdb += plan1;
          } else {
            if (isHcv) hcvNihb += plan1;
            else nonHcvNihb += plan1;
          }
        }
      }
      const MNAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const [pyY, pyM, pyD] = c.payDate.split("-").map(Number);
      const payShort = `Pay ${MNAMES[pyM - 1]} ${pyD}`;
      hcvOdbSeries.push({ date: c.shortLabel, payDate: payShort, Patients: +hcvOdb.toFixed(2) });
      nonHcvOdbSeries.push({ date: c.shortLabel, payDate: payShort, Patients: +nonHcvOdb.toFixed(2) });
      hcvNihbSeries.push({ date: c.shortLabel, Patients: +hcvNihb.toFixed(2) });
      nonHcvNihbSeries.push({ date: c.shortLabel, Patients: +nonHcvNihb.toFixed(2) });
    }
    return {
      series: {
        hcv_sales: sortedMonths.map((s) => ({ date: labelFor(s.ym), Patients: +s.hcvSales.toFixed(2) })),
        nonhcv_sales: sortedMonths.map((s) => ({ date: labelFor(s.ym), Patients: +s.nonHcvSales.toFixed(2) })),
        hcv_odb: hcvOdbSeries,
        nonhcv_odb: nonHcvOdbSeries,
        hcv_nihb: hcvNihbSeries,
        nonhcv_nihb: nonHcvNihbSeries,
        hcv_psp: sortedMonths.map((s) => ({ date: labelFor(s.ym), Patients: +s.hcvPsp.toFixed(2) })),
        nonhcv_psp: sortedMonths.map((s) => ({ date: labelFor(s.ym), Patients: +s.nonHcvPsp.toFixed(2) })),
      },
      totals: {
        hcv_sales: sortedMonths.reduce((s, x) => s + x.hcvSales, 0),
        nonhcv_sales: sortedMonths.reduce((s, x) => s + x.nonHcvSales, 0),
        hcv_odb: hcvOdbSeries.reduce((s, x) => s + x.Patients, 0),
        nonhcv_odb: nonHcvOdbSeries.reduce((s, x) => s + x.Patients, 0),
        hcv_nihb: hcvNihbSeries.reduce((s, x) => s + x.Patients, 0),
        nonhcv_nihb: nonHcvNihbSeries.reduce((s, x) => s + x.Patients, 0),
        hcv_psp: sortedMonths.reduce((s, x) => s + x.hcvPsp, 0),
        nonhcv_psp: sortedMonths.reduce((s, x) => s + x.nonHcvPsp, 0),
      },
    };
  }, [transactions, date_range_start, date_range_end]);

  // When the user toggles HCV ↔ Pharmacy scope, drop the chart selection
  // so we don't render a chart whose tile is no longer visible.
  useEffect(() => {
    setChartsSelectedKey(null);
  }, [viewMode]);

  const avgPatientsPerDay = useMemo(() => {
    if (!visibleDays.length) return 0;
    const total = visibleDays.reduce((s, d) => s + d.unique_patients, 0);
    return total / visibleDays.length;
  }, [visibleDays]);

  const auditRows = useMemo(() => {
    let rows = zero_fee_entries;
    if (customRange) {
      rows = rows.filter(
        (z) =>
          z.service_date >= customRange.start &&
          z.service_date <= customRange.end
      );
    } else {
      if (selectedMonth !== "all") {
        rows = rows.filter((z) => z.service_date.startsWith(selectedMonth));
      }
      if (selectedDate !== "all") {
        rows = rows.filter((z) => z.service_date === selectedDate);
      }
    }
    // HCV course-fee rule: for each (patient × HCV drug), 1 fee covers the
    // whole 12-week course (8-week for Maviret). If any dispensing in a
    // course has a fee, exclude the $0 rows in that same course from the
    // audit — they're not actually missing fees, they're just the other
    // weeks of a properly-billed course.
    const COURSE_LEN = { EPCLUSA: 12, VOSEVI: 12, HARVONI: 12, MAVIRET: 8 };
    const hcvRowsByKey = new Map();
    for (const t of visibleTransactionsForFees) {
      const k = hcvDrugKey(t.drug_name);
      if (!k) continue;
      const key = `${t.patient}||${k}`;
      const arr = hcvRowsByKey.get(key) || [];
      arr.push(t);
      hcvRowsByKey.set(key, arr);
    }
    // Map "patient||drugKey||service_date" → courseIdx so we can label
    // each zero-fee entry with its course, then a set of (patient||drug||courseIdx)
    // courses that contain at least one fee-bearing dispensing.
    const dateToCourse = new Map();
    const coveredCourses = new Set();
    for (const [key, rs] of hcvRowsByKey) {
      const [, drugKey] = key.split("||");
      const chunk = COURSE_LEN[drugKey] || 12;
      const sorted = rs
        .slice()
        .sort((a, b) => (a.service_date || "").localeCompare(b.service_date || ""));
      sorted.forEach((r, idx) => {
        const courseIdx = Math.floor(idx / chunk);
        const tag = `${key}||${courseIdx}`;
        dateToCourse.set(`${r.patient}||${drugKey}||${r.service_date}||${r.rx_id || ""}`, tag);
        if ((r.fees || 0) > 0) coveredCourses.add(tag);
      });
    }
    rows = rows.filter((z) => {
      const k = hcvDrugKey(z.drug_name);
      if (!k) return true; // non-HCV rows always shown
      const tag = dateToCourse.get(
        `${z.patient}||${k}||${z.service_date}||${z.rx_id || ""}`
      );
      // If we couldn't match it back, keep the row (safer to show than hide).
      if (!tag) return true;
      return !coveredCourses.has(tag);
    });
    return rows;
  }, [
    zero_fee_entries,
    selectedDate,
    selectedMonth,
    customRange,
    visibleTransactionsForFees,
  ]);

  const downloadAuditCsv = () => {
    const rows = auditRows.map((r) => ({
      service_date: r.service_date,
      patient: r.patient,
      drug_name: r.drug_name,
      rx_id: r.rx_id,
      time: r.time,
    }));
    const tag =
      selectedDate === "all" ? "all-days" : selectedDate;
    exportToCsv(
      `zero-fee-audit_${tag}.csv`,
      ["service_date", "patient", "drug_name", "rx_id", "time"],
      rows
    );
  };

  return (
    <div className="min-h-screen" data-testid="dashboard-view">
      {/* Header — centered title, clean three-zone grid: back | title | print
           Padding-top respects iOS safe-area-inset so the title/date row
           never gets clipped by the Dynamic Island / notch on iPhone PWA
           (added to home screen, no Safari chrome to push us down). */}
      <header
        className="border-b border-[#E6EBE8] bg-white"
        style={{ paddingTop: `max(52px, env(safe-area-inset-top))` }}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-5 grid grid-cols-[auto_1fr_auto] items-center gap-3">
          <button
            data-testid="back-to-upload"
            onClick={onReset}
            className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-[#E6EBE8] bg-white text-[#1B3B2E] hover:bg-[#F7F9F8] transition-colors"
            aria-label="Upload another report"
            title={filename}
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
          </button>
          <div className="text-center min-w-0">
            <div className="font-heading text-base sm:text-xl font-semibold tracking-tight text-[#1B3B2E] truncate">
              Operations Dashboard
            </div>
            <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-[#5C746A] flex-wrap">
              <CalendarDays className="h-3.5 w-3.5 text-[#5C746A]" strokeWidth={1.75} />
              {(() => {
                // Resolve the effective filter currently in play so the header
                // is always honest about what every section below is showing.
                let s = date_range_start;
                let e = date_range_end;
                let isFiltered = false;
                if (customRange) {
                  s = customRange.start;
                  e = customRange.end;
                  isFiltered = true;
                } else if (selectedDate !== "all") {
                  s = selectedDate;
                  e = selectedDate;
                  isFiltered = true;
                } else if (selectedMonth !== "all") {
                  s = `${selectedMonth}-01`;
                  e = lastDayOfMonth(selectedMonth);
                  isFiltered = true;
                }
                const dayCount = isFiltered ? visibleDays.length : num_days;
                return (
                  <>
                    <button
                      type="button"
                      data-testid="open-range-dialog"
                      onClick={() => setRangeDialogOpen(true)}
                      className="font-mono-data text-[#3D4F46] hover:text-[#1B3B2E] underline-offset-4 hover:underline transition-colors"
                      title="Tap to change date range"
                    >
                      {s}
                      {s !== e ? ` → ${e}` : ""}
                    </button>
                    <span className="rounded-full bg-[#F0F4F2] text-[#2B5E4A] font-medium px-2 py-0.5">
                      {dayCount}d
                    </span>
                    {isFiltered ? (
                      <button
                        type="button"
                        data-testid="clear-range"
                        onClick={() => {
                          setCustomRange(null);
                          setSelectedMonth("all");
                          setSelectedDate("all");
                        }}
                        className="rounded-full bg-[#FFF2EF] text-[#B65A45] hover:bg-[#FDE5E0] font-medium px-2 py-0.5 transition-colors"
                        title="Clear filter — show full report"
                      >
                        Reset
                      </button>
                    ) : null}
                  </>
                );
              })()}
            </div>
          </div>
          <button
            data-testid="print-export-button"
            onClick={() => window.print()}
            className="no-print inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#E6EBE8] bg-white h-9 w-9 sm:w-auto sm:px-3 text-xs font-medium text-[#1B3B2E] hover:bg-[#F7F9F8] transition-all duration-200 hover:-translate-y-[1px]"
            aria-label="Print or save as PDF"
            title="Print or save as PDF"
          >
            <Printer className="h-4 w-4" strokeWidth={1.75} />
            <span className="hidden sm:inline">Print / PDF</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-6 sm:space-y-8">
        {/* Control bar removed — Product Scope + Date Range now live inside the header Filters dialog. */}

        {num_days === 0 && (
          <section
            data-testid="empty-view-state"
            className="reveal bg-white border border-dashed border-[#E6EBE8] rounded-2xl p-6 sm:p-10 text-center"
          >
            <div className="mx-auto h-10 w-10 rounded-full bg-[#F0F4F2] inline-flex items-center justify-center mb-3">
              <PackageOpen className="h-5 w-5 text-[#5C746A]" strokeWidth={1.75} />
            </div>
            <div className="font-heading text-base sm:text-lg font-semibold text-[#1B3B2E]">
              {viewMode === "hcv"
                ? "No HCV sales in this report"
                : "No Non-HCV sales in this report"}
            </div>
            <p className="text-xs sm:text-sm text-[#5C746A] mt-1.5 max-w-md mx-auto leading-snug">
              {viewMode === "hcv"
                ? "This report contains no dispensings of Epclusa, Vosevi, Maviret, or Harvoni — so there's nothing to project here. Switch to Non-HCV Products to see the rest of the P&L."
                : "Every dispensing in this report is an HCV medication. Switch to HCV Products to view its P&L."}
            </p>
          </section>
        )}

        {/* Processed in-memory notice — sits just above the first tab, indented to align with tab labels */}
        {num_days > 0 && (
          <div
            data-testid="in-memory-notice"
            className="px-3 sm:px-4 flex items-center justify-start gap-2 text-[11px] text-[#5C746A] -mb-2"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-[#2B5E4A] shrink-0" strokeWidth={2} />
            <span>Processed in-memory · never stored on our servers</span>
          </div>
        )}

        {/* Charts — top-of-dashboard quick view of full-data trends. Four
            tiles (HCV Sales / Pharmacy Sales / HCV ODB Cycles / Pharmacy
            ODB Cycles); tap one to render a dark-green monthly bar chart
            in the same style as the per-month Patient Volume cards. Uses
            the FULL data set, not the currently-filtered window. */}
        {num_days > 0 && chartsMonthly && (
          <CollapsibleBlock
            title="Charts"
            storageKey="top-charts"
            testid="top-charts-collapse"
            onHeaderTap={handleChartsTap}
          >
            <section
              data-testid="top-charts-section"
              className="bg-white border border-[#E6EBE8] rounded-xl p-3 sm:p-4 space-y-3"
            >
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {(viewMode === "hcv"
                  ? [
                      { key: "hcv_sales", label: "HCV Sales" },
                      { key: "hcv_odb", label: "HCV ODB Cycles" },
                      { key: "hcv_nihb", label: "HCV NIHB Cycles" },
                      { key: "hcv_psp", label: "HCV PSP Copays" },
                    ]
                  : [
                      { key: "nonhcv_sales", label: "Pharmacy Sales" },
                      { key: "nonhcv_odb", label: "Pharmacy ODB Cycles" },
                      { key: "nonhcv_nihb", label: "Pharmacy NIHB Cycles" },
                      { key: "nonhcv_psp", label: "Pharmacy PSP Copays" },
                    ]
                ).map(({ key, label }) => {
                  const active = chartsSelectedKey === key;
                  const total = chartsMonthly.totals[key] || 0;
                  return (
                    <button
                      key={key}
                      type="button"
                      data-testid={`top-chart-tile-${key}`}
                      onClick={() =>
                        setChartsSelectedKey(active ? null : key)
                      }
                      aria-pressed={active}
                      className={`text-left rounded-xl border p-3 transition-colors ${
                        active
                          ? "bg-[#1B3B2E] border-[#1B3B2E] text-white"
                          : "bg-[#F7F9F8] border-[#E6EBE8] text-[#1B3B2E] hover:bg-[#F0F4F2] active:bg-[#E6EBE8]"
                      }`}
                    >
                      <div
                        className={`text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.12em] ${
                          active ? "text-white/70" : "text-[#5C746A]"
                        }`}
                      >
                        {label}
                      </div>
                      <div className="font-mono-data text-sm sm:text-base font-semibold tabular-nums mt-1 whitespace-nowrap">
                        {formatCurrency(total)}
                      </div>
                      <div
                        className={`text-[10px] mt-0.5 ${
                          active ? "text-white/60" : "text-[#8A9C93]"
                        }`}
                      >
                        Tap to {active ? "hide" : "view"} chart
                      </div>
                    </button>
                  );
                })}
              </div>
              {chartsSelectedKey ? (() => {
                const series = chartsMonthly.series[chartsSelectedKey] || [];
                const titles = {
                  hcv_sales: "HCV Sales",
                  nonhcv_sales: "Pharmacy Sales",
                  hcv_odb: "HCV ODB Cycles",
                  nonhcv_odb: "Pharmacy ODB Cycles",
                  hcv_nihb: "HCV NIHB Cycles",
                  nonhcv_nihb: "Pharmacy NIHB Cycles",
                  hcv_psp: "HCV PSP Copays",
                  nonhcv_psp: "Pharmacy PSP Copays",
                };
                return (
                  <div
                    data-testid={`top-chart-${chartsSelectedKey}`}
                    className="rounded-xl border border-[#E6EBE8] bg-white p-3 sm:p-4"
                  >
                    <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
                      <div>
                        <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                          {titles[chartsSelectedKey]}
                        </div>
                        <h3 className="text-base sm:text-lg font-semibold text-[#1B3B2E] mt-0.5">
                          Monthly trend · full report
                        </h3>
                      </div>
                      <div className="font-mono-data text-base sm:text-lg font-semibold tabular-nums text-[#1B3B2E] whitespace-nowrap">
                        {formatCurrency(chartsMonthly.totals[chartsSelectedKey] || 0)}
                      </div>
                    </div>
                    <div className="-ml-2 overflow-x-auto overflow-y-hidden">
                      {(() => {
                        const isOdb = chartsSelectedKey.endsWith("_odb");
                        const isNihb = chartsSelectedKey.endsWith("_nihb");
                        const isCycle = isOdb || isNihb;
                        const perBar = isCycle ? 140 : 72;
                        // Floor at ~per-bar-slot so a 2-bar monthly chart doesn't get
                        // stretched into a wide empty box (bars then look far apart).
                        const w = Math.max(perBar + 80, series.length * perBar + 48);
                        // ODB charts: render a 2-line tick (window + pay date).
                        // eslint-disable-next-line react/no-unstable-nested-components
                        const OdbTick = ({ x, y, payload }) => {
                          const item = series.find((s) => s.date === payload.value);
                          return (
                            <g transform={`translate(${x},${y + 12})`}>
                              <text textAnchor="middle" fill="#5C746A" fontSize={12} fontFamily="JetBrains Mono">
                                {payload.value}
                              </text>
                              {item?.payDate ? (
                                <text y={14} textAnchor="middle" fill="#2B5E4A" fontSize={10} fontFamily="JetBrains Mono" fontWeight={600}>
                                  {item.payDate}
                                </text>
                              ) : null}
                            </g>
                          );
                        };
                        return (
                      <div
                        className={isOdb ? "h-80" : "h-72"}
                        style={{ minWidth: `${w}px`, width: `${w}px` }}
                      >
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={series} barCategoryGap="20%" margin={{ top: 8, right: 16, left: 0, bottom: isOdb ? 16 : 8 }}>
                            <CartesianGrid vertical={false} stroke="#E6EBE8" strokeDasharray="3 3" />
                            <XAxis
                              dataKey="date"
                              tick={isOdb ? <OdbTick /> : { fill: "#5C746A", fontSize: 12, fontFamily: "JetBrains Mono" }}
                              axisLine={{ stroke: "#E6EBE8" }}
                              tickLine={false}
                              interval={0}
                              height={isOdb ? 48 : 28}
                            />
                            <YAxis
                              tick={{ fill: "#5C746A", fontSize: 11, fontFamily: "JetBrains Mono" }}
                              axisLine={false}
                              tickLine={false}
                              width={64}
                              tickFormatter={(v) =>
                                v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`
                              }
                            />
                            <Tooltip
                              cursor={{ fill: "#F0F4F2" }}
                              content={<CurrencyTooltip />}
                            />
                            <Bar dataKey="Patients" name="Amount" fill="#2B5E4A" radius={[6, 6, 0, 0]} maxBarSize={64} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })() : null}
            </section>
          </CollapsibleBlock>
        )}

        {/* Patient Search — first section after the filters (top-of-dashboard quick lookup) */}
        {num_days > 0 && patientsBreakdown.length > 0 && (
          <CollapsibleBlock
            title="Patient Search"
            storageKey="patient-search"
            testid="patient-search-collapse"
          >
            <div className="bg-white border border-[#E6EBE8] rounded-xl overflow-hidden">
              {viewMode === "hcv" ? (
                <div
                  data-testid="hcv-fee-rule-banner"
                  className="px-4 sm:px-5 py-1.5 bg-[#F2F7F4] border-b border-[#CDE3D7] text-[10px] sm:text-[11px] text-[#2B5E4A] leading-tight"
                >
                  <span className="font-bold uppercase tracking-[0.12em]">
                    HCV rule ·{" "}
                  </span>
                  One dispensing fee per course. Annual revenue = actual course
                  value (not extrapolated).
                </div>
              ) : null}
              <div className="p-2.5 sm:p-3 border-b border-[#F0F4F2]">
                <label className="flex items-center gap-2.5 rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] px-3.5 py-2.5 focus-within:border-[#2B5E4A] focus-within:bg-white transition-colors">
                  <Search
                    className="h-4 w-4 text-[#5C746A] shrink-0"
                    strokeWidth={1.75}
                  />
                  <input
                    type="text"
                    data-testid="patient-search-input"
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    placeholder={`Search ${patientsBreakdown.length} patient${
                      patientsBreakdown.length === 1 ? "" : "s"
                    }…`}
                    className="flex-1 bg-transparent text-sm text-[#1B3B2E] focus:outline-none placeholder:text-[#A3B8AD] w-full"
                  />
                  {patientSearch ? (
                    <button
                      type="button"
                      data-testid="patient-search-clear"
                      onClick={() => setPatientSearch("")}
                      className="shrink-0 text-[#5C746A] hover:text-[#1B3B2E] transition-colors"
                      aria-label="Clear search"
                    >
                      <XIcon className="h-4 w-4" strokeWidth={2} />
                    </button>
                  ) : null}
                </label>
              </div>

              {/* Lost Patients — adherence-risk callout. Established
                  patients (≥ 7 lifetime dispense days) whose last
                  dispense is more than 7 days before the visible window's
                  most recent dispense date. Tap to open the same drill
                  dialog the search list uses so pharmacy staff can review
                  their history + call with context.
                  Hidden under HCV scope — HCV patients are dispensed on
                  8/12-week courses so "no dispense in 7 days" isn't a
                  meaningful adherence signal for them. */}
              {viewMode !== "hcv" && activeLostPatients.length > 0 ? (
                <details
                  data-testid="lost-patients"
                  className="border-b border-[#F0F4F2] group"
                >
                  <summary className="cursor-pointer list-none px-4 sm:px-5 py-2.5 bg-[#FBF3EE] hover:bg-[#F7EBE2] transition-colors flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1 flex items-center gap-2">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#D36C56] text-white text-[10px] font-bold shrink-0">
                        !
                      </span>
                      <div className="min-w-0">
                        <div className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.14em] text-[#8B3A24]">
                          Lost patients
                        </div>
                        <div className="text-[10px] text-[#8B3A24]/70 mt-0.5">
                          No dispense in 7 days
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                      <span
                        data-testid="lost-patients-count"
                        className="font-mono-data text-sm font-bold text-[#8B3A24] min-w-[24px] text-right tabular-nums"
                      >
                        {activeLostPatients.length}
                      </span>
                      {cohortPct.total > 0 ? (
                        <span
                          className="text-[10px] font-semibold text-[#8B3A24]/70 tabular-nums min-w-[28px] text-right"
                          data-testid="lost-patients-pct"
                        >
                          {cohortPct.lost}%
                        </span>
                      ) : null}
                      <CohortDownloadButton
                        label="Lost patients"
                        tint="lost"
                        testId="lost-patients-download"
                        onClick={() => downloadCohortPdf("lost")}
                      />
                      <ChevronDown
                        className="h-3.5 w-3.5 text-[#8B3A24] shrink-0 transition-transform group-open:rotate-180"
                        strokeWidth={2}
                      />
                    </div>
                  </summary>
                  <div className="max-h-[45vh] overflow-y-auto divide-y divide-[#F0F4F2] bg-white">
                    {activeLostPatients.slice(0, 100).map((lp) => (
                      <button
                        key={lp.patient}
                        type="button"
                        data-testid={`lost-patient-row-${lp.patient}`}
                        onClick={() =>
                          setDrillDown({
                            patient: lp.patient,
                            service_date: null,
                            pretty_date: "all visible dates",
                          })
                        }
                        className="w-full flex items-start justify-between gap-3 px-4 sm:px-5 py-2.5 text-left hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-[#1B3B2E] truncate">
                            {lp.patient}
                          </div>
                          <div className="text-[11px] text-[#5C746A] mt-0.5">
                            Last dispense · {lp.lastDate}
                          </div>
                          {lp.historyDays > 0 ? (
                            <div className="text-[11px] text-[#5C746A] mt-0.5">
                              {lp.historyDays} total dispense day
                              {lp.historyDays === 1 ? "" : "s"}
                            </div>
                          ) : null}
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="font-mono-data text-sm font-bold text-[#8B3A24] tabular-nums">
                            {lp.daysSince}d
                          </div>
                          <div className="text-[10px] text-[#8A9C93] mt-0.5">
                            days lost
                          </div>
                        </div>
                      </button>
                    ))}
                    {activeLostPatients.length > 100 ? (
                      <div className="px-5 py-3 text-[11px] text-[#8A9C93] text-center">
                        Showing first 100 of {activeLostPatients.length} lost
                        patients.
                      </div>
                    ) : null}
                  </div>
                </details>
              ) : null}

              {/* Discharged patients — non-HCV clients whose lifetime
                  engagement is < 7 total dispense days AND who haven't
                  filled in > 7 days. Likely one-off scripts (visitor,
                  urgent refill, single trial). Separated from Lost so
                  the follow-up funnel stays focused on established
                  patients. Same collapsible + tap-to-drill behaviour as
                  the Lost list. Uses a cooler slate palette to signal
                  "informational, not urgent". */}
              {viewMode !== "hcv" && dischargedPatients.length > 0 ? (
                <details
                  data-testid="discharged-patients"
                  className="border-b border-[#F0F4F2] group"
                >
                  <summary className="cursor-pointer list-none px-4 sm:px-5 py-2.5 bg-[#EEF2F4] hover:bg-[#E4EAED] transition-colors flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1 flex items-center gap-2">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#5A6C78] text-white shrink-0">
                        <UserMinus className="h-3 w-3" strokeWidth={2.5} />
                      </span>
                      <div className="min-w-0">
                        <div className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.14em] text-[#3A4A55]">
                          Discharged
                        </div>
                        <div className="text-[10px] text-[#3A4A55]/70 mt-0.5">
                          &lt; 7 lifetime dispense days
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                      <span
                        data-testid="discharged-patients-count"
                        className="font-mono-data text-sm font-bold text-[#3A4A55] min-w-[24px] text-right tabular-nums"
                      >
                        {dischargedPatients.length}
                      </span>
                      {cohortPct.total > 0 ? (
                        <span
                          className="text-[10px] font-semibold text-[#3A4A55]/70 tabular-nums min-w-[28px] text-right"
                          data-testid="discharged-patients-pct"
                        >
                          {cohortPct.discharged}%
                        </span>
                      ) : null}
                      <CohortDownloadButton
                        label="Discharged patients"
                        tint="discharged"
                        testId="discharged-patients-download"
                        onClick={() => downloadCohortPdf("discharged")}
                      />
                      <ChevronDown
                        className="h-3.5 w-3.5 text-[#3A4A55] shrink-0 transition-transform group-open:rotate-180"
                        strokeWidth={2}
                      />
                    </div>
                  </summary>
                  <div className="max-h-[45vh] overflow-y-auto divide-y divide-[#F0F4F2] bg-white">
                    {dischargedPatients.slice(0, 100).map((dp) => (
                      <button
                        key={dp.patient}
                        type="button"
                        data-testid={`discharged-patient-row-${dp.patient}`}
                        onClick={() =>
                          setDrillDown({
                            patient: dp.patient,
                            service_date: null,
                            pretty_date: "all visible dates",
                          })
                        }
                        className="w-full flex items-start justify-between gap-3 px-4 sm:px-5 py-2.5 text-left hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-[#1B3B2E] truncate">
                            {dp.patient}
                          </div>
                          <div className="text-[11px] text-[#5C746A] mt-0.5">
                            Last dispense · {dp.lastDate}
                          </div>
                          <div className="text-[11px] text-[#5C746A] mt-0.5">
                            {dp.historyDays} total dispense day
                            {dp.historyDays === 1 ? "" : "s"}
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="font-mono-data text-sm font-bold text-[#3A4A55] tabular-nums">
                            {dp.daysSince}d
                          </div>
                          <div className="text-[10px] text-[#8A9C93] mt-0.5">
                            days ago
                          </div>
                        </div>
                      </button>
                    ))}
                    {dischargedPatients.length > 100 ? (
                      <div className="px-5 py-3 text-[11px] text-[#8A9C93] text-center">
                        Showing first 100 of {dischargedPatients.length}{" "}
                        discharged patients.
                      </div>
                    ) : null}
                  </div>
                </details>
              ) : null}

              {/* Reactivated Patients — clients previously Lost or
                  Discharged who dispensed again within the last 7 days.
                  Banner is ALWAYS rendered (Non-HCV only) — even when
                  the list is empty — so the auditor can rely on its
                  position and see at a glance that no returns
                  occurred. Individual clients auto-drop off this list
                  once their return dispense is more than 7 days behind
                  the report's max date (they remain fully visible
                  under the Patients cohort below). */}
              {viewMode !== "hcv" ? (
                <details
                  data-testid="reactivated-patients-details"
                  className="border-b border-[#F0F4F2] group"
                >
                  <summary
                    data-testid="reactivated-patients-banner"
                    className="cursor-pointer list-none px-4 sm:px-5 py-2.5 bg-[#EEF2FF] hover:bg-[#E5EBFB] transition-colors flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#3D5AB5] text-white text-[10px] font-bold shrink-0">
                        ↻
                      </span>
                      <div className="min-w-0">
                        <div className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.14em] text-[#3D5AB5]">
                          Reactivated Patients
                        </div>
                        <div className="text-[10px] text-[#5A6B99] mt-0.5">
                          Returned within last 7 days
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                      <span
                        data-testid="reactivated-patients-count"
                        className="font-mono-data text-sm font-bold text-[#3D5AB5] min-w-[24px] text-right tabular-nums"
                      >
                        {reactivatedPatients.length}
                      </span>
                      {cohortPct.total > 0 ? (
                        <span
                          className="text-[10px] font-semibold text-[#3D5AB5]/70 tabular-nums min-w-[28px] text-right"
                          data-testid="reactivated-patients-pct"
                        >
                          {cohortPct.reactivated}%
                        </span>
                      ) : null}
                      <CohortDownloadButton
                        label="Reactivated patients"
                        tint="reactivated"
                        testId="reactivated-patients-download"
                        onClick={() => downloadCohortPdf("reactivated")}
                      />
                      <ChevronDown
                        className="h-3.5 w-3.5 text-[#3D5AB5] shrink-0 transition-transform group-open:rotate-180"
                        strokeWidth={2}
                      />
                    </div>
                  </summary>
                  <div className="divide-y divide-[#F0F4F2]" data-testid="reactivated-patients-list">
                    {reactivatedPatients.length === 0 ? (
                      <div className="px-5 py-4 text-center text-[11px] text-[#8A9C93]">
                        No patients have returned from a &gt;7-day gap in the last 7 days.
                      </div>
                    ) : (
                      reactivatedPatients.slice(0, 100).map((rp) => (
                        <button
                          key={rp.patient}
                          type="button"
                          className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-2 text-left hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors"
                          data-testid={`reactivated-patient-row-${rp.patient}`}
                          onClick={() =>
                            setDrillDown({
                              patient: rp.patient,
                              service_date: null,
                              pretty_date: "all visible dates",
                            })
                          }
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-[#1B3B2E] truncate">
                              {rp.patient}
                            </div>
                            <div className="text-[11px] text-[#8A9C93] mt-0.5 truncate">
                              Returned {formatShortDate(rp.lastDate)}
                              {" · "}
                              <span className="text-[#D36C56]">
                                {rp.gapDays}-day gap
                              </span>
                              {" · was "}
                              <span
                                className={
                                  rp.wasDischarged
                                    ? "text-[#3A4A55] font-medium"
                                    : "text-[#8B3A24] font-medium"
                                }
                              >
                                {rp.wasDischarged ? "discharged" : "lost"}
                              </span>
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <div
                              className="inline-flex items-center gap-1 rounded-full bg-[#EEF2FF] px-2 py-0.5 text-[10px] font-semibold text-[#3D5AB5] tabular-nums"
                              title="Days since this patient's return dispense — drops off after 7 days."
                            >
                              {rp.daysSinceLatest === 0
                                ? "today"
                                : `${rp.daysSinceLatest}d ago`}
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                    {reactivatedPatients.length > 100 ? (
                      <div className="px-5 py-3 text-[11px] text-[#8A9C93] text-center">
                        Showing first 100 of {reactivatedPatients.length}{" "}
                        reactivated patients.
                      </div>
                    ) : null}
                  </div>
                </details>
              ) : null}

              {/* New Clients — patients whose FIRST-EVER dispense across
                  the report falls within the last 7 days. Auto-expires
                  after 7 days (same 7-day window as Reactivated). Uses
                  a distinct amber accent so first-timers are visually
                  distinguishable from returning (blue) and long-term
                  active (green) patients. Non-HCV only. */}
              {viewMode !== "hcv" ? (
                <details
                  data-testid="new-clients-details"
                  className="border-b border-[#F0F4F2] group"
                >
                  <summary
                    data-testid="new-clients-banner"
                    className="cursor-pointer list-none px-4 sm:px-5 py-2.5 bg-[#FFF4E0] hover:bg-[#FCE9C7] transition-colors flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#B5761A] text-white text-[10px] font-bold shrink-0">
                        +
                      </span>
                      <div className="min-w-0">
                        <div className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.14em] text-[#B5761A]">
                          New Clients
                        </div>
                        <div className="text-[10px] text-[#8A6516] mt-0.5">
                          First dispense within last 7 days
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                      <span
                        data-testid="new-clients-count"
                        className="font-mono-data text-sm font-bold text-[#B5761A] min-w-[24px] text-right tabular-nums"
                      >
                        {newClients.length}
                      </span>
                      {cohortPct.total > 0 ? (
                        <span
                          className="text-[10px] font-semibold text-[#B5761A]/70 tabular-nums min-w-[28px] text-right"
                          data-testid="new-clients-pct"
                        >
                          {cohortPct.newClients}%
                        </span>
                      ) : null}
                      <CohortDownloadButton
                        label="New clients"
                        tint="new"
                        testId="new-clients-download"
                        onClick={() => downloadCohortPdf("new")}
                      />
                      <ChevronDown
                        className="h-3.5 w-3.5 text-[#B5761A] shrink-0 transition-transform group-open:rotate-180"
                        strokeWidth={2}
                      />
                    </div>
                  </summary>
                  <div className="divide-y divide-[#F0F4F2]" data-testid="new-clients-list">
                    {newClients.length === 0 ? (
                      <div className="px-5 py-4 text-center text-[11px] text-[#8A9C93]">
                        No first-time dispenses in the last 7 days.
                      </div>
                    ) : (
                      newClients.slice(0, 100).map((nc) => (
                        <button
                          key={nc.patient}
                          type="button"
                          className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-2 text-left hover:bg-[#FDF7EA] active:bg-[#FBEFD3] transition-colors"
                          data-testid={`new-client-row-${nc.patient}`}
                          onClick={() =>
                            setDrillDown({
                              patient: nc.patient,
                              service_date: null,
                              pretty_date: "all visible dates",
                            })
                          }
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-[#1B3B2E] truncate">
                              {nc.patient}
                            </div>
                            <div className="text-[11px] text-[#8A9C93] mt-0.5 truncate">
                              First seen {formatShortDate(nc.firstDate)}
                              {" · "}
                              <span className="text-[#B5761A]">
                                {nc.dispenseDays} dispense{nc.dispenseDays === 1 ? "" : "s"}
                              </span>
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <div
                              className="inline-flex items-center gap-1 rounded-full bg-[#FFF4E0] px-2 py-0.5 text-[10px] font-semibold text-[#B5761A] tabular-nums"
                              title="Days since this patient's first-ever dispense — drops off after 7 days."
                            >
                              {nc.daysSinceFirst === 0
                                ? "today"
                                : `${nc.daysSinceFirst}d ago`}
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                    {newClients.length > 100 ? (
                      <div className="px-5 py-3 text-[11px] text-[#8A9C93] text-center">
                        Showing first 100 of {newClients.length}{" "}
                        new clients.
                      </div>
                    ) : null}
                  </div>
                </details>
              ) : null}

              {(() => {
                const q = patientSearch.trim().toLowerCase();
                // Exclude anyone already surfaced under Lost or Discharged
                // pills — those are lifetime-adherence-derived (last
                // dispense > 7 days across the full PDF) and shouldn't
                // also appear here as an "active" patient. HCV view has
                // no Lost/Discharged concept so the filter no-ops there.
                const excluded = new Set();
                for (const lp of lostPatients) excluded.add(lp.patient);
                const activePool = viewMode === "hcv"
                  ? patientsBreakdown
                  : patientsBreakdown.filter((p) => !excluded.has(p.patient));
                const matches = q
                  ? activePool.filter((p) =>
                      p.patient.toLowerCase().includes(q)
                    )
                  : activePool;
                if (matches.length === 0) {
                  return (
                    <div className="px-5 py-6 text-center text-sm text-[#5C746A]">
                      No patients match &ldquo;{patientSearch}&rdquo;.
                    </div>
                  );
                }
                const shown = matches.slice(0, 100);
                return (
                  <div data-testid="patient-search-results">
                    {/* Green banner header for the "Patients" list —
                        mirrors the red Lost Patients pill above and uses the
                        same <details> disclosure pattern so both cohorts have
                        a consistent expand/collapse UX with matching chevron.
                        Defaulted OPEN so the roster still shows on first load. */}
                    <details
                      data-testid="patient-search-details"
                      className="border-b border-[#F0F4F2] group"
                    >
                      <summary
                        data-testid="patient-search-banner"
                        className="cursor-pointer list-none px-4 sm:px-5 py-2.5 bg-[#E8F0EB] hover:bg-[#DDE9E2] transition-colors flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#2B5E4A] text-white text-[10px] font-bold shrink-0">
                            ✓
                          </span>
                          <div className="min-w-0">
                            <div className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.14em] text-[#2B5E4A]">
                              {viewMode === "hcv" ? "HCV Patients" : "Active Patients"}
                            </div>
                            <div className="text-[10px] text-[#2B5E4A]/70 mt-0.5">
                              Dispensed within last 7 days
                            </div>
                          </div>
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                          <span
                            data-testid="patient-search-banner-count"
                            className="font-mono-data text-sm font-bold text-[#2B5E4A] min-w-[24px] text-right tabular-nums"
                          >
                            {matches.length}
                          </span>
                          {cohortPct.total > 0 ? (
                            <span
                              className="text-[10px] font-semibold text-[#2B5E4A]/70 tabular-nums min-w-[28px] text-right"
                              data-testid="patient-search-banner-pct"
                            >
                              {cohortPct.active}%
                            </span>
                          ) : null}
                          <CohortDownloadButton
                            label={viewMode === "hcv" ? "HCV patients" : "Active patients"}
                            tint="active"
                            testId="active-patients-download"
                            onClick={() => downloadCohortPdf("active")}
                          />
                          <ChevronDown
                            className="h-3.5 w-3.5 text-[#2B5E4A] shrink-0 transition-transform group-open:rotate-180"
                            strokeWidth={2}
                          />
                        </div>
                      </summary>
                      <div className="max-h-[55vh] overflow-y-auto divide-y divide-[#F0F4F2]">
                    {shown.map((p) => (
                      <button
                        key={p.patient}
                        type="button"
                        data-testid={`patient-search-row-${p.patient}`}
                        onClick={() =>
                          setDrillDown({
                            patient: p.patient,
                            service_date: null,
                            pretty_date: "all visible dates",
                          })
                        }
                        className="w-full flex items-stretch justify-between gap-3 px-4 sm:px-5 text-left hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors"
                      >
                        <div className="min-w-0 flex-1 flex flex-col justify-center py-2">
                          <div className="text-sm font-medium text-[#1B3B2E] truncate">
                            {p.patient}
                          </div>
                          <div className="text-[11px] text-[#8A9C93] mt-0.5 truncate">
                            <span className="text-[#5C746A]">{p.cadence}</span>
                            {" · "}
                            <span title="Total unique calendar days this patient has been dispensed a medication across the ENTIRE PDF report (lifetime dispensing days in this dataset).">
                              {p.historyDays} day{p.historyDays === 1 ? "" : "s"}
                            </span>
                          </div>
                          {/* Adherence + zero-fee moved to their own row to
                              keep the primary cadence + dispense-day line
                              readable at a glance on iPhone-width layouts. */}
                          {(viewMode === "non-hcv" || p.zero > 0) ? (
                            <div className="text-[11px] mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                              {viewMode === "non-hcv" ? (
                                <span
                                  className={
                                    p.adherence >= 80
                                      ? "text-[#2B5E4A] font-medium"
                                      : p.adherence >= 50
                                      ? "text-[#9C7A1D] font-medium"
                                      : "text-[#D36C56] font-medium"
                                  }
                                >
                                  {p.adherence}% adh.
                                </span>
                              ) : null}
                              {p.zero > 0 ? (
                                <span className="text-[#D36C56]">
                                  {p.zero} zero-fee
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                        {/* Middle column — anchored to the patient's
                            LAST dispense date. Uses NATURAL flex-column
                            centering (no position:absolute). Because
                            the outer row uses `items-stretch`, all
                            three columns match row height, AND the row
                            height grows to accommodate the tallest
                            column's content. This means the middle
                            content can NEVER overflow — the row is
                            always at least as tall as the middle needs.
                            All font sizes/spacings applied via inline
                            style for maximum specificity. */}
                        <div
                          className="shrink-0 w-[84px] sm:w-[96px] flex flex-col items-center justify-center py-2 border-x border-dashed border-[#E6EBE8]"
                          title="Distinct medications dispensed on this patient's last visit — green = fee collected, red = zero-fee that day."
                          data-testid={`patient-search-paid-meds-${p.patient}`}
                        >
                          {/* Inner wrapper — critical to work around an
                              iOS Safari flexbox bug where a stretched
                              parent + `justify-content: center` +
                              multiple children makes items behave like
                              `justify-content: space-between` (spread
                              across the full height with big gaps).
                              Wrapping all children in ONE flex item
                              makes the outer flex-col cluster it as a
                              single unit; the inner wrapper then
                              stacks its children as normal blocks with
                              the tight inline-styled margins. */}
                          <div className="flex flex-col items-center" style={{ lineHeight: 1 }}>
                            {p.lastDispenseDate ? (
                              <div
                                className="uppercase tracking-[0.12em] text-[#8A9C93]"
                                style={{ fontSize: 9, lineHeight: 1, marginBottom: 4 }}
                              >
                                {formatShortDate(p.lastDispenseDate)}
                              </div>
                            ) : null}
                            <div
                              className="font-heading font-semibold text-[#2B5E4A] tabular-nums"
                              style={{ fontSize: 20, lineHeight: 1 }}
                            >
                              {p.paidDrugCount}
                            </div>
                            <div
                              className="uppercase tracking-[0.12em] text-[#2B5E4A]/70"
                              style={{ fontSize: 9, lineHeight: 1, marginTop: 2 }}
                            >
                              {p.paidDrugCount === 1 ? "med" : "meds"}
                            </div>
                            {p.zeroFeeDrugCount > 0 ? (
                              <>
                                <div
                                  className="font-heading font-semibold text-[#D36C56] tabular-nums"
                                  style={{ fontSize: 14, lineHeight: 1, marginTop: 6 }}
                                  data-testid={`patient-search-zero-meds-${p.patient}`}
                                >
                                  {p.zeroFeeDrugCount}
                                </div>
                                <div
                                  className="uppercase tracking-[0.12em] text-[#D36C56]/80"
                                  style={{ fontSize: 9, lineHeight: 1, marginTop: 2 }}
                                >
                                  no fee
                                </div>
                              </>
                            ) : null}
                          </div>
                        </div>
                        <div className="shrink-0 w-[104px] sm:w-[120px] flex flex-col justify-center py-2 text-right">
                          <div className="font-mono-data text-sm text-[#1B3B2E]">
                            {formatCurrency(p.netFees)}
                          </div>
                          <div
                            className="text-[10px] text-[#8A9C93] mt-0.5"
                            title={
                              p.annualWindowDays
                                ? `Annualized from the last ${p.annualWindowDays} days of compliant dispensing (≥60% adherence). Ignores prior non-compliant history so the projection reflects the patient's current trajectory.`
                                : "Annualized from full lifetime dispensing fees ÷ tenure days × 365."
                            }
                          >
                            <span className="text-[#2B5E4A] font-medium">
                              {formatCurrency(p.annualValue)}
                            </span>
                            /yr
                            {p.annualWindowDays ? (
                              <span
                                className="ml-1 inline-block rounded-sm bg-[#EEF2FF] px-1 text-[8px] font-semibold text-[#3D5AB5] align-middle"
                                data-testid={`patient-search-recent-badge-${p.patient}`}
                              >
                                {p.annualWindowDays}d
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </button>
                    ))}
                    {matches.length > shown.length ? (
                      <div className="px-5 py-3 text-[11px] text-[#8A9C93] text-center">
                        Showing first 100 of {matches.length} matches — type to
                        narrow down.
                      </div>
                    ) : null}
                    </div>
                    </details>
                  </div>
                );
              })()}
            </div>
          </CollapsibleBlock>
        )}

        {/* Birthday Lookup — upload a PDF with patient name + DOB, then
            filter to a month within the visible period. Only patients who
            appear on the loaded sales report show up — this is intentional
            (the rule is: the DOB list can be a superset, but only matches
            who are also clients in the visible window are surfaced). */}
        {num_days > 0 && patientsBreakdown.length > 0 && (
          <CollapsibleBlock
            title="Birthday Lookup"
            storageKey="birthday-lookup"
            testid="birthday-lookup-collapse"
          >
            {(() => {
              // Set of patient names currently visible in the dashboard
              // (case-normalized so the LASTNAME, FIRSTNAME match is robust
              // to whitespace differences). Also keep a name → stats map so
              // we can surface adherence % alongside the birthday row for
              // at-a-glance compliance triage.
              const visibleSet = new Set();
              const visibleStats = new Map();
              for (const p of patientsBreakdown) {
                const key = (p.patient || "").trim().toUpperCase().replace(/\s+/g, " ");
                visibleSet.add(key);
                visibleStats.set(key, p);
              }
              // Months covered by the currently visible period — derived
              // from the actual visible transactions so the month picker
              // never offers a month that has zero data.
              const monthSet = new Set();
              for (const t of visibleTransactions) {
                if (t.service_date && t.service_date.length >= 7) {
                  monthSet.add(t.service_date.slice(0, 7)); // "YYYY-MM"
                }
              }
              const visibleMonths = Array.from(monthSet).sort();
              // Render all 12 months in the picker so the user can see
              // birthdays in months that fall outside the visible report
              // period (the underlying sales-report filter still applies —
              // only patients on the visible report ever appear in results).
              // We synthesize a fixed year for the pill keys; matching uses
              // the month number only.
              const allMonths = Array.from({ length: 12 }, (_, i) => `2000-${String(i + 1).padStart(2, "0")}`);
              // Default the selected month to the first month present in the
              // currently visible period so the initial view is relevant.
              const effectiveBirthdayMonth =
                birthdayMonth
                  ? birthdayMonth
                  : (visibleMonths[0] || allMonths[0]);
              const MONTH_NAMES = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December",
              ];
              const labelForMonth = (ym) => {
                const [y, m] = ym.split("-").map(Number);
                return `${MONTH_NAMES[m - 1]} ${y}`;
              };
              // Compute age as of today.
              const today = new Date();
              const ageFor = (dob) => {
                if (!dob) return null;
                const [y, m, d] = dob.split("-").map(Number);
                if (!y || !m || !d) return null;
                let age = today.getFullYear() - y;
                if (
                  today.getMonth() + 1 < m ||
                  (today.getMonth() + 1 === m && today.getDate() < d)
                ) {
                  age -= 1;
                }
                return age;
              };
              const fmtDob = (dob) => {
                const [y, m, d] = dob.split("-").map(Number);
                if (!y || !m || !d) return dob;
                return `${MONTH_NAMES[m - 1]} ${d}, ${y}`;
              };

              // Match birthdays → visible patients (exact match, normalized).
              // Filter to selected month if one is chosen.
              const matched = (birthdayList || [])
                .filter((b) =>
                  visibleSet.has((b.patient || "").trim().toUpperCase().replace(/\s+/g, " "))
                )
                .filter((b) => {
                  if (!effectiveBirthdayMonth) return true;
                  const dobMonth = b.dob ? Number(b.dob.split("-")[1]) : 0;
                  return dobMonth === Number(effectiveBirthdayMonth.split("-")[1]);
                })
                .map((b) => {
                  const key = (b.patient || "").trim().toUpperCase().replace(/\s+/g, " ");
                  const stats = visibleStats.get(key);
                  return {
                    ...b,
                    age: ageFor(b.dob),
                    adherence:
                      stats && typeof stats.adherence === "number"
                        ? stats.adherence
                        : null,
                  };
                })
                .sort((a, b) => {
                  // Sort by day-of-month so birthdays read chronologically.
                  const da = a.dob ? Number(a.dob.split("-")[2]) : 0;
                  const db = b.dob ? Number(b.dob.split("-")[2]) : 0;
                  return da - db;
                });

              const onUpload = async (e) => {
                const file = e.target.files && e.target.files[0];
                e.target.value = ""; // allow re-upload of same file
                if (!file) return;
                setBirthdayUploading(true);
                setBirthdayError("");
                try {
                  const res = await uploadBirthdayPdf(file);
                  setBirthdayList(res.patients || []);
                } catch (err) {
                  const msg =
                    err && err.response && err.response.data && err.response.data.detail
                      ? err.response.data.detail
                      : err && err.message
                        ? err.message
                        : "Upload failed.";
                  setBirthdayError(msg);
                } finally {
                  setBirthdayUploading(false);
                }
              };

              return (
                <div className="bg-white border border-[#E6EBE8] rounded-xl overflow-hidden">
                  {/* Upload row */}
                  <div className="p-4 sm:p-5 border-b border-[#F0F4F2] flex flex-col sm:flex-row sm:items-center gap-3">
                    <label
                      className={`relative inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer shrink-0 ${
                        birthdayUploading
                          ? "bg-[#F0F4F2] text-[#8A9C93] cursor-wait"
                          : "bg-[#1B3B2E] text-white hover:bg-[#2B5E4A]"
                      }`}
                      data-testid="birthday-upload-label"
                    >
                      <Upload className="h-4 w-4" strokeWidth={1.75} />
                      {birthdayUploading
                        ? "Reading PDF…"
                        : birthdayList
                          ? "Replace PDF"
                          : "Upload DOB PDF"}
                      <input
                        type="file"
                        accept="application/pdf"
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        disabled={birthdayUploading}
                        onChange={onUpload}
                        data-testid="birthday-upload-input"
                      />
                    </label>
                    <div className="text-[11px] sm:text-xs text-[#5C746A] flex-1 min-w-0">
                      {birthdayList
                        ? null
                        : "Upload a PDF containing patient names with dates of birth (session only — not saved)."}
                    </div>
                    {birthdayList ? (
                      <button
                        type="button"
                        onClick={() => {
                          setBirthdayList(null);
                          setBirthdayMonth("");
                          setBirthdayError("");
                        }}
                        className="text-[11px] sm:text-xs text-[#5C746A] hover:text-[#1B3B2E] underline underline-offset-2 shrink-0"
                        data-testid="birthday-clear"
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>

                  {birthdayError ? (
                    <div
                      data-testid="birthday-upload-error"
                      className="px-4 sm:px-5 py-2.5 bg-[#FBEEEA] border-b border-[#F0D4CB] text-[11px] sm:text-xs text-[#B65A45]"
                    >
                      {birthdayError}
                    </div>
                  ) : null}

                  {birthdayList ? (
                    <>
                      {/* Month picker — restricted to months that exist in the
                          currently visible period. Laid out as a 6-column
                          grid so Jan–Jun fit on one row on iPhone and
                          Jul–Dec wrap to a clean second row. */}
                      <div className="p-4 sm:p-5 border-b border-[#F0F4F2]">
                        <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.15em] text-[#5C746A] mb-2">
                          Birth month
                        </div>
                        <div className="grid grid-cols-6 gap-1.5">
                          {allMonths.map((ym) => {
                            const monthNum = Number(ym.split("-")[1]);
                            const isActive =
                              Number(effectiveBirthdayMonth.split("-")[1]) === monthNum;
                            return (
                              <button
                                key={ym}
                                type="button"
                                onClick={() => setBirthdayMonth(ym)}
                                data-testid={`birthday-month-${ym}`}
                                className={`px-2 py-1.5 rounded-full text-[11px] sm:text-xs font-medium transition-colors ${
                                  isActive
                                    ? "bg-[#1B3B2E] text-white"
                                    : "bg-[#F7F9F8] text-[#5C746A] hover:bg-[#F0F4F2]"
                                }`}
                              >
                                {MONTH_NAMES[monthNum - 1].slice(0, 3)}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Results */}
                      {matched.length === 0 ? (
                        <div
                          data-testid="birthday-no-results"
                          className="px-5 py-6 text-center text-sm text-[#5C746A]"
                        >
                          {effectiveBirthdayMonth
                            ? `No visible patients have a birthday in ${MONTH_NAMES[Number(effectiveBirthdayMonth.split("-")[1]) - 1]}.`
                            : "None of the patients in the uploaded PDF appear on the visible sales report."}
                        </div>
                      ) : (
                        <div
                          className="max-h-[55vh] overflow-y-auto divide-y divide-[#F0F4F2]"
                          data-testid="birthday-results"
                        >
                          {matched.map((r) => (
                            <button
                              key={`${r.patient}-${r.dob}`}
                              type="button"
                              data-testid={`birthday-row-${r.patient}`}
                              onClick={() =>
                                setDrillDown({
                                  patient: r.patient,
                                  service_date: null,
                                  pretty_date: "all visible dates",
                                })
                              }
                              className="w-full px-4 sm:px-5 py-3 flex items-start justify-between gap-3 text-left hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-[#1B3B2E] truncate">
                                  {r.patient}
                                </div>
                                <div className="text-[11px] text-[#8A9C93] mt-0.5 tabular-nums">
                                  {fmtDob(r.dob)}
                                  {r.adherence != null ? (
                                    <>
                                      {" · "}
                                      <span
                                        className={
                                          r.adherence >= 80
                                            ? "text-[#2B5E4A] font-medium"
                                            : r.adherence >= 50
                                              ? "text-[#9C7A1D] font-medium"
                                              : "text-[#D36C56] font-medium"
                                        }
                                      >
                                        {r.adherence}% adh.
                                      </span>
                                    </>
                                  ) : null}
                                </div>
                              </div>
                              <div className="shrink-0 text-right">
                                <div className="font-mono-data text-base font-light text-[#1B3B2E] tabular-nums">
                                  {r.age != null ? r.age : "—"}
                                </div>
                                <div className="text-[9px] sm:text-[10px] text-[#8A9C93] uppercase tracking-[0.1em] mt-0.5">
                                  yrs old
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              );
            })()}
          </CollapsibleBlock>
        )}

        {/* Patient Volume Chart - multi-month aware */}
        {isMultiDay && (
          <CollapsibleBlock
            title="Patient Volumes"
            storageKey="patient-volume"
            testid="patient-volume-collapse"
          >
          <>
            {isMultiMonth && selectedMonth === "all" ? (
              <section
                data-testid="patient-volume-chart"
                className="reveal reveal-delay-3 space-y-4 sm:space-y-6"
              >
                {perMonthCharts.map((mc) => (
                  <PatientVolumeChartCard
                    key={mc.ym}
                    title="Patient Volumes"
                    subtitle={mc.monthLabel}
                    data={mc.data}
                    avg={mc.avg}
                    numDays={mc.days.length}
                    testid={`patient-volume-chart-${mc.ym}`}
                    showAvg={viewMode !== "hcv"}
                    totalPatients={mc.uniquePatients}
                    totalLabel="Total HCV patients"
                  />
                ))}
              </section>
            ) : (
              <PatientVolumeChartCard
                title="Patient Volumes"
                subtitle={
                  selectedMonth === "all"
                    ? "Unique patients seen each day"
                    : monthsAvailable.find((m) => m.ym === selectedMonth)?.label
                }
                data={chartData}
                avg={avgPatientsPerDay}
                numDays={visibleDays.length}
                testid="patient-volume-chart"
                wrapClass="reveal reveal-delay-3"
                showAvg={viewMode !== "hcv"}
                totalPatients={summary.unique_patients}
                totalLabel={selectedMonth === "all" ? "Total HCV patients" : "Total monthly patients"}
              />
            )}
          </>
          </CollapsibleBlock>
        )}

        {/* Drug Payments — Plan 1 reimbursements split by payer. ODB cycles
             show in chronological cards (paid 15th/30th). NIHB clients appear
             in their own block below for the same selected date range. */}
        {(odbCycleDeposits.length > 0 || nihbCycleDeposits.length > 0) && (
          <CollapsibleBlock
            title="Drug Payments"
            storageKey="odb-cycles"
            testid="odb-cycles-collapse"
          >
            <section
              data-testid="odb-cycles"
              className="reveal reveal-delay-2 rounded-2xl border border-[#E6EBE8] bg-white overflow-hidden"
            >
              {/* Hide the ODB deposit section entirely when the user picks
                  NIHB cycles ONLY from the filters — they have different
                  pay cadences and mixing both in the same view is
                  confusing. Combined "both" selection shows both sections. */}
              {customRange?.source !== "nihb" && (
              <>
              <div className="px-4 sm:px-5 py-3 bg-[#E8F0EB] border-b border-[#C7DBCF] flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="inline-flex items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#1B3B2E]">
                    <Banknote className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={1.75} />
                    ODB Deposits
                  </div>
                  <div className="text-[11px] text-[#1B3B2E]/70 mt-0.5">
                    Ontario Drug Benefit · paid on the 15th &amp; 30th of each month
                  </div>
                </div>
                {/* Auditor-friendly payment-ledger downloads. Snapshots the
                    currently-visible date range so the PDF reflects the
                    same window the user is auditing on screen. Options
                    popover controls the split mode (Supplier / Program)
                    and lets the user redact patient names + acquisition
                    cost before download. */}
                <div className="flex gap-2 flex-wrap items-start">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        data-testid="export-options-btn"
                        className="text-[11px] font-semibold uppercase tracking-[0.1em] px-3 py-1.5 rounded-md border border-[#C7DBCF] text-[#1B3B2E] bg-white hover:bg-[#F0F4F2] active:translate-y-px transition-colors inline-flex items-center gap-1.5"
                      >
                        Options
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-72 p-3 space-y-3"
                      data-testid="export-options-menu"
                      onCloseAutoFocus={(e) => e.preventDefault()}
                    >
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C746A] mb-1.5">
                          Split ledger by
                        </div>
                        <div className="flex rounded-md border border-[#C7DBCF] overflow-hidden flex-wrap">
                          {[
                            { v: "supplier", l: "Supplier" },
                            { v: "420",      l: "George" },
                            { v: "HepCURE",  l: "Racquel" },
                            { v: "Malachi",  l: "Malachi" },
                            { v: "Maria",    l: "Maria" },
                          ].map((opt) => (
                            <button
                              key={opt.v}
                              type="button"
                              data-testid={`export-split-${opt.v}`}
                              onClick={() => setExportSplitMode(opt.v)}
                              className={`flex-1 min-w-[33%] text-[10px] font-semibold uppercase tracking-[0.08em] px-2 py-1.5 transition-colors ${
                                exportSplitMode === opt.v
                                  ? "bg-[#1B3B2E] text-white"
                                  : "bg-white text-[#1B3B2E] hover:bg-[#F0F4F2]"
                              }`}
                            >
                              {opt.l}
                            </button>
                          ))}
                        </div>
                        {exportSplitMode !== "supplier" ? (
                          <div className="mt-1.5 flex items-center justify-between text-[10px] text-[#5C746A] gap-2">
                            <span data-testid="export-program-count">
                              {(() => {
                                const vals = Object.values(patientProgramMap);
                                const inMode = vals.filter((p) => p === exportSplitMode).length;
                                const excluded = vals.filter((p) => p === "Excluded").length;
                                // Display name mapping — DB keys stay as "420"/"HepCURE"
                                // for backward compat; UI shows the human names.
                                const displayName =
                                  exportSplitMode === "420" ? "George" :
                                  exportSplitMode === "HepCURE" ? "Racquel" :
                                  exportSplitMode;
                                return (
                                  <>
                                    <span className="font-semibold text-[#1B3B2E]">
                                      {inMode}
                                    </span>{" "}
                                    in {displayName}
                                    {excluded > 0 ? (
                                      <span className="ml-1.5 text-[#5C746A]">
                                        · {excluded} excluded
                                      </span>
                                    ) : null}
                                  </>
                                );
                              })()}
                            </span>
                            {Object.keys(patientProgramMap).length > 0 ? (
                              <button
                                type="button"
                                data-testid="export-clear-programs"
                                onClick={clearAllPatientPrograms}
                                className="text-[#D36C56] font-semibold uppercase tracking-[0.08em] hover:underline shrink-0"
                              >
                                Clear overrides
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                      <div className="border-t border-[#F0F4F2] pt-2 space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid="export-hide-patient-names"
                            checked={exportHidePatientNames}
                            onChange={(e) => setExportHidePatientNames(e.target.checked)}
                            className="h-3.5 w-3.5 accent-[#1B3B2E]"
                          />
                          <span className="text-[11px] text-[#1B3B2E]">
                            Hide patient names
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid="export-mask-acq-cost"
                            checked={exportMaskAcqCost}
                            onChange={(e) => setExportMaskAcqCost(e.target.checked)}
                            className="h-3.5 w-3.5 accent-[#1B3B2E]"
                          />
                          <span className="text-[11px] text-[#1B3B2E]">
                            Remove acquisition cost column
                          </span>
                        </label>
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <button
                    type="button"
                    data-testid="export-odb-pdf"
                    onClick={() => {
                      const dates = (visibleDays || []).map((d) => d.service_date).filter(Boolean).sort();
                      exportOdbPaymentPdf({
                        transactions: visibleTransactions,
                        rangeStart: dates[0] || null,
                        rangeEnd: dates[dates.length - 1] || null,
                        scope: viewMode === "hcv" ? "hcv" : viewMode === "non-hcv" ? "non-hcv" : "all",
                        programByPatient: patientProgramMap,
                        splitMode: exportSplitMode,
                        maskPatientNames: exportHidePatientNames,
                        maskAcquisitionCost: exportMaskAcqCost,
                      });
                    }}
                    className="text-[11px] font-semibold uppercase tracking-[0.1em] px-3 py-1.5 rounded-md bg-[#1B3B2E] text-white hover:bg-[#2B5E4A] active:translate-y-px transition-colors inline-flex items-center gap-1.5"
                  >
                    <Download className="h-3.5 w-3.5" strokeWidth={2} />
                    ODB PDF
                  </button>
                  {/* NIHB export is HCV/mixed-only — NIHB doesn't apply
                      to regular pharmacy dispensings, so it's hidden when
                      the dashboard is in the non-HCV view. */}
                  {viewMode !== "non-hcv" && (
                    <button
                      type="button"
                      data-testid="export-nihb-pdf"
                      onClick={() => {
                        const dates = (visibleDays || []).map((d) => d.service_date).filter(Boolean).sort();
                        exportNihbPaymentPdf({
                          transactions: visibleTransactions,
                          rangeStart: dates[0] || null,
                          rangeEnd: dates[dates.length - 1] || null,
                          scope: viewMode === "hcv" ? "hcv" : "all",
                          programByPatient: patientProgramMap,
                          splitMode: exportSplitMode,
                          maskPatientNames: exportHidePatientNames,
                          maskAcquisitionCost: exportMaskAcqCost,
                        });
                      }}
                      className="text-[11px] font-semibold uppercase tracking-[0.1em] px-3 py-1.5 rounded-md border border-[#1B3B2E] text-[#1B3B2E] bg-white hover:bg-[#F0F4F2] active:translate-y-px transition-colors inline-flex items-center gap-1.5"
                    >
                      <Download className="h-3.5 w-3.5" strokeWidth={2} />
                      NIHB PDF
                    </button>
                  )}
                </div>
              </div>
              {(() => {
                // Only surface cycles that actually have Plan 1 dispensings
                // inside the selected range. Hides future/empty cycles so
                // there's no $0 / 0-dispensing row to confuse the reader.
                const visible = odbCycleDeposits.filter((c) => c.count > 0);
                // Order newest-first by pay date, but pin in-progress/upcoming at the top.
                const ordered = [...visible].sort((a, b) => {
                  const rank = (s) =>
                    s === "in-progress" ? 0 : s === "upcoming" ? 1 : 2;
                  if (rank(a.status) !== rank(b.status))
                    return rank(a.status) - rank(b.status);
                  return b.payDate.localeCompare(a.payDate);
                });
                if (ordered.length === 0) {
                  // Mirror the NIHB empty-state style so a $0 ODB total
                  // reads as intentional (user picked a cycle with no
                  // sales) rather than a rendering bug.
                  const pickedOdbExplicitly =
                    Array.isArray(customRange?.odbCycleKeys) &&
                    customRange.odbCycleKeys.length > 0;
                  return (
                    <div
                      data-testid="odb-cycles-empty"
                      className="px-4 sm:px-5 py-5 text-center text-xs text-[#2B5E4A] bg-[#F2F7F4]"
                    >
                      <div className="font-semibold uppercase tracking-[0.12em] text-[10px] mb-1">
                        {pickedOdbExplicitly
                          ? "No ODB sales in this cycle"
                          : "No ODB sales in the selected range"}
                      </div>
                      <div className="text-[11px] text-[#2B5E4A]/75 normal-case tracking-normal">
                        Nothing to reconcile.
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="divide-y divide-[#F0F4F2]">
                    {ordered.map((c) => {
                      const isInProgress = c.status === "in-progress";
                      const isUpcoming = c.status === "upcoming";
                      const badgeClasses = isInProgress
                        ? "bg-[#FFF8E7] text-[#9C7A1D] border-[#F1E1B5]"
                        : isUpcoming
                          ? "bg-[#F2F7F4] text-[#2B5E4A] border-[#CDE3D7]"
                          : "bg-[#F7F9F8] text-[#5C746A] border-[#E6EBE8]";
                      const badgeLabel = isInProgress
                        ? "In progress"
                        : isUpcoming
                          ? "Upcoming"
                          : "Paid";
                      return (
                        <button
                          type="button"
                          key={c.key}
                          data-testid={`odb-cycle-${c.key}`}
                          onClick={() => setOdbDrill({ ...c, _scope: "ODB" })}
                          className="w-full text-left px-4 sm:px-5 py-3.5 flex items-start justify-between gap-3 hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-heading text-sm sm:text-base font-semibold text-[#1B3B2E]">
                                {formatPayDate(c.payDate)}
                              </span>
                              <span
                                className={`inline-flex items-center text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full border ${badgeClasses}`}
                              >
                                {badgeLabel}
                              </span>
                            </div>
                            <div className="text-[10px] sm:text-[11px] text-[#8A9C93] mt-0.5 tabular-nums whitespace-nowrap truncate">
                              Cycle {c.type} · {c.shortLabel}
                            </div>
                            <div className="text-[10px] sm:text-[11px] text-[#8A9C93] mt-0.5 tabular-nums">
                              {c.count} dispensing{c.count === 1 ? "" : "s"} · Tap to audit
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-heading text-base sm:text-lg font-semibold text-[#1B3B2E] tabular-nums whitespace-nowrap">
                              {formatCurrency(c.plan1)}
                            </div>
                            <div className="text-[9px] sm:text-[10px] text-[#8A9C93] uppercase tracking-[0.1em] mt-0.5">
                              Plan 1
                            </div>
                            {c.odbDrugCost > 0.005 ? (
                              <>
                                <div className="font-heading text-xs sm:text-sm font-medium text-[#9C7A1D] tabular-nums whitespace-nowrap mt-1.5">
                                  − {formatCurrency(c.odbDrugCost)}
                                </div>
                                <div className="text-[9px] sm:text-[10px] text-[#9C7A1D]/80 uppercase tracking-[0.1em] mt-0.5">
                                  Drug cost (to supplier)
                                </div>
                              </>
                            ) : null}
                            <div className="font-heading text-sm sm:text-base font-semibold text-[#2B5E4A] tabular-nums whitespace-nowrap mt-1.5">
                              {formatCurrency(c.odbProfit)}
                            </div>
                            <div className="text-[9px] sm:text-[10px] text-[#8A9C93] uppercase tracking-[0.1em] mt-0.5 tabular-nums">
                              ODB profit · {c.odbMarkupPct}%
                            </div>
                            {c.rebateProfit > 0.005 ? (
                              <>
                                <div className="font-heading text-sm sm:text-base font-medium text-[#2B5E4A]/80 tabular-nums whitespace-nowrap mt-1.5">
                                  + {formatCurrency(c.rebateProfit)}
                                </div>
                                <div className="text-[9px] sm:text-[10px] text-[#8A9C93] uppercase tracking-[0.1em] mt-0.5 tabular-nums">
                                  Rebate (later) · {c.odbRebatePct}%
                                </div>
                                {c.odbRebateBreakdown && c.odbRebateBreakdown.length >= 1 ? (
                                  <div className="mt-0.5 space-y-0.5">
                                    {c.odbRebateBreakdown.map((b) => (
                                      <div
                                        key={`odb-rebate-${c.key}-${b.drug}`}
                                        className="text-[9px] sm:text-[10px] text-[#8A9C93]/80 tabular-nums whitespace-nowrap"
                                      >
                                        · {b.drug} {b.rebatePct}% · {formatCurrency(b.amount)}
                                      </div>
                                    ))}
                                  </div>
                                ) : null}
                                <div className="mt-2 pt-2 border-t border-dashed border-[#CDE3D7]">
                                  <div className="font-heading text-base sm:text-lg font-bold text-[#1B3B2E] tabular-nums whitespace-nowrap">
                                    {formatCurrency(c.odbProfit + c.rebateProfit)}
                                  </div>
                                  <div className="text-[9px] sm:text-[10px] text-[#2B5E4A] font-bold uppercase tracking-[0.1em] mt-0.5 tabular-nums">
                                    Total profit · {c.odbGpPct}%
                                  </div>
                                </div>
                              </>
                            ) : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
              <div className="px-4 sm:px-5 py-2.5 bg-[#F7F9F8] border-t border-[#F0F4F2] text-[10px] text-[#8A9C93]">
                Cycle A pays the 15th · Cycle B pays the 30th · ODB profit = markup + net fees (paid with deposit) · Rebate arrives later from the manufacturer
              </div>
              </>
              )}

              {/* NIHB section — bi-weekly payment cycles that DO NOT
                  align with the ODB semi-monthly schedule. Each 14-day
                  window ends Saturday, deposit lands the following
                  Wednesday. Card set is built from the standalone
                  nihbCycleDeposits memo so drilling in shows the true
                  NIHB deposit window, not an ODB proxy. */}
              {customRange?.source !== "odb" && (() => {
                const nihbCycles = nihbCycleDeposits.filter(
                  (c) => c.nihbPlan1 > 0.005 || c.nihbCount > 0
                );
                // Track whether the user EXPLICITLY picked NIHB cycles.
                // When they did but every picked cycle came back empty,
                // we still render the section — with a "No NIHB sales
                // in this cycle" note — so the auditor can visually
                // confirm the $0 is intentional rather than a bug.
                const pickedNihbExplicitly =
                  Array.isArray(customRange?.nihbCycleKeys) &&
                  customRange.nihbCycleKeys.length > 0;
                if (nihbCycles.length === 0 && !pickedNihbExplicitly) return null;
                return (
                  <div className="border-t-4 border-[#F0F4F2]" data-testid="nihb-cycles">
                    <div className="px-4 sm:px-5 py-3 bg-[#F4E4C5]/30 border-b border-[#E6D8B0]">
                      <div className="inline-flex items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#8A6B2B]">
                        <Banknote className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={1.75} />
                        NIHB receivables
                      </div>
                      <div className="text-[11px] text-[#8A6B2B]/80 mt-0.5">
                        Federal Non-Insured Health Benefits · paid on a separate schedule from ODB
                      </div>
                    </div>
                    {nihbCycles.length === 0 ? (
                      <div
                        data-testid="nihb-cycles-empty"
                        className="px-4 sm:px-5 py-5 text-center text-xs text-[#8A6B2B] bg-[#FDFAF3]"
                      >
                        <div className="font-semibold uppercase tracking-[0.12em] text-[10px] mb-1">
                          No NIHB sales in this cycle
                        </div>
                        <div className="text-[11px] text-[#8A6B2B]/75 normal-case tracking-normal">
                          Nothing to reconcile.
                        </div>
                      </div>
                    ) : (
                    <>
                    <div className="divide-y divide-[#F0F4F2]">
                      {nihbCycles.map((c) => (
                        <button
                          type="button"
                          key={`nihb-${c.key}`}
                          data-testid={`nihb-cycle-${c.key}`}
                          onClick={() => setOdbDrill({ ...c, _scope: "NIHB" })}
                          className="w-full px-4 sm:px-5 py-3.5 flex items-start justify-between gap-3 text-left hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors"
                        >
                          <div className="min-w-0">
                            <div className="font-heading text-base sm:text-lg font-medium text-[#1B3B2E] truncate">
                              {c.shortLabel}
                            </div>
                            <div className="text-[11px] text-[#5C746A] truncate mt-0.5">
                              {c.nihbCount} dispensing{c.nihbCount === 1 ? "" : "s"} · Tap to audit
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-heading text-base sm:text-lg font-semibold text-[#1B3B2E] tabular-nums whitespace-nowrap">
                              {formatCurrency(c.nihbPlan1)}
                            </div>
                            <div className="text-[9px] sm:text-[10px] text-[#8A9C93] uppercase tracking-[0.1em] mt-0.5">
                              NIHB receivable
                            </div>
                            {c.nihbDrugCost > 0.005 ? (
                              <>
                                <div className="font-heading text-xs sm:text-sm font-medium text-[#9C7A1D] tabular-nums whitespace-nowrap mt-1.5">
                                  − {formatCurrency(c.nihbDrugCost)}
                                </div>
                                <div className="text-[9px] sm:text-[10px] text-[#9C7A1D]/80 uppercase tracking-[0.1em] mt-0.5">
                                  Drug cost (to supplier)
                                </div>
                              </>
                            ) : null}
                            {c.nihbProfit > 0.005 ? (
                              <>
                                <div className="font-heading text-sm sm:text-base font-semibold text-[#2B5E4A] tabular-nums whitespace-nowrap mt-1.5">
                                  {formatCurrency(c.nihbProfit)}
                                </div>
                                <div className="text-[9px] sm:text-[10px] text-[#8A9C93] uppercase tracking-[0.1em] mt-0.5 tabular-nums">
                                  NIHB profit · {c.nihbMarkupPct}%
                                </div>
                              </>
                            ) : null}
                            {c.nihbRebate > 0.005 ? (
                              <>
                                <div className="font-heading text-sm sm:text-base font-medium text-[#2B5E4A]/80 tabular-nums whitespace-nowrap mt-1.5">
                                  + {formatCurrency(c.nihbRebate)}
                                </div>
                                <div className="text-[9px] sm:text-[10px] text-[#8A9C93] uppercase tracking-[0.1em] mt-0.5 tabular-nums">
                                  Rebate (later) · {c.nihbRebatePct}%
                                </div>
                                {c.nihbRebateBreakdown && c.nihbRebateBreakdown.length >= 1 ? (
                                  <div className="mt-0.5 space-y-0.5">
                                    {c.nihbRebateBreakdown.map((b) => (
                                      <div
                                        key={`nihb-rebate-${c.key}-${b.drug}`}
                                        className="text-[9px] sm:text-[10px] text-[#8A9C93]/80 tabular-nums whitespace-nowrap"
                                      >
                                        · {b.drug} {b.rebatePct}% · {formatCurrency(b.amount)}
                                      </div>
                                    ))}
                                  </div>
                                ) : null}
                                <div className="mt-2 pt-2 border-t border-dashed border-[#CDE3D7]">
                                  <div className="font-heading text-base sm:text-lg font-bold text-[#1B3B2E] tabular-nums whitespace-nowrap">
                                    {formatCurrency(c.nihbProfit + c.nihbRebate)}
                                  </div>
                                  <div className="text-[9px] sm:text-[10px] text-[#2B5E4A] font-bold uppercase tracking-[0.1em] mt-0.5 tabular-nums">
                                    Total profit · {c.nihbGpPct}%
                                  </div>
                                </div>
                              </>
                            ) : null}
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="px-4 sm:px-5 py-2.5 bg-[#F7F9F8] border-t border-[#F0F4F2] text-[10px] text-[#8A9C93]">
                      NIHB profit = markup + net fees · Rebate arrives later from the manufacturer
                    </div>
                    </>
                    )}
                  </div>
                );
              })()}

              {/* Combined ODB + NIHB profitability for the selected period.
                  Sums every cycle in view (both payers) and shows the blended
                  GP% against total drug cost so the user gets one final
                  bottom-line takeaway.
                  Hidden when the user has explicitly picked an ODB-only or
                  NIHB-only cycle from the filters — mixing the two payers'
                  totals under a single-payer selection is confusing.
                  Still shows for Full Range / Month / Day / no-source. */}
              {customRange?.source !== "odb" && customRange?.source !== "nihb" && (() => {
                // Sum ODB portion from odbCycleDeposits + NIHB portion
                // from nihbCycleDeposits. This is critical when the user
                // picks a specific ODB cycle AND a separate NIHB cycle
                // whose windows don't overlap — the old logic iterated
                // only odbCycleDeposits and pulled NIHB claims that
                // happened to fall inside each ODB cycle window, which
                // dropped every NIHB claim outside the picked ODB window.
                // Because ODB.plan1 counts only ODB-classified patients
                // and NIHB.nihbPlan1 counts only NIHB-classified
                // patients, summing them across their own cycle sets is
                // both complete AND double-count-free — including for
                // Full Range / Month / Day where ODB cycles cover every
                // calendar day.
                const odbTotals = odbCycleDeposits.reduce(
                  (acc, c) => {
                    acc.drugCost += (c.odbDrugCost || 0);
                    acc.deposits += (c.plan1 || 0);
                    acc.markupProfit += (c.odbProfit || 0);
                    acc.rebateProfit += (c.rebateProfit || 0);
                    return acc;
                  },
                  { drugCost: 0, deposits: 0, markupProfit: 0, rebateProfit: 0 }
                );
                const nihbTotals = nihbCycleDeposits.reduce(
                  (acc, c) => {
                    acc.drugCost += (c.nihbDrugCost || 0);
                    acc.deposits += (c.nihbPlan1 || 0);
                    acc.markupProfit += (c.nihbProfit || 0);
                    acc.rebateProfit += (c.nihbRebate || 0);
                    return acc;
                  },
                  { drugCost: 0, deposits: 0, markupProfit: 0, rebateProfit: 0 }
                );
                const totals = {
                  drugCost: odbTotals.drugCost + nihbTotals.drugCost,
                  deposits: odbTotals.deposits + nihbTotals.deposits,
                  markupProfit: odbTotals.markupProfit + nihbTotals.markupProfit,
                  rebateProfit: odbTotals.rebateProfit + nihbTotals.rebateProfit,
                };
                totals.profit = totals.markupProfit + totals.rebateProfit;
                if (totals.profit < 0.005 && totals.drugCost < 0.005) return null;
                const blendedGp =
                  totals.drugCost > 0
                    ? +((totals.profit / totals.drugCost) * 100).toFixed(1)
                    : 0;
                return (
                  <div
                    className="border-t-4 border-[#1B3B2E]/10 bg-gradient-to-br from-[#F2F7F4] to-[#E8F0EB] px-4 sm:px-5 py-4"
                    data-testid="cycle-combined-total"
                  >
                    <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-[#2B5E4A]/70">
                      Combined ODB + NIHB · selected period
                    </div>
                    {/* Zero-sales note — reassures the auditor that a $0
                        contribution from one payer is intentional (no
                        dispensings in the picked window), not a bug. */}
                    {(odbTotals.deposits < 0.005 || nihbTotals.deposits < 0.005) && (
                      <div
                        data-testid="cycle-combined-zero-note"
                        className="mt-2 rounded-md border border-dashed border-[#B99A5C]/50 bg-[#FDFAF3] px-2.5 py-1.5 text-[10px] leading-snug text-[#8A6B2B]"
                      >
                        {odbTotals.deposits < 0.005 && nihbTotals.deposits < 0.005
                          ? "No ODB or NIHB sales in the selected cycles."
                          : odbTotals.deposits < 0.005
                            ? "No ODB sales in the selected cycle — total reflects NIHB only."
                            : "No NIHB sales in the selected cycle — total reflects ODB only."}
                      </div>
                    )}
                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
                      <div>
                        <div className="text-[9px] sm:text-[10px] text-[#5C746A] uppercase tracking-[0.1em]">
                          Receivables
                        </div>
                        <div className="font-heading text-sm sm:text-base font-semibold text-[#1B3B2E] tabular-nums">
                          {formatCurrency(totals.deposits)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[9px] sm:text-[10px] text-[#9C7A1D]/80 uppercase tracking-[0.1em]">
                          Drug cost (supplier)
                        </div>
                        <div className="font-heading text-sm sm:text-base font-semibold text-[#9C7A1D] tabular-nums">
                          − {formatCurrency(totals.drugCost)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[9px] sm:text-[10px] text-[#5C746A] uppercase tracking-[0.1em]">
                          Markup + net fees
                        </div>
                        <div className="font-heading text-sm sm:text-base font-medium text-[#2B5E4A] tabular-nums">
                          {formatCurrency(totals.markupProfit)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[9px] sm:text-[10px] text-[#5C746A] uppercase tracking-[0.1em]">
                          Rebate (later)
                        </div>
                        <div className="font-heading text-sm sm:text-base font-medium text-[#2B5E4A]/80 tabular-nums">
                          + {formatCurrency(totals.rebateProfit)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-dashed border-[#1B3B2E]/15 flex items-end justify-between gap-3">
                      <div>
                        <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.15em] text-[#1B3B2E]">
                          Total profit
                        </div>
                        <div className="text-[10px] text-[#5C746A] mt-0.5">
                          Blended GP · {blendedGp}%
                        </div>
                      </div>
                      <div className="font-heading text-xl sm:text-2xl font-bold text-[#1B3B2E] tabular-nums whitespace-nowrap">
                        {formatCurrency(totals.profit)}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </section>
          </CollapsibleBlock>
        )}

        {/* Generic Rebates — additional monthly income source, folded into the P&L
             below. Hidden for HCV scope: HCV rebates are auto-computed from
             manufacturer rates per row (3% / 0.5%) rather than uploaded cheques. */}
        {num_days > 0 && viewMode !== "hcv" && (
          <CollapsibleBlock
            title="Purchasing Rebates · Non-HCV"
            storageKey="rebates-v2"
            defaultOpen={false}
            testid="rebates-collapse"
          >
            <GenericRebatesCard
              rebates={rebates}
              onChange={setRebates}
              monthsAvailable={monthsAvailable}
              viewMode={viewMode}
              visibleMonths={Array.from(
                new Set(visibleDays.map((d) => d.service_date.slice(0, 7)))
              )}
              visibleDayCount={visibleDays.length}
            />
          </CollapsibleBlock>
        )}

        {/* Top Line Sales — gross dollar volume billed to Plan 1 + Plan 2, with monthly/annual projection and margin % */}
        {num_days > 0 && topLineSales.combined > 0 && (
          <CollapsibleBlock
            title="Top Line Sales"
            storageKey="top-line-sales"
            testid="top-line-sales-collapse"
          >
            {(() => {
              const planCombined = topLineSales.combined;
              // True Gross Profit percentage — what we keep AS A PERCENT
              // OF DRUG COST (cost of goods sold). This is the
              // "markup-over-cost" view the pharmacy thinks in: 5% ODB
              // markup + 3% manufacturer rebate + dispensing fee per
              // claim → ~8%. The previous "margin over revenue" view was
              // mathematically valid but answered a different question
              // (what % of the dollar we billed did we keep, ~7.6%).
              const drugCost = topLineSales.drugCost || 0;
              const revenueMargin = drugCost > 0
                ? +((topLineSales.ourRevenue / drugCost) * 100).toFixed(1)
                : 0;
              return (
                <section
                  data-testid="top-line-sales"
                  className="reveal reveal-delay-1 rounded-2xl border border-[#E6EBE8] bg-white p-5 sm:p-7 space-y-5 sm:space-y-6"
                >
                  {/* Header strap */}
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="inline-flex items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                        <Banknote className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={1.75} />
                        Top Line Sales
                      </div>
                      <p className="mt-1.5 text-[11px] sm:text-xs text-[#5C746A] leading-snug max-w-md">
                        Gross dollar volume billed to Plan 1 + Plan 2. Plan 4 (waived $2 co-pay) is excluded.
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] sm:text-xs font-medium uppercase tracking-[0.12em] text-[#5C746A]">
                        Span
                      </div>
                      <div
                        data-testid="top-line-span-days"
                        className="font-heading text-sm sm:text-base font-semibold text-[#1B3B2E]"
                      >
                        {topLineSales.spanDays} day{topLineSales.spanDays === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>

                  {/* Plan totals — Plan 1 and Plan 2 side-by-side, Combined gets its own full-width strip below for breathing room. Tap any card to validate. */}
                  <div className="space-y-2.5 sm:space-y-3">
                    <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                      <button
                        type="button"
                        data-testid="top-line-plan-1"
                        onClick={() => setTopLineDrill("plan1")}
                        className="text-left rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] p-3 sm:p-4 hover:border-[#1B3B2E] hover:bg-white active:bg-[#F0F4F2] transition-colors"
                      >
                        <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.12em] text-[#5C746A]">
                          Plan 1
                        </div>
                        <div className="mt-1.5 font-heading text-base sm:text-2xl font-semibold text-[#1B3B2E] tabular-nums whitespace-nowrap">
                          {formatCurrency(topLineSales.plan1)}
                        </div>
                        <div className="mt-0.5 text-[9px] sm:text-[10px] text-[#8A9C93] uppercase tracking-[0.1em]">
                          Tap to validate
                        </div>
                      </button>
                      <button
                        type="button"
                        data-testid="top-line-plan-2"
                        onClick={() => setTopLineDrill("plan2")}
                        className="text-left rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] p-3 sm:p-4 hover:border-[#1B3B2E] hover:bg-white active:bg-[#F0F4F2] transition-colors"
                      >
                        <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.12em] text-[#5C746A]">
                          Plan 2
                        </div>
                        <div className="mt-1.5 font-heading text-base sm:text-2xl font-semibold text-[#1B3B2E] tabular-nums whitespace-nowrap">
                          {formatCurrency(topLineSales.plan2)}
                        </div>
                        <div className="mt-0.5 text-[9px] sm:text-[10px] text-[#8A9C93] uppercase tracking-[0.1em]">
                          Tap to validate
                        </div>
                      </button>
                    </div>
                    <button
                      type="button"
                      data-testid="top-line-plan-combined"
                      onClick={() => setTopLineDrill("combined")}
                      className="w-full text-left rounded-xl border border-[#1B3B2E] bg-[#1B3B2E] text-white p-4 sm:p-5 hover:bg-[#2B5E4A] active:bg-[#163127] transition-colors flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.12em] text-[#A3B8AD]">
                          Combined · Plan 1 {topLineSales.pspOffset > 0 ? "− PSP (HCV)" : "+ Plan 2"}
                        </div>
                        <div className="mt-0.5 text-[9px] sm:text-[10px] text-[#A3B8AD] uppercase tracking-[0.1em]">
                          Tap to validate
                          {topLineSales.pspOffset > 0
                            ? ` · PSP offset ${formatCurrency(topLineSales.pspOffset)}`
                            : ""}
                        </div>
                      </div>
                      <div className="font-heading text-xl sm:text-3xl font-semibold tabular-nums whitespace-nowrap shrink-0">
                        {formatCurrency(planCombined)}
                      </div>
                    </button>
                  </div>

                  {/* Plan 4 informational note — captures waived $2 co-pay that we DON'T actually collect, so it's excluded from sales/margin math entirely. */}
                  {topLineSales.plan4 > 0.5 || topLineSales.plan3 > 0.5 ? (
                    <div className="rounded-xl border border-dashed border-[#E6EBE8] bg-[#F7F9F8] px-3.5 py-2.5 space-y-1.5">
                      {topLineSales.plan4 > 0.5 ? (
                        <div
                          data-testid="top-line-plan-4-waived"
                          className="flex items-center justify-between gap-3 text-[11px] sm:text-xs leading-snug"
                        >
                          <span className="text-[#5C746A] truncate">
                            Plan 4 · waived $2 co-pay
                          </span>
                          <span className="font-heading font-semibold text-[#5C746A] tabular-nums shrink-0 whitespace-nowrap">
                            {formatCurrency(topLineSales.plan4)}
                          </span>
                        </div>
                      ) : null}
                      {topLineSales.plan3 > 0.5 ? (
                        <div
                          data-testid="top-line-plan-3"
                          className="flex items-center justify-between gap-3 text-[11px] sm:text-xs leading-snug"
                        >
                          <span className="text-[#5C746A] truncate">
                            Plan 3 · excluded from Combined
                          </span>
                          <span className="font-heading font-semibold text-[#5C746A] tabular-nums shrink-0 whitespace-nowrap">
                            {formatCurrency(topLineSales.plan3)}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {/* Annual Average projection — actual numbers are above; this
                       is the only projection. Hidden for HCV: courses are one-
                       time per patient so "daily × 365" massively overshoots. */}
                  {viewMode !== "hcv" && (
                  <div
                    data-testid="top-line-annual-avg"
                    className="rounded-xl border border-[#E6EBE8] p-3.5 sm:p-4"
                  >
                    <div className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold uppercase tracking-[0.12em] text-[#5C746A]">
                      <TrendingUp className="h-3 w-3 sm:h-3.5 sm:w-3.5" strokeWidth={1.75} />
                      Annual Avg.
                    </div>
                    <div className="mt-1.5 font-heading text-xl sm:text-2xl font-semibold text-[#1B3B2E] tabular-nums whitespace-nowrap">
                      {formatCurrency(topLineSales.annualEst)}
                    </div>
                    <div className="text-[10px] sm:text-[11px] text-[#5C746A] mt-0.5">
                      365-day projection · {topLineSales.spanDays}-day basis
                    </div>
                  </div>
                  )}

                  {/* Monthly Breakdown — total Plan 1 + Plan 2 sales for each month touched by the visible range. */}
                  {(() => {
                    const monthMap = new Map();
                    for (const t of visibleTransactions) {
                      const ym = (t.service_date || "").slice(0, 7);
                      if (!ym) continue;
                      const amt = combinedAmountForRow(t);
                      if (amt <= 0) continue;
                      const prev = monthMap.get(ym) || { plan1: 0, plan2: 0, total: 0, count: 0, psp: 0 };
                      prev.plan1 += t.plan1_paid || 0;
                      prev.plan2 += t.plan2_paid || 0;
                      if (isPspRow(t)) prev.psp += pspAmountForRow(t);
                      prev.total += amt;
                      prev.count += 1;
                      monthMap.set(ym, prev);
                    }
                    if (monthMap.size === 0) return null;
                    const monthRows = Array.from(monthMap.entries())
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([ym, v]) => {
                        const [yr, mo] = ym.split("-");
                        const label = new Date(Number(yr), Number(mo) - 1, 1)
                          .toLocaleString("en-CA", { month: "short", year: "numeric" });
                        return { ym, label, ...v };
                      });
                    return (
                      <div
                        data-testid="top-line-monthly-breakdown"
                        className="rounded-xl border border-[#E6EBE8] overflow-hidden"
                      >
                        <div className="px-3.5 sm:px-4 py-2.5 bg-[#F7F9F8] border-b border-[#E6EBE8]">
                          <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                            Sales by Month
                          </div>
                          <div className="text-[10px] sm:text-[11px] text-[#8A9C93] mt-0.5">
                            Combined P1 + P2 by month
                          </div>
                        </div>
                        <div className="divide-y divide-[#F0F4F2]">
                          {monthRows.map((m) => (
                            <div
                              key={m.ym}
                              data-testid={`top-line-month-${m.ym}`}
                              className="flex items-center justify-between gap-3 px-3.5 sm:px-4 py-2.5"
                            >
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-[#1B3B2E]">
                                  {m.label}
                                </div>
                                <div className="text-[10px] sm:text-[11px] text-[#8A9C93] mt-0.5 tabular-nums whitespace-nowrap truncate">
                                  {m.count} disp.
                                  {m.plan2 > 0.5 ? (
                                    <>
                                      {" · P1 "}
                                      <span className="font-mono-data">{formatCurrency(m.plan1)}</span>
                                      {" · P2 "}
                                      <span className="font-mono-data">{formatCurrency(m.plan2)}</span>
                                    </>
                                  ) : null}
                                </div>
                              </div>
                              <div className="font-heading text-base sm:text-lg font-semibold text-[#1B3B2E] tabular-nums whitespace-nowrap shrink-0">
                                {formatCurrency(m.total)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Gross Profit % — what we keep on every dollar billed to Plan 1 + Plan 2.
                       Tap to drill into the underlying transaction list with
                       per-row dispensing fee, markup and (HCV) auto-rebate so
                       the total can be audited row by row. */}
                  <button
                    type="button"
                    data-testid="top-line-revenue-margin"
                    onClick={() => setTopLineDrill("gross-profit")}
                    className="w-full text-left rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] p-3.5 sm:p-5 hover:bg-white active:bg-[#F0F4F2] transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                        Gross Profit %
                      </div>
                      <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.12em] text-[#2B5E4A]">
                        Tap to validate →
                      </span>
                    </div>
                    <div className="mt-1.5 font-heading text-2xl sm:text-3xl font-semibold text-[#1B3B2E] tabular-nums whitespace-nowrap">
                      {revenueMargin}%
                    </div>
                    <div className="text-[10px] sm:text-[11px] text-[#5C746A] mt-1 tabular-nums whitespace-nowrap">
                      <span>{formatCurrency(topLineSales.ourRevenue)}</span>
                      <span className="text-[#8A9C93]"> kept on </span>
                      <span>{formatCurrency(topLineSales.drugCost)}</span>
                      <span className="text-[#8A9C93]"> drug cost</span>
                    </div>
                  </button>
                </section>
              );
            })()}
          </CollapsibleBlock>
        )}

        {/* PSP Reimbursement — Patient Support Program / Plan 2 audit.
             • HCV view: HCV claims where Plan 2 > 0 (PSP-covered deductible).
             • Non-HCV view: all non-HCV claims with Plan 2 > 0 — surfaces
               pharmacy-side co-pays for audit. */}
        {(() => {
          const isHcvScope = viewMode === "hcv";
          const rowMatches = (t) => {
            // HCV scope: use Pat/Ded Paid (or plan2_paid fallback) — HCV PSP
            // deductibles populate the Pat/Ded column on the source PDF.
            // Non-HCV scope: still keys off plan2_paid (true secondary plan
            // co-pays for pharmacy audit).
            const psp = isHcvScope ? pspAmountForRow(t) : (t.plan2_paid || 0);
            if (psp <= 0) return false;
            return isHcvScope ? !!hcvDrugKey(t.drug_name) : !hcvDrugKey(t.drug_name);
          };
          const pspRows = allVisibleTransactions
            .filter(rowMatches)
            .map((t) => ({
              ...t,
              _amount: isHcvScope ? pspAmountForRow(t) : (t.plan2_paid || 0),
            }));
          // Always render the section (even with no PSP rows in range) so it's
          // a stable, discoverable tab on the dashboard. When empty, show a
          // clear placeholder explaining what would fill it.
          const pspTotal = pspRows.reduce((s, r) => s + r._amount, 0);
          const pspClients = new Set(pspRows.map((r) => r.patient)).size;
          // Bucket by patient for the collapsed-by-client list.
          const byPatient = new Map();
          for (const r of pspRows) {
            const slot = byPatient.get(r.patient) || {
              patient: r.patient,
              claims: [],
              drugs: new Set(),
              total: 0,
              p1Total: 0,
            };
            slot.claims.push(r);
            if (r.drug_name) slot.drugs.add(canonicalDrugName(r.drug_name));
            slot.total += r._amount || 0;
            slot.p1Total += r.plan1_paid || 0;
            byPatient.set(r.patient, slot);
          }
          const patientList = Array.from(byPatient.values())
            .map((p) => ({
              ...p,
              total: +p.total.toFixed(2),
              p1Total: +p.p1Total.toFixed(2),
              drugList: Array.from(p.drugs),
            }))
            .sort((a, b) => b.total - a.total);
          return (
            <CollapsibleBlock
              title={isHcvScope ? "PSP Reimbursement · HCV" : "Plan 2 Audit · Pharmacy"}
              storageKey="psp-reimbursement"
              testid="psp-reimbursement-collapse"
            >
              <section
                data-testid="psp-reimbursement"
                className="reveal rounded-2xl border border-[#E6EBE8] bg-white p-5 sm:p-7 space-y-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="inline-flex items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                      <ShieldCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={1.75} />
                      {isHcvScope ? "PSP Reimbursement" : "Plan 2 Audit"}
                    </div>
                    <h2 className="font-heading text-lg sm:text-xl font-medium text-[#1B3B2E] mt-1">
                      {isHcvScope
                        ? "HCV deductibles paid by Patient Support Programs"
                        : "Non-HCV claims with a Plan 2 co-pay"}
                    </h2>
                    <p className="text-[11px] sm:text-xs text-[#5C746A] mt-1 leading-relaxed">
                      {isHcvScope
                        ? "Tracks every HCV claim where Plan 2 has a non-zero payment — that amount is the patient's deductible covered by PSP, already offset out of the Top Line Combined."
                        : "Every non-HCV dispensing where Plan 2 has a non-zero payment. Use this list to audit secondary-plan / patient co-pay amounts feeding into the Pharmacy PSP Copays tile above."}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] p-3 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                      Clients
                    </div>
                    <div
                      data-testid="psp-clients-count"
                      className="font-mono-data text-xl sm:text-2xl font-light text-[#1B3B2E] mt-1"
                    >
                      {pspClients}
                    </div>
                    <div className="text-[10px] text-[#8A9C93] mt-0.5">
                      {pspRows.length} dispensing{pspRows.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="rounded-xl border border-[#1B3B2E] bg-[#1B3B2E] text-white p-3 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3B8AD]">
                      {isHcvScope ? "PSP Reimbursed" : "Plan 2 Total"}
                    </div>
                    <div
                      data-testid="psp-total"
                      className="font-mono-data text-base sm:text-xl font-light mt-1 tabular-nums whitespace-nowrap overflow-hidden text-ellipsis"
                    >
                      {formatCurrency(pspTotal)}
                    </div>
                  </div>
                </div>

                <div className="border border-[#E6EBE8] rounded-xl overflow-hidden divide-y divide-[#F0F4F2]">
                  {patientList.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[#5C746A]">
                      {isHcvScope
                        ? "No HCV claims with PSP (Plan 2) reimbursement in the current date range."
                        : "No non-HCV claims with a Plan 2 payment in the current date range."}
                    </div>
                  ) : (
                  patientList.map((p) => {
                    const isOpen = pspExpandedPatient === p.patient;
                    return (
                      <div
                        key={p.patient}
                        data-testid={`psp-patient-${p.patient}`}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setPspExpandedPatient(isOpen ? null : p.patient)
                          }
                          aria-expanded={isOpen}
                          data-testid={`psp-patient-toggle-${p.patient}`}
                          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors"
                        >
                          <div className="min-w-0 flex-1 flex items-center gap-2">
                            {isOpen ? (
                              <ChevronDown className="h-4 w-4 text-[#5C746A] shrink-0" strokeWidth={2} />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-[#5C746A] shrink-0" strokeWidth={2} />
                            )}
                            <div className="min-w-0">
                              <div className="font-semibold text-[#1B3B2E] truncate text-sm">
                                {p.patient}
                              </div>
                              <div className="text-[10px] sm:text-[11px] text-[#8A9C93] tabular-nums truncate mt-0.5">
                                {p.claims.length} disp.{p.drugList.length ? ` · ${p.drugList.join(", ")}` : ""}
                              </div>
                            </div>
                          </div>
                          <div className="font-mono-data font-semibold text-[#1B3B2E] tabular-nums whitespace-nowrap text-sm sm:text-base shrink-0">
                            {formatCurrency(p.total)}
                          </div>
                        </button>
                        {isOpen ? (
                          <div className="bg-[#F7F9F8] border-t border-[#F0F4F2]">
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead className="text-[10px] uppercase tracking-[0.1em] text-[#5C746A] font-semibold">
                                  <tr>
                                    <th className="text-left px-3 py-2 font-semibold">Date</th>
                                    <th className="text-left px-3 py-2 font-semibold">Drug · Rx</th>
                                    <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Plan 1</th>
                                    <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">PSP (P2)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[...p.claims]
                                    .sort((a, b) => a.service_date.localeCompare(b.service_date))
                                    .map((r, idx) => (
                                      <tr
                                        key={`${r.rx_id || idx}-${r.service_date}`}
                                        data-testid={`psp-row-${p.patient}-${idx}`}
                                        className="border-t border-[#E6EBE8] text-[#3D4F46]"
                                      >
                                        <td className="px-3 py-2 font-mono-data whitespace-nowrap">
                                          {r.service_date}
                                        </td>
                                        <td className="px-3 py-2 min-w-[8rem]">
                                          <div className="text-[#1B3B2E] truncate">
                                            {canonicalDrugName(r.drug_name)}
                                          </div>
                                          <div className="text-[10px] text-[#8A9C93] font-mono-data">
                                            Rx {r.rx_id}
                                          </div>
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap text-[#5C746A]">
                                          {formatCurrency(r.plan1_paid || 0)}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap font-semibold text-[#1B3B2E]">
                                          {formatCurrency(r._amount)}
                                        </td>
                                      </tr>
                                    ))}
                                  <tr className="border-t border-[#E6EBE8] bg-white text-[#1B3B2E] font-semibold">
                                    <td className="px-3 py-2" colSpan={3}>
                                      Subtotal ({p.claims.length} disp.)
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap">
                                      {formatCurrency(p.total)}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                  )}
                </div>
              </section>
            </CollapsibleBlock>
          );
        })()}

        {/* Revenue Banner — gross monthly/annual with co-pay waiver deducted to show net.
             Hidden for HCV scope: HCV courses are one-time so daily→annual
             extrapolation produces misleading numbers. */}
        {num_days > 0 && viewMode !== "hcv" && (
        <CollapsibleBlock
          title="Projected Dispensing Fee Revenue"
          storageKey="revenue-banner"
          testid="revenue-banner-collapse"
        >
        <section
          data-testid="revenue-banner"
          className="reveal reveal-delay-1 relative overflow-hidden rounded-2xl border border-[#1B3B2E] bg-[#1B3B2E] text-white p-5 sm:p-8"
        >
          <div
            aria-hidden
            className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#2B5E4A] opacity-50 blur-3xl"
          />
          <div className="relative space-y-5 sm:space-y-6">
            {(() => {
              // Determine the basis for projection:
              //   - specific day selected → that day's fees / paid count
              //   - "all" with month filter → avg across visible days
              //   - "all" no month filter → report-wide avg
              const isSpecificDay = selectedDate !== "all";
              const focusDay = isSpecificDay
                ? per_day.find((d) => d.service_date === selectedDate)
                : null;
              const dailyFeesBasis = isSpecificDay
                ? focusDay
                  ? focusDay.total_fees
                  : 0
                : visibleDays.length
                  ? visibleDays.reduce((s, d) => s + d.total_fees, 0) /
                    visibleDays.length
                  : 0;
              const dailyPaidCountBasis = isSpecificDay
                ? focusDay
                  ? focusDay.paid_fee_count
                  : 0
                : visibleDays.length
                  ? visibleDays.reduce((s, d) => s + d.paid_fee_count, 0) /
                    visibleDays.length
                  : 0;
              const basisLabel = isSpecificDay
                ? focusDay
                  ? focusDay.pretty_date
                  : selectedDate
                : selectedMonth === "all"
                  ? `${visibleDays.length} day${visibleDays.length === 1 ? "" : "s"}`
                  : monthsAvailable.find((m) => m.ym === selectedMonth)?.label ||
                    selectedMonth;
              const monthlyEst = dailyFeesBasis * 30;
              const annualEst = dailyFeesBasis * 365;
              const monthlyWaiver =
                dailyPaidCountBasis * COPAY_WAIVED_PER_DISPENSING * 30;
              const annualWaiver =
                dailyPaidCountBasis * COPAY_WAIVED_PER_DISPENSING * 365;
              return (
                <>
                  <div>
                    <div className="inline-flex items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#A3B8AD]">
                      <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={1.75} />
                      Projected Dispensing Fee Revenue
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-5">
                    <RevenueBlock
                      periodLabel="Monthly Est."
                      netLabel="Approx. monthly"
                      gross={monthlyEst}
                      waiver={monthlyWaiver}
                      net={monthlyEst - monthlyWaiver}
                      grossTestId="revenue-monthly"
                      waiverTestId="revenue-monthly-waiver"
                      netTestId="revenue-monthly-net"
                    />
                    <RevenueBlock
                      periodLabel="Annual Est."
                      netLabel="Approx. annual"
                      gross={annualEst}
                      waiver={annualWaiver}
                      net={annualEst - annualWaiver}
                      grossTestId="revenue-annual"
                      waiverTestId="revenue-annual-waiver"
                      netTestId="revenue-annual-net"
                    />
                  </div>
                </>
              );
            })()}
          </div>
        </section>
        </CollapsibleBlock>
        )}

        {/* Profitability — HCV: single Visible-Period column (one-time courses,
             no extrapolation, auto-rebate). Non-HCV: classic Daily/Monthly/Annual
             extrapolation. Operating cost is editable inline per-scope. */}
        {num_days > 0 && (
        <CollapsibleBlock
          title="Profitability (P&L)"
          storageKey="profitability"
          testid="profitability-collapse"
        >
        <section
          data-testid="profitability-section"
          className="reveal reveal-delay-1 bg-white border border-[#E6EBE8] rounded-2xl overflow-hidden"
        >
          {(() => {
            const isSpecificDay = selectedDate !== "all";
            const focusDay = isSpecificDay
              ? per_day.find((d) => d.service_date === selectedDate)
              : null;

            // ===== HCV branch — no extrapolation, single Visible Period =====
            if (viewMode === "hcv") {
              // Classify every HCV patient in the visible window so we can
              // exclude NIHB clients from the $2 co-pay deduction.
              const visibleByPatient = new Map();
              for (const t of visibleTransactionsForFees) {
                const arr = visibleByPatient.get(t.patient) || [];
                arr.push(t);
                visibleByPatient.set(t.patient, arr);
              }
              const visiblePayerByPatient = new Map();
              for (const [pt, arr] of visibleByPatient) {
                visiblePayerByPatient.set(pt, classifyPayer(arr));
              }
              const windowFees = visibleTransactionsForFees.reduce(
                (s, t) => s + (t.fees || 0),
                0
              );
              // ODB-only paid count drives co-pay waiver — NIHB has no co-pay.
              const windowOdbPaidCount = visibleTransactionsForFees.reduce(
                (s, t) =>
                  s +
                  ((t.fees || 0) > 0 &&
                  visiblePayerByPatient.get(t.patient) !== "NIHB"
                    ? 1
                    : 0),
                0
              );
              const windowMarkup = visibleTransactionsForFees.reduce(
                (s, t) => s + (t.markup || 0),
                0
              );
              // HCV purchasing rebate — payer-aware aggregation so the
              // total is exact (no per-row $0.01 float drift from
              // plan1_paid). ODB anchors to $5,000/claim × rebate%;
              // NIHB anchors to $20,000/bottle (1 bottle per patient × drug).
              const windowRebate = aggregateHcvRebate(visibleTransactionsForFees);
              // NIHB drug-cost spread — the cash kept from billing NIHB
              // at its negotiated ingredient cost ($25K+/bottle) vs paying
              // the supplier $20K wholesale. Without this line, the HCV
              // P&L's "Total revenue" understates by the spread because
              // the PDF's markup column doesn't include the drug-cost gap.
              const windowNihbSpread = aggregateNihbDrugSpread(visibleTransactionsForFees);
              // Two consultant commissions paid on every HCV course at 0.5%
              // of drug cost (~$25/week × 12wk = $300, or × 8wk = $200 for
              // Maviret). Sales consultant is NOT paid on Maviret rows.
              // NIHB rows are capped at $100/bottle per consultant
              // (1 patient × 1 drug = 1 bottle for NIHB).
              const consultants = aggregateConsultantFeesPerCycle(visibleTransactionsForFees, visibleConsultantCycles, patientPayerMap);
              const windowMedical = consultants.medical;
              const windowSales = consultants.sales;
              const windowNp = consultants.np;
              // Milestone incentive — $240 paid per unique HCV client in
              // the visible period. Drillable per-patient.
              const milestones = aggregateMilestoneIncentives(visibleTransactionsForFees);
              const windowMilestone = milestones.total;
              const feesRevenue = +(
                windowFees - windowOdbPaidCount * COPAY_WAIVED_PER_DISPENSING
              ).toFixed(2);
              const windowDays = Math.max(1, visibleDays.length);
              // HCV is a one-time-per-patient cost (the per-patient op cost
              // is already multiplied by the patients in scope), so skip the
              // days-in-window proration that Non-HCV's flat monthly cost uses.
              const cost = +monthlyOperatingCost.toFixed(2);
              // Additional pharmacy-operations overhead — $350 × distinct HCV
              // patients in the visible window, kept as a separate P/L line
              // alongside the main operating cost.
              const windowPharmacyOps = +(HCV_PHARMACY_OPS_PER_PATIENT * hcvPatientCountInWindow).toFixed(2);
              // Staffing cost — $500 × distinct HCV patients in the visible
              // window.
              const windowStaffing = +(HCV_STAFFING_PER_PATIENT * hcvPatientCountInWindow).toFixed(2);
              // HCV netRevenue includes the auto-computed Purchasing
              // rebate (manufacturer kickback — 3% Epclusa/Vosevi/Harvoni,
              // 0.5% Maviret) so it matches the revenue lines rendered in
              // the P&L block below.
              const netRevenue = +(feesRevenue + windowMarkup + windowRebate + windowNihbSpread).toFixed(2);
              const pl = +(netRevenue - cost - windowPharmacyOps - windowStaffing - windowMedical - windowSales - windowNp - windowMilestone).toFixed(2);
              const windowLabel = isSpecificDay
                ? focusDay
                  ? focusDay.pretty_date
                  : selectedDate
                : `${windowDays} day${windowDays === 1 ? "" : "s"}`;
              return (
                <>
                  <div className="p-5 sm:p-6 border-b border-[#F0F4F2] flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-[10px] sm:text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A] flex items-center gap-2">
                        <TrendingUp className="h-3.5 w-3.5 text-[#2B5E4A]" strokeWidth={2} />
                        Profitability vs Operating Cost
                      </div>
                      <div className="mt-1 text-[11px] text-[#8A9C93]">
                        HCV courses are one-time — totals reflect the visible window only ({windowLabel}). Rebates auto-computed from manufacturer rates (3% Epclusa/Vosevi/Harvoni, 0.5% Maviret). Consultant commissions 0.5% of drug cost (sales consultant skipped on Maviret).
                      </div>
                    </div>
                    <OperatingCostEditor
                      value={opCostHcv}
                      onSave={(val) => {
                        const n = Number.isFinite(+val) ? Math.max(0, +val) : 0;
                        setOpCostHcv(n);
                        try { localStorage.setItem("haven.opcost.hcv", String(n)); } catch { /* non-fatal */ }
                      }}
                      editing={opCostEditing}
                      setEditing={setOpCostEditing}
                      scopeLabel="HCV"
                      perPatient
                      patientCount={hcvPatientCountInWindow}
                      effectiveTotal={monthlyOperatingCost}
                    />
                  </div>
                  <div className="grid grid-cols-1">
                    <PLPeriodBlock
                      testid="pl-visible"
                      period={`Visible Period · ${windowLabel}`}
                      revenueLines={[
                        { label: "Dispensing fees", amount: feesRevenue },
                        { label: "Sbmt. Markup", amount: windowMarkup },
                        // HCV purchasing rebate — auto-computed from
                        // manufacturer rates (3% Epclusa/Vosevi/Harvoni,
                        // 0.5% Maviret). Only show when there's a non-zero
                        // amount in the visible window.
                        ...(Math.abs(windowRebate) > 0.005
                          ? [{ label: "Purchasing rebates", amount: windowRebate }]
                          : []),
                        // Only surface the NIHB drug-cost spread when there
                        // actually is one in the visible period — keeps the
                        // ODB-only views uncluttered.
                        ...(Math.abs(windowNihbSpread) > 0.005
                          ? [{ label: "NIHB drug spread", amount: windowNihbSpread }]
                          : []),
                      ]}
                      cost={cost}
                      result={pl}
                      onCostClick={() => setCostDrill({ type: "op-cost" })}
                      costTestid="pl-op-cost-drill"
                      onCostToggle={() => setOpCostBreakdownOpen((v) => !v)}
                      costExpanded={opCostBreakdownOpen}
                      costBreakdown={HCV_OP_COST_BREAKDOWN.map((line) => ({
                        label: line.label,
                        amount: line.amount * hcvPatientCountInWindow,
                      }))}
                      extraCostLines={[
                        {
                          label: "Staffing",
                          amount: windowStaffing,
                          testid: "pl-staffing-drill",
                          onClick: () => setCostDrill({ type: "staffing" }),
                        },
                        {
                          label: "Pharmacy operations",
                          amount: windowPharmacyOps,
                          testid: "pl-pharmacy-ops-drill",
                          onClick: () => setCostDrill({ type: "pharmacy-ops" }),
                        },
                        {
                          label: "Medical consultant",
                          amount: windowMedical,
                          testid: "pl-medical-consultant-drill",
                          onClick: () => setConsultantDrill({ type: "medical" }),
                        },
                        {
                          label: "Sales consultant",
                          amount: windowSales,
                          testid: "pl-sales-consultant-drill",
                          onClick: () => setConsultantDrill({ type: "sales" }),
                        },
                        {
                          label: "NP consultant",
                          amount: windowNp,
                          testid: "pl-np-consultant-drill",
                          onClick: () => setConsultantDrill({ type: "np" }),
                        },
                        {
                          label: "Milestone incentives",
                          amount: windowMilestone,
                          testid: "pl-milestone-drill",
                          onClick: () => setConsultantDrill({ type: "milestone" }),
                        },
                      ]}
                    />
                  </div>
                </>
              );
            }

            // ===== Non-HCV branch — classic Daily/Monthly/Annual extrapolation =====
            const dailyFees = isSpecificDay
              ? focusDay
                ? focusDay.total_fees
                : 0
              : visibleDays.length
                ? visibleDays.reduce((s, d) => s + d.total_fees, 0) /
                  visibleDays.length
                : 0;
            const dailyPaidCount = isSpecificDay
              ? focusDay
                ? focusDay.paid_fee_count
                : 0
              : visibleDays.length
                ? visibleDays.reduce((s, d) => s + d.paid_fee_count, 0) /
                  visibleDays.length
                : 0;
            const dailyWaiver = dailyPaidCount * COPAY_WAIVED_PER_DISPENSING;
            const dailyFeesRevenue = dailyFees - dailyWaiver;

            // ----- Sbmt. Markup basis -----
            // Pharmacy markup paid by drug plans, extracted from the "Markup"
            // column of each transaction.
            const dailyMarkup = isSpecificDay
              ? focusDay
                ? focusDay.total_markup || 0
                : 0
              : visibleDays.length
                ? visibleDays.reduce((s, d) => s + (d.total_markup || 0), 0) /
                  visibleDays.length
                : 0;

            // ----- Purchasing rebate basis -----
            // Determine the *monthly* rebate amount applicable to the
            // currently-viewed slice, then prorate to day / extrapolate to year.
            // Strict rule: only count rebates that have been uploaded for
            // months actually inside the visible window. If the user filters
            // to a month with no rebate recorded (e.g. June not yet entered),
            // the rebate basis is $0 — no synthetic projection from other
            // months. That keeps the P&L honest for audit purposes.
            const visibleRebateMonthSet = new Set(
              visibleDays.map((d) => d.service_date.slice(0, 7))
            );
            const allRebateMonths = Object.keys(rebates).filter(
              (ym) => rebateAmountFor(rebates, ym, viewMode) > 0
            );
            const filteredRebateMonths = allRebateMonths.filter((ym) =>
              visibleRebateMonthSet.has(ym)
            );
            const rebateValues = filteredRebateMonths.map((ym) =>
              rebateAmountFor(rebates, ym, viewMode)
            );
            const avgUploadedRebate = rebateValues.length
              ? rebateValues.reduce((a, b) => a + b, 0) / rebateValues.length
              : 0;
            let monthlyRebateBasis = 0;
            if (isSpecificDay) {
              const ymOfDay = selectedDate.slice(0, 7);
              monthlyRebateBasis = rebateAmountFor(rebates, ymOfDay, viewMode);
            } else if (selectedMonth !== "all") {
              monthlyRebateBasis = rebateAmountFor(rebates, selectedMonth, viewMode);
            } else {
              // Multi-month filter (Months/Quarters/ODB-cycles multi-select)
              // → use the average across the months in the visible window
              // that have an uploaded rebate. Months with no rebate
              // contribute 0 to the basis.
              monthlyRebateBasis = avgUploadedRebate;
            }
            // Use 365/12 (≈30.4167) so Monthly × 12 = Annual exactly.
            // (Using a flat 30 leaves a 5-day gap between Monthly×12 and
            // the calendar-year Annual projection.)
            const DAYS_PER_MONTH = 365 / 12;

            // -------- Display reconciliation --------
            // We round the per-line daily values to cents FIRST, then derive
            // Monthly / Annual from the rounded daily. Otherwise the user
            // can paper-check `Daily × 365` and get a $1–$2 variance from
            // the displayed Annual (sub-cent precision lost to display
            // rounding × 365 days). The trade-off is a sub-dollar accumulation
            // error in Annual vs. the truly raw daily — but that's invisible
            // and matches what's on screen.
            const round2 = (v) => Math.round((v || 0) * 100) / 100;
            const dailyFeesRevR = round2(dailyFeesRevenue);
            const dailyMarkupR = round2(dailyMarkup);
            const dailyRebateR = round2(monthlyRebateBasis / DAYS_PER_MONTH);
            const monthlyRebateR = round2(monthlyRebateBasis);

            // Derived monthly/annual — kept in lockstep with the rounded
            // daily so the Daily × multiplier mental check ties out to
            // every line on screen.
            const monthlyMarkup = dailyMarkupR * DAYS_PER_MONTH;
            const annualMarkup = dailyMarkupR * 365;
            // Rebate annual/monthly are derived from the rounded daily
            // (not from the uploaded monthly × 12) so Daily × 365 = Annual
            // for the rebate line too. Sub-$2 variance vs. the literal
            // uploaded monthly figure — acceptable for a projection.
            const monthlyRebateProjection = dailyRebateR * DAYS_PER_MONTH;
            const annualRebate = dailyRebateR * 365;

            const dailyNetRevenue = dailyFeesRevR + dailyMarkupR + dailyRebateR;
            const monthlyNetRevenue =
              dailyFeesRevR * DAYS_PER_MONTH +
              dailyMarkupR * DAYS_PER_MONTH +
              monthlyRebateProjection;
            const annualNetRevenue =
              dailyFeesRevR * 365 + dailyMarkupR * 365 + annualRebate;

            const dailyCost = round2(monthlyOperatingCost / DAYS_PER_MONTH);
            const monthlyCost = dailyCost * DAYS_PER_MONTH;
            const annualCost = dailyCost * 365;

            const dailyPL = dailyNetRevenue - dailyCost;
            const monthlyPL = monthlyNetRevenue - monthlyCost;
            const annualPL = annualNetRevenue - annualCost;

            return (
              <>
                <div className="p-5 sm:p-6 border-b border-[#F0F4F2] flex items-start justify-between gap-3 flex-wrap">
                  <div className="text-[10px] sm:text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A] flex items-center gap-2">
                    <TrendingUp className="h-3.5 w-3.5 text-[#2B5E4A]" strokeWidth={2} />
                    Profitability vs Operating Cost
                  </div>
                  <OperatingCostEditor
                    value={monthlyOperatingCost}
                    onSave={setMonthlyOperatingCost}
                    editing={opCostEditing}
                    setEditing={setOpCostEditing}
                    scopeLabel="Non-HCV"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[#F0F4F2]">
                  <PLPeriodBlock
                    testid="pl-daily"
                    period="Daily"
                    revenueLines={[
                      { label: "Dispensing fees", amount: dailyFeesRevR },
                      { label: "Sbmt. Markup", amount: dailyMarkupR },
                      { label: "Purchasing rebates", amount: dailyRebateR },
                    ]}
                    cost={dailyCost}
                    result={dailyPL}
                  />
                  <PLPeriodBlock
                    testid="pl-monthly"
                    period="Monthly"
                    revenueLines={[
                      { label: "Dispensing fees", amount: dailyFeesRevR * DAYS_PER_MONTH },
                      { label: "Sbmt. Markup", amount: monthlyMarkup },
                      { label: "Purchasing rebates", amount: monthlyRebateProjection },
                    ]}
                    cost={monthlyCost}
                    result={monthlyPL}
                  />
                  <PLPeriodBlock
                    testid="pl-annual"
                    period="Annual"
                    revenueLines={[
                      { label: "Dispensing fees", amount: dailyFeesRevR * 365 },
                      { label: "Sbmt. Markup", amount: annualMarkup },
                      { label: "Purchasing rebates", amount: annualRebate },
                    ]}
                    cost={annualCost}
                    result={annualPL}
                  />
                </div>
              </>
            );
          })()}
        </section>
        </CollapsibleBlock>
        )}


        {/* Monthly Patient Variance - only if multi-month */}
        {isMultiMonth && per_month_summary && per_month_summary.length > 1 && (
          <CollapsibleBlock
            title="Monthly Patient Variance"
            storageKey="monthly-variance"
            testid="monthly-variance-collapse"
          >
          <section
            data-testid="monthly-variance"
            className="reveal reveal-delay-1 bg-white border border-[#E6EBE8] rounded-xl overflow-hidden"
          >
            <div className="p-6 border-b border-[#F0F4F2]">
              <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A] flex items-center gap-2">
                <TrendingUp className="h-3.5 w-3.5 text-[#2B5E4A]" strokeWidth={2} />
                Monthly Patient Variance
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="monthly-variance-table">
                <thead className="bg-[#F7F9F8] text-xs uppercase tracking-[0.1em] text-[#5C746A] font-semibold border-b border-[#E6EBE8]">
                  <tr>
                    <th className="text-left px-6 py-3 font-semibold">Month</th>
                    <th className="text-right px-6 py-3 font-semibold">Unique Pts</th>
                    <th
                      className="text-right px-6 py-3 font-semibold"
                      title="Patients whose first-ever prescription in this report landed in this month"
                    >
                      New Pts
                    </th>
                    <th className="text-right px-6 py-3 font-semibold">
                      Net Δ vs Prev
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const visibleMonths = per_month_summary.filter((m) =>
                      visibleDays.some((d) => d.service_date.startsWith(m.year_month))
                    );
                    return visibleMonths.map((m) => {
                    const hasNet = m.net_change_vs_prev !== null && m.net_change_vs_prev !== undefined;
                    const netUp = hasNet && m.net_change_vs_prev >= 0;
                    const netTooltip = hasNet
                      ? `${m.new_patients_vs_prev} new · ${m.lost_patients_vs_prev} lost vs ${m.prev_month_label}`
                      : "Earliest month in report — no prior month to compare";
                    return (
                      <tr
                        key={m.year_month}
                        data-testid={`monthly-variance-row-${m.year_month}`}
                        className="border-b border-[#F0F4F2] hover:bg-[#F7F9F8] cursor-pointer transition-colors text-sm text-[#3D4F46]"
                        onClick={() => handleMonthChange(m.year_month)}
                      >
                        <td className="px-6 py-3 font-medium text-[#1B3B2E]">
                          {m.month_label}
                        </td>
                        <td className="px-6 py-3 text-right font-mono-data text-[#1B3B2E]">
                          {m.unique_patients}
                        </td>
                        <td
                          className="px-6 py-3 text-right font-mono-data text-[#2B5E4A]"
                          title="First-ever prescription in this report landed in this month"
                          data-testid={`monthly-new-patients-${m.year_month}`}
                        >
                          {newPatientsByMonth.get(m.year_month) || 0}
                        </td>
                        <td
                          className="px-6 py-3 text-right"
                          title={netTooltip}
                          data-testid={`monthly-net-change-${m.year_month}`}
                        >
                          {hasNet ? (
                            <span
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-mono-data font-medium ${
                                netUp
                                  ? "bg-[#E6F2EC] text-[#1B5E40]"
                                  : "bg-[#FFF2EF] text-[#B65A45]"
                              }`}
                            >
                              {netUp ? (
                                <TrendingUp className="h-3 w-3" strokeWidth={2} />
                              ) : (
                                <TrendingDown className="h-3 w-3" strokeWidth={2} />
                              )}
                              {netUp ? "+" : ""}
                              {m.net_change_vs_prev}
                            </span>
                          ) : (
                            <span className="text-xs text-[#8A9C93]">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  });
                  })()}
                </tbody>
              </table>
            </div>
          </section>
          </CollapsibleBlock>
        )}

        {/* Drug Breakdown chart moved inside the Unique Drugs KpiCard
            below — the standalone placement was removed per user request. */}

        {/* Day-context heading */}
        {num_days > 0 && (
        <>
        {/* KPI Grid */}
        <CollapsibleBlock
          title="Operational KPIs"
          storageKey="kpi-grid"
          testid="kpi-grid-collapse"
        >
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 reveal reveal-delay-2">
          <KpiCard
            testid="dashboard-kpi-patients"
            label="Unique Patients"
            value={summary.unique_patients}
            sub={selectedDate === "all"
              ? "Distinct patients across selected range"
              : "Counted once per service date"}
            icon={Users}
            expandable
            expanded={expandedKpi === "patients"}
            onToggle={() => {
              setExpandedKpi((cur) => (cur === "patients" ? null : "patients"));
              setPatientsKpiExpandedRow(null);
            }}
          >
            <KpiBreakdownList
              testid="patients-breakdown"
              headers={["Patient", "Rx", "Fees"]}
              rows={patientsBreakdown.map((p) => ({
                label: p.patient,
                col2: p.count,
                col3: formatCurrency(p.fees),
              }))}
              emptyText={
                transactions && transactions.length === 0 && summary.unique_patients > 0
                  ? "Patient-level detail is not loaded for this very large report. Pick a single day or month to drill in."
                  : "No patients in current view."
              }
              expandedRow={patientsKpiExpandedRow}
              onToggleRow={setPatientsKpiExpandedRow}
              renderRowDetail={(row) => (
                <PatientRxDetail
                  patient={row.label}
                  transactions={visibleTransactionsForFees}
                />
              )}
            />
          </KpiCard>
          <KpiCard
            testid="dashboard-kpi-drugs"
            label="Unique Drugs"
            value={summary.unique_drugs}
            sub="Distinct drug names dispensed"
            icon={Pill}
            expandable
            expanded={expandedKpi === "drugs"}
            onToggle={() =>
              setExpandedKpi((cur) => (cur === "drugs" ? null : "drugs"))
            }
          >
            {/* Top fee contributors chart — rendered inside the expanded
                Unique Drugs card, directly above the breakdown table. */}
            {drugsBreakdown && drugsBreakdown.length > 0 ? (
              <div
                data-testid="drug-fees-chart"
                className="px-3 pt-3 pb-2 border-b border-[#F0F4F2]"
              >
                <div className="text-[10px] uppercase tracking-[0.12em] text-[#5C746A] font-semibold mb-1">
                  Top fee contributors
                </div>
                <div
                  className="-ml-2"
                  style={{
                    height: `${Math.max(220, Math.min(10, drugsBreakdown.length) * 36 + 32)}px`,
                  }}
                >
                  {(() => {
                    const totalFees = drugsBreakdown.reduce(
                      (s, d) => s + (d.total_fees || 0),
                      0
                    );
                    const topDrugs = [...drugsBreakdown]
                      .sort((a, b) => b.total_fees - a.total_fees)
                      .slice(0, 10);
                    const chartData = topDrugs.map((d) => ({
                      drug: d.drug_name,
                      Fees: d.total_fees,
                      Pct:
                        totalFees > 0
                          ? +((d.total_fees / totalFees) * 100).toFixed(1)
                          : 0,
                      Count: d.count,
                    }));
                    const countMap = Object.fromEntries(
                      topDrugs.map((d) => [d.drug_name, d.count])
                    );
                    return (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={chartData}
                          layout="vertical"
                          margin={{ top: 4, right: 70, left: 0, bottom: 0 }}
                        >
                          <CartesianGrid
                            horizontal={false}
                            stroke="#E6EBE8"
                            strokeDasharray="3 3"
                          />
                          <XAxis type="number" hide={true} />
                          <YAxis
                            type="category"
                            dataKey="drug"
                            width={150}
                            // eslint-disable-next-line react/no-unstable-nested-components
                            tick={(props) => (
                              <DrugTick {...props} maxChars={16} countMap={countMap} />
                            )}
                            axisLine={false}
                            tickLine={false}
                            interval={0}
                          />
                          <Tooltip
                            cursor={{ fill: "#F0F4F2" }}
                            content={<CurrencyTooltip />}
                          />
                          <Bar
                            dataKey="Fees"
                            fill="#1B3B2E"
                            radius={[0, 6, 6, 0]}
                            maxBarSize={20}
                          >
                            <LabelList
                              dataKey="Pct"
                              position="right"
                              formatter={(v) => `${v}%`}
                              style={{
                                fill: "#2B5E4A",
                                fontSize: 10,
                                fontFamily: "JetBrains Mono",
                                fontWeight: 600,
                              }}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </div>
              </div>
            ) : null}
            <KpiBreakdownList
              testid="drugs-breakdown"
              headers={["Drug Name", "Strength", "Form", "DIN", "Patients", "Rx"]}
              rows={drugsBreakdown.map((d) => {
                // Strength = numeric dose + unit (e.g. "KADIAN 100MG
                // CAP" → "100MG", "ODAN-METHADONE 10MG/ML" → "10MG/ML",
                // "MINT-CLONIDINE 0.1MG TAB" → "0.1MG"). Blank for
                // strength-less items like "ENSURE PLUS".
                const nm = (d.drug_name || "").toUpperCase();
                const sm = nm.match(
                  /\b(\d+(?:\.\d+)?)\s?(MG|MCG|ML|G|UNIT[S]?|%|IU)(?:\/(ML|G))?\b/i
                );
                let strength = "";
                if (sm) {
                  const denom = sm[3] ? "/" + sm[3].toUpperCase() : "";
                  strength = `${sm[1]}${sm[2].toUpperCase()}${denom}`;
                }
                // Form = dosage form keyword + optional release
                // modifier. Matches TAB / CAP / SOL / SUSP / etc. and
                // appends XL / SR / ER / CR / LA / IR / XR / MR / CD
                // when present (so "APO-BUPROPION XL 300MG TAB" reads
                // as "TAB XL"). Some names carry only the modifier
                // (e.g. "KADIAN SRT" — SR alone), which we still show.
                const fm = nm.match(
                  /\b(TABLET|TABS?|CAPSULE|CAPS?|SUSPENSION|SUSP|SOLUTION|SOL|SYRUP|CREAM|OINT(?:MENT)?|SPRAY|PATCH|INHALER|HFA|INJ(?:ECTION)?|POWDER|GEL|LOTION|DROPS?|MDI|DPI|LIQUID|LOZENGE|GRAN(?:ULES)?|EMULSION|WASH|SHAMPOO|SUPP)\b/
                );
                const rm = nm.match(/\b(XL|SR|SRT|ER|CR|LA|IR|XR|MR|CD)\b/);
                const formParts = [];
                if (fm) formParts.push(fm[1]);
                if (rm) formParts.push(rm[1]);
                const form = formParts.join(" ");
                return {
                  label: d.drug_name,
                  col1b: strength,
                  col1c: form,
                  col1d: d.din || "—",
                  col2: d.unique_patients,
                  col3: d.count,
                  muted: d.zero > 0 ? `${d.zero} zero-fee` : null,
                };
              })}
              emptyText={
                transactions && transactions.length === 0 && summary.unique_drugs > 0
                  ? "Drug-level detail is not loaded for this very large report. Pick a single day or month to drill in."
                  : "No drugs in current view."
              }
            />
          </KpiCard>
        </section>
        </CollapsibleBlock>
        </>
        )}

        {/* Per-Drug Breakdown chart was moved above the Unique Drugs KPI;
            the CollapsibleBlock wrapper here was removed per user request. */}



        {/* Zero-Fee Recovery Estimate */}
        {num_days > 0 && (
        <CollapsibleBlock
          title="Zero-Fee Recovery Estimate"
          storageKey="recovery-widget"
          testid="recovery-widget-collapse"
        >
        <section
          data-testid="recovery-widget"
          className="reveal reveal-delay-4 rounded-2xl border border-[#FDE5E0] bg-gradient-to-br from-[#FFF8F5] to-[#FFF2EF] p-6 sm:p-7"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 shrink-0 rounded-xl bg-white border border-[#FDE5E0] flex items-center justify-center">
                <Banknote className="h-6 w-6 text-[#D36C56]" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#B65A45]">
                  Zero-Fee Recovery Estimate
                </div>
                <h2 className="text-lg sm:text-xl font-semibold text-[#1B3B2E] mt-1">
                  Potentially unbilled professional fees
                </h2>
                <p className="text-sm text-[#5C746A] mt-1 max-w-xl">
                  Each zero-fee dispensing represents ≈{" "}
                  <span className="font-mono-data text-[#1B3B2E]">
                    {formatCurrency(DISPENSING_FEE)}
                  </span>{" "}
                  of missed professional fee.{" "}
                  <span className="font-mono-data text-[#1B3B2E]">
                    {auditRows.length}
                  </span>{" "}
                  flagged
                  {selectedDate === "all" ? " in the report" : " on this day"}.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-start lg:items-end">
              <div className="text-xs uppercase tracking-[0.15em] text-[#B65A45] font-semibold">
                Recoverable
              </div>
              <div
                data-testid="recovery-amount"
                className="font-mono-data text-3xl sm:text-4xl font-light tracking-tighter text-[#D36C56] mt-1"
              >
                {formatCurrency(auditRows.length * DISPENSING_FEE)}
              </div>
            </div>
          </div>
        </section>
        </CollapsibleBlock>
        )}


        {/* Zero-Fee Audit Table */}
        {num_days > 0 && (
        <CollapsibleBlock
          title="Zero-Fee Audit Report"
          storageKey="audit-table"
          testid="audit-table-collapse"
        >
        <section className="reveal reveal-delay-4">
          <div className="bg-white border border-[#E6EBE8] rounded-xl overflow-hidden">
            <div className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[#F0F4F2]">
              <div>
                <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A] flex items-center gap-2">
                  <AlertTriangle
                    className="h-3.5 w-3.5 text-[#D36C56]"
                    strokeWidth={2}
                  />
                  Zero-Fee Audit Report
                </div>
                <h2 className="text-lg sm:text-xl font-semibold text-[#1B3B2E] mt-1">
                  Dispensings with $0.00 Sbmt. Fee
                </h2>
                <p className="text-sm text-[#5C746A] mt-1">
                  {auditRows.length} entr{auditRows.length === 1 ? "y" : "ies"}{" "}
                  flagged for review
                  {selectedDate !== "all" ? " on this day" : " in the report"}.
                </p>
              </div>
              <button
                data-testid="export-csv-button"
                onClick={downloadAuditCsv}
                disabled={auditRows.length === 0}
                className="inline-flex items-center gap-2 rounded-lg border border-[#E6EBE8] bg-white px-4 py-2 text-sm font-medium text-[#1B3B2E] hover:bg-[#F7F9F8] transition-all duration-200 hover:-translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                <Download className="h-4 w-4" strokeWidth={1.75} />
                Export CSV
              </button>
            </div>

            <div className="overflow-x-auto">
              <table
                data-testid="audit-table"
                className="w-full"
              >
                <thead className="bg-[#F7F9F8] text-xs uppercase tracking-[0.1em] text-[#5C746A] font-semibold border-b border-[#E6EBE8]">
                  <tr>
                    <th className="text-left px-6 py-3 font-semibold">Service Date</th>
                    <th className="text-left px-6 py-3 font-semibold">Patient</th>
                    <th className="text-left px-6 py-3 font-semibold">Drug Name</th>
                    <th className="text-left px-6 py-3 font-semibold">Rx #</th>
                    <th className="text-left px-6 py-3 font-semibold">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {auditRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-10 text-center text-sm text-[#5C746A]"
                      >
                        No zero-fee dispensings for this selection.
                      </td>
                    </tr>
                  ) : (
                    auditRows.map((r, idx) => (
                      <tr
                        key={`${r.rx_id}-${r.service_date}-${idx}`}
                        className="border-b border-[#F0F4F2] hover:bg-[#F7F9F8] transition-colors text-sm text-[#3D4F46]"
                      >
                        <td className="px-6 py-3 font-mono-data text-[#1B3B2E]">
                          {r.pretty_date}
                        </td>
                        <td className="px-6 py-3">
                          <button
                            type="button"
                            data-testid={`patient-drill-${r.service_date}-${r.rx_id}`}
                            onClick={() =>
                              setDrillDown({
                                patient: r.patient,
                                service_date: r.service_date,
                                pretty_date: r.pretty_date,
                              })
                            }
                            className="text-left text-[#2B5E4A] hover:text-[#1B3B2E] hover:underline underline-offset-4 transition-colors font-medium"
                            title={`View all Rx for ${r.patient} on ${r.pretty_date}`}
                          >
                            {r.patient}
                          </button>
                        </td>
                        <td className="px-6 py-3">{r.drug_name}</td>
                        <td className="px-6 py-3 font-mono-data text-[#5C746A]">
                          {r.rx_id}
                        </td>
                        <td className="px-6 py-3 font-mono-data text-[#5C746A]">
                          {r.time}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
        </CollapsibleBlock>
        )}

        {/* Per-day breakdown table */}
        {isMultiDay && (
          <CollapsibleBlock
            title="Per-Day Breakdown"
            storageKey="per-day-table"
            testid="per-day-collapse"
          >
          <section className="reveal reveal-delay-5">
            <div className="bg-white border border-[#E6EBE8] rounded-xl overflow-hidden">
              <div className="p-3 sm:p-4 border-b border-[#F0F4F2]">
                <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                  Per-Day Breakdown
                </div>
                <h2 className="text-sm sm:text-base font-semibold text-[#1B3B2E] mt-0.5">
                  Daily metrics across the report
                </h2>
              </div>
              {/* Reconciliation flag banner intentionally omitted — the
                  detail was visual noise for the auditor's day-to-day
                  workflow. The underlying `reconciliationFlags` memo is
                  still computed so per-row highlighting inside the daily
                  drill can still surface individual mismatches when
                  useful, but the top-of-section warning is hidden. */}
              <div className="overflow-x-auto">
                <table
                  className="w-full text-[11px] sm:text-xs [&_th]:px-3 [&_th]:py-1.5 [&_td]:px-3 [&_td]:py-1"
                  data-testid="per-day-table"
                >
                  <thead className="bg-[#F7F9F8] uppercase tracking-[0.08em] text-[#5C746A] font-semibold border-b border-[#E6EBE8]">
                    <tr>
                      <th className="text-left font-semibold">Date</th>
                      <th className="text-center font-semibold">Patients</th>
                      <th className="text-center font-semibold">Drugs</th>
                      <th className="text-center font-semibold">Dispensings</th>
                      <th className="text-right font-semibold">Zero-Fee</th>
                      <th className="text-right font-semibold">Copay Waived</th>
                      <th className="text-right font-semibold">Net Fees</th>
                      <th className="text-right font-semibold">Markup</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleDays.length > 1 ? (() => {
                      // Daily averages across the currently-visible reporting
                      // window. Drugs are averaged as distinct unique drugs/day
                      // (already per-day on each row), not deduped across days.
                      const n = visibleDays.length;
                      const avgPatients = visibleDays.reduce((s, d) => s + (d.unique_patients || 0), 0) / n;
                      const avgDrugs = visibleDays.reduce((s, d) => s + (d.unique_drugs || 0), 0) / n;
                      const avgDisp = visibleDays.reduce((s, d) => s + (d.total_dispensings || 0), 0) / n;
                      const avgZero = visibleDays.reduce((s, d) => s + (d.zero_fee_count || 0), 0) / n;
                      const avgCopay = visibleDays.reduce(
                        (s, d) => s + (d.paid_fee_count || 0) * COPAY_WAIVED_PER_DISPENSING,
                        0
                      ) / n;
                      const avgNetFees = visibleDays.reduce((s, d) => {
                        const cw = (d.paid_fee_count || 0) * COPAY_WAIVED_PER_DISPENSING;
                        return s + Math.max(0, (d.total_fees || 0) - cw);
                      }, 0) / n;
                      const avgMarkup =
                        visibleDays.reduce((s, d) => s + (d.total_markup || 0), 0) / n;
                      const intFmt = (v) => v.toFixed(1).replace(/\.0$/, "");
                      return (
                        <tr
                          data-testid="per-day-avg-row"
                          className="border-b border-[#E6EBE8] bg-[#F0F4F2] text-[#1B3B2E] font-semibold"
                        >
                          <td className="px-3 py-2 uppercase tracking-[0.1em] text-[10px] text-[#5C746A] whitespace-nowrap">
                            Daily avg · {n}d
                          </td>
                          <td className="text-center font-mono-data">{intFmt(avgPatients)}</td>
                          <td className="text-center font-mono-data">{intFmt(avgDrugs)}</td>
                          <td className="text-center font-mono-data">{intFmt(avgDisp)}</td>
                          <td className="text-right font-mono-data text-[#D36C56]">{intFmt(avgZero)}</td>
                          <td className="text-right font-mono-data text-[#D36C56]">{formatCurrency(avgCopay)}</td>
                          <td className="text-right font-mono-data">{formatCurrency(avgNetFees)}</td>
                          <td className="text-right font-mono-data">{formatCurrency(avgMarkup)}</td>
                        </tr>
                      );
                    })() : null}
                    {[...visibleDays]
                      .sort((a, b) => b.service_date.localeCompare(a.service_date))
                      .map((d) => {
                      const isOpen = dailyExpandedDate === d.service_date;
                      const copayTotal = d.paid_fee_count * COPAY_WAIVED_PER_DISPENSING;
                      const netFees = Math.max(0, (d.total_fees || 0) - copayTotal);
                      return (
                        <React.Fragment key={d.service_date}>
                          <tr
                            data-testid={`per-day-row-${d.service_date}`}
                            className={`border-b border-[#F0F4F2] cursor-pointer transition-colors text-[#3D4F46] ${
                              isOpen ? "bg-[#F7F9F8]" : "hover:bg-[#F7F9F8]"
                            }`}
                            onClick={() =>
                              setDailyExpandedDate(isOpen ? null : d.service_date)
                            }
                            role="button"
                            aria-expanded={isOpen}
                          >
                            <td className="font-mono-data text-[#1B3B2E] whitespace-nowrap">
                              <span className="inline-flex items-center gap-1.5">
                                {isOpen ? (
                                  <ChevronDown
                                    className="h-3.5 w-3.5 text-[#5C746A] shrink-0"
                                    strokeWidth={2}
                                  />
                                ) : (
                                  <ChevronRight
                                    className="h-3.5 w-3.5 text-[#5C746A] shrink-0"
                                    strokeWidth={2}
                                  />
                                )}
                                {d.pretty_date}
                              </span>
                            </td>
                            <td className="text-center font-mono-data">
                              {d.unique_patients}
                            </td>
                            <td className="text-center font-mono-data">
                              {d.unique_drugs}
                            </td>
                            <td className="text-center font-mono-data">
                              {d.total_dispensings}
                            </td>
                            <td className="text-right font-mono-data text-[#D36C56]">
                              {d.zero_fee_count}
                            </td>
                            <td className="text-right font-mono-data text-[#D36C56]">
                              {formatCurrency(copayTotal)}
                            </td>
                            <td className="text-right font-mono-data text-[#1B3B2E]">
                              {formatCurrency(netFees)}
                            </td>
                            <td className="text-right font-mono-data text-[#3D4F46]">
                              {formatCurrency(d.total_markup || 0)}
                            </td>
                          </tr>
                          {isOpen ? (
                            <tr
                              data-testid={`per-day-detail-${d.service_date}`}
                              className="bg-[#F7F9F8] border-b border-[#F0F4F2]"
                            >
                              <td colSpan={8} className="p-0">
                                <DailyClaimsDetail
                                  serviceDate={d.service_date}
                                  transactions={visibleTransactionsForFees}
                                />
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
          </CollapsibleBlock>
        )}
      </main>

      {/* Custom date range dialog */}
      <Dialog open={rangeDialogOpen} onOpenChange={setRangeDialogOpen}>
        <DialogContent
          data-testid="range-dialog"
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="max-w-md bg-white border-[#E6EBE8] rounded-2xl shadow-2xl p-0 overflow-hidden"
        >
          <DialogHeader className="px-6 pt-6 pb-2 space-y-1">
            <DialogTitle className="font-heading text-xl font-medium text-[#1B3B2E]">
              Filters
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5C746A]">
              Available:{" "}
              <span className="font-mono-data text-[#1B3B2E]">
                {date_range_start}
              </span>{" "}
              →{" "}
              <span className="font-mono-data text-[#1B3B2E]">
                {date_range_end}
              </span>
            </DialogDescription>
          </DialogHeader>
          <RangePicker
            reportStart={date_range_start}
            reportEnd={date_range_end}
            monthsAvailable={monthsAvailable}
            initial={customRange}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onApply={(range) => {
              setCustomRange(range);
              setSelectedMonth("all");
              setSelectedDate("all");
              setRangeDialogOpen(false);
            }}
            onClear={() => {
              setCustomRange(null);
              setRangeDialogOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Patient drill-down dialog */}
      <Dialog
        open={!!drillDown}
        onOpenChange={(open) => {
          if (!open) {
            setDrillDown(null);
            setDrillExpandedDates(new Set());
            setDrillExpandedRxIds(new Set());
          }
        }}
      >
        <DialogContent
          data-testid="patient-drill-dialog"
          className="max-w-2xl bg-white border-[#E6EBE8] max-h-[calc(100vh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-2rem)] overflow-y-auto p-6 sm:p-7"
        >
          <DialogHeader>
            <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A]">
              Patient drill-down
            </div>
            <DialogTitle className="font-heading text-xl font-medium text-[#1B3B2E]">
              {drillDown?.patient}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#5C746A]">
              {drillDown?.service_date ? (
                <>
                  All prescriptions dispensed on{" "}
                  <span className="font-mono-data text-[#1B3B2E]">
                    {drillDown?.pretty_date}
                  </span>
                </>
              ) : (
                <>
                  All prescriptions dispensed across the currently visible
                  range.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div
            className="mt-3 grid gap-3"
            style={{
              gridTemplateColumns:
                viewMode === "non-hcv" ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
            }}
          >
            <div className="rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                Paid dispensings
              </div>
              <div
                data-testid="drill-count"
                className="font-mono-data text-xl font-light text-[#1B3B2E] mt-1"
              >
                {drillDownTotals.paidCount}
              </div>
              {drillDownTotals.zero > 0 ? (
                <div className="text-[10px] text-[#8A9C93] mt-0.5">
                  +{drillDownTotals.zero} zero-fee
                </div>
              ) : null}
            </div>
            <div className="rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                Net Sbmt. Fees
                <span className="block text-[8px] text-[#8A9C93] font-medium normal-case tracking-normal mt-0.5">
                  after $2 co-pay
                </span>
              </div>
              <div
                data-testid="drill-fees"
                className="font-mono-data text-xl font-light text-[#1B3B2E] mt-1"
              >
                {formatCurrency(
                  drillDownProfile
                    ? drillDownProfile.netFees
                    : drillDownTotals.fees -
                        drillDownTotals.paidCount * COPAY_WAIVED_PER_DISPENSING
                )}
              </div>
              <div className="text-[10px] text-[#8A9C93] mt-0.5">
                Gross {formatCurrency(drillDownTotals.fees)}
              </div>
            </div>
            {viewMode === "non-hcv" && drillDownAdherence ? (
              <div
                className={`col-span-2 rounded-xl border p-3 ${
                  drillDownAdherence.pct >= 80
                    ? "border-[#CDE3D7] bg-[#F2F7F4]"
                    : drillDownAdherence.pct >= 50
                    ? "border-[#F1E1B5] bg-[#FFF8E7]"
                    : "border-[#FDE5E0] bg-[#FFF8F5]"
                }`}
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                  Daily-dispense adherence
                </div>
                <div className="flex items-baseline gap-2 mt-1">
                  <div
                    data-testid="drill-adherence"
                    className={`font-mono-data text-xl font-light ${
                      drillDownAdherence.pct >= 80
                        ? "text-[#2B5E4A]"
                        : drillDownAdherence.pct >= 50
                        ? "text-[#9C7A1D]"
                        : "text-[#D36C56]"
                    }`}
                  >
                    {drillDownAdherence.pct}%
                  </div>
                  <div className="text-[11px] text-[#5C746A]">
                    {drillDownAdherence.visitDays} of {drillDownAdherence.totalDays} days seen
                  </div>
                </div>
              </div>
            ) : null}
            {viewMode === "hcv" || !drillDownAdherence ? null : null}
            <div
              className={`rounded-xl border border-[#FDE5E0] bg-[#FFF8F5] p-3 ${
                viewMode === "non-hcv" ? "col-span-2" : ""
              }`}
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#B65A45]">
                Zero-fee
              </div>
              <div
                data-testid="drill-zero"
                className="font-mono-data text-xl font-light text-[#D36C56] mt-1"
              >
                {drillDownTotals.zero}
              </div>
            </div>
          </div>

          {/* Patient profile — estimated value + cadence (shown only when drilled
              across the full visible range; per-day drill keeps the dialog short) */}
          {drillDownProfile && !drillDown?.service_date ? (() => {
            const visTotal = drillDownProfile.totalValueVisible;
            const annual = drillDownProfile.annualValue;
            // Derive monthly from the (already calendar-span-corrected) annual
            // figure so the math is consistent: monthly × 12 = annual.
            const monthlyEst = +(annual / 12).toFixed(2);
            // Days inside the visible range that produced this revenue.
            const visibleDays = drillDownProfile.totalDays;
            return (
            <div
              className="mt-3 rounded-xl border border-[#1B3B2E] bg-[#1B3B2E] text-white p-4 sm:p-5"
              data-testid="drill-profile"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60">
                    Cadence
                  </div>
                  <div className="mt-1 inline-flex items-center gap-2 flex-wrap">
                    <span className="rounded-full bg-white/15 text-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em]">
                      {drillDownProfile.cadence}
                    </span>
                    {drillDownProfile.avgGap > 0 ? (
                      <span className="text-[11px] text-white/70">
                        avg {drillDownProfile.avgGap}d gap
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60">
                    Avg monthly value
                  </div>
                  <div
                    className="mt-1 font-mono-data text-2xl sm:text-3xl font-light tracking-tighter text-white"
                    data-testid="drill-monthly-value"
                  >
                    {formatCurrency(monthlyEst)}
                    <span className="text-[11px] text-white/60 ml-1.5">/mo</span>
                  </div>
                  <div className="text-[11px] text-white/70 mt-1">
                    Annual ≈{" "}
                    <span
                      className="font-mono-data text-white"
                      data-testid="drill-annual-value"
                    >
                      {formatCurrency(annual)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-white/15 grid grid-cols-3 gap-3 text-[11px]">
                <div>
                  <div className="text-white/60 uppercase tracking-[0.12em] font-bold text-[9px]">
                    Net Sbmt. fees
                    <span className="block text-[8px] text-white/40 font-medium normal-case tracking-normal mt-0.5">
                      visible range · after $2 co-pay
                    </span>
                  </div>
                  <div className="font-mono-data text-white mt-0.5">
                    {formatCurrency(drillDownProfile.netFees)}
                  </div>
                </div>
                <div>
                  <div className="text-white/60 uppercase tracking-[0.12em] font-bold text-[9px]">
                    Markup share
                    <span className="block text-[8px] text-white/40 font-medium normal-case tracking-normal mt-0.5">
                      visible range
                    </span>
                  </div>
                  <div className="font-mono-data text-white mt-0.5">
                    {formatCurrency(drillDownProfile.markupShare)}
                  </div>
                </div>
                <div>
                  <div className="text-white/60 uppercase tracking-[0.12em] font-bold text-[9px]">
                    Rebate share
                    <span className="block text-[8px] text-white/40 font-medium normal-case tracking-normal mt-0.5">
                      visible range
                    </span>
                  </div>
                  <div className="font-mono-data text-white mt-0.5">
                    {formatCurrency(drillDownProfile.rebateShare)}
                  </div>
                </div>
              </div>
              <p className="mt-3 text-[10px] text-white/55 leading-snug">
                <span className="font-mono-data text-white/80">
                  {formatCurrency(visTotal)}
                </span>{" "}
                over {visibleDays} active day{visibleDays === 1 ? "" : "s"} ≈{" "}
                <span className="font-mono-data text-white/80">
                  {formatCurrency(monthlyEst)}
                </span>
                /mo × 12 ={" "}
                <span className="font-mono-data text-white/80">
                  {formatCurrency(annual)}
                </span>{" "}
                projected per year.
              </p>
            </div>
            );
          })() : null}

          {/* Smooth mobile card list — one card per dispensing, grouped by date when showing the full range */}
          <div className="mt-3 flex items-center justify-end">
            <button
              type="button"
              data-testid="drill-csv-export"
              onClick={() => {
                // Emit a single-patient audit CSV so the user can match the
                // parsed rows against the source PDF (search by Rx #).
                const safe = (v) => {
                  const s = String(v ?? "");
                  return s.includes(",") || s.includes('"') || s.includes("\n")
                    ? `"${s.replace(/"/g, '""')}"`
                    : s;
                };
                const header = [
                  "service_date",
                  "time",
                  "rx_id",
                  "patient",
                  "drug_name",
                  "fees",
                  "markup",
                  "plan1_paid",
                  "plan2_paid",
                  "total_paid",
                ];
                const lines = [header.join(",")];
                for (const r of drillDownRows) {
                  lines.push(
                    [
                      r.service_date,
                      r.time,
                      r.rx_id,
                      r.patient,
                      r.drug_name,
                      r.fees,
                      r.markup,
                      r.plan1_paid,
                      r.plan2_paid,
                      r.total_paid,
                    ]
                      .map(safe)
                      .join(",")
                  );
                }
                const blob = new Blob([lines.join("\n")], {
                  type: "text/csv;charset=utf-8",
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${(drillDown?.patient || "patient").replace(/[^a-z0-9]+/gi, "-")}-rows.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.12em] text-[#2B5E4A] hover:text-[#1B3B2E] underline-offset-4 hover:underline transition-colors"
            >
              Export raw rows (CSV)
            </button>
          </div>
          <div
            className="mt-2 rounded-xl border border-[#E6EBE8] overflow-hidden"
            data-testid="drill-list"
          >
            <div className="divide-y divide-[#F0F4F2]">
              {drillDownRows.length === 0 ? (
                <div className="px-5 py-6 text-center text-sm text-[#5C746A]">
                  No prescriptions found.
                </div>
              ) : (
                (() => {
                  // Group by service_date so a single tap collapses an entire
                  // day of fills (e.g. 5 EPCLUSA rows on the same day).
                  const groups = [];
                  const seen = new Map();
                  for (const r of drillDownRows) {
                    if (!seen.has(r.service_date)) {
                      const g = { date: r.service_date, rows: [], totalFee: 0, paid: 0, zero: 0 };
                      seen.set(r.service_date, g);
                      groups.push(g);
                    }
                    const g = seen.get(r.service_date);
                    g.rows.push(r);
                    g.totalFee += r.fees || 0;
                    if ((r.fees || 0) > 0) g.paid += 1;
                    else g.zero += 1;
                  }
                  // If the drill is already scoped to a single day, expand it
                  // by default — collapsing one-of-one would just add a tap.
                  const singleDayDrill = !!drillDown?.service_date || groups.length === 1;
                  return groups.map((g) => {
                    const isOpen = singleDayDrill || drillExpandedDates.has(g.date);
                    const rxLabel = `${g.rows.length} Rx`;
                    return (
                      <div key={g.date} data-testid={`drill-day-${g.date}`}>
                        <button
                          type="button"
                          data-testid={`drill-day-toggle-${g.date}`}
                          onClick={() => {
                            if (singleDayDrill) return;
                            setDrillExpandedDates((prev) => {
                              const next = new Set(prev);
                              if (next.has(g.date)) next.delete(g.date);
                              else next.add(g.date);
                              return next;
                            });
                          }}
                          aria-expanded={isOpen}
                          disabled={singleDayDrill}
                          className={`w-full bg-[#F7F9F8] px-4 py-2.5 flex items-center justify-between gap-3 text-left transition-colors ${
                            singleDayDrill ? "cursor-default" : "hover:bg-[#F0F4F2] active:bg-[#E6EBE8]"
                          }`}
                        >
                          <div className="flex items-center gap-1.5 min-w-0">
                            {singleDayDrill ? null : isOpen ? (
                              <ChevronDown className="h-3.5 w-3.5 text-[#5C746A] shrink-0" strokeWidth={2} />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5 text-[#5C746A] shrink-0" strokeWidth={2} />
                            )}
                            <span className="font-mono-data text-[12px] text-[#1B3B2E] tracking-normal">
                              {g.date}
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.12em] text-[#5C746A] font-semibold ml-1">
                              {rxLabel}
                              {g.zero > 0 ? (
                                <span className="text-[#D36C56] ml-1 font-mono-data normal-case tracking-normal">
                                  · {g.zero} zero
                                </span>
                              ) : null}
                            </span>
                          </div>
                          <span
                            className={`font-mono-data text-sm font-semibold tabular-nums whitespace-nowrap ${
                              g.totalFee === 0 ? "text-[#D36C56]" : "text-[#1B3B2E]"
                            }`}
                          >
                            {formatCurrency(g.totalFee)}
                          </span>
                        </button>
                        {isOpen
                          ? g.rows.map((r, idx) => {
                              const rxOpen = drillExpandedRxIds.has(r.rx_id);
                              const totalPaid =
                                r.total_paid != null
                                  ? r.total_paid
                                  : (r.plan1_paid || 0) + (r.plan2_paid || 0) + (r.fees || 0);
                              return (
                                <div
                                  key={`${r.rx_id}-${idx}`}
                                  data-testid={`drill-row-${r.rx_id}`}
                                  className={`border-t border-[#F0F4F2] ${
                                    r.fees === 0 ? "bg-[#FFF8F5]/30" : ""
                                  }`}
                                >
                                  <button
                                    type="button"
                                    data-testid={`drill-row-toggle-${r.rx_id}`}
                                    onClick={() => {
                                      setDrillExpandedRxIds((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(r.rx_id)) next.delete(r.rx_id);
                                        else next.add(r.rx_id);
                                        return next;
                                      });
                                    }}
                                    aria-expanded={rxOpen}
                                    className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left hover:bg-[#F0F4F2]/40 active:bg-[#F0F4F2] transition-colors"
                                  >
                                    <div className="min-w-0 flex items-start gap-1.5">
                                      {rxOpen ? (
                                        <ChevronDown
                                          className="h-3.5 w-3.5 text-[#5C746A] shrink-0 mt-0.5"
                                          strokeWidth={2}
                                        />
                                      ) : (
                                        <ChevronRight
                                          className="h-3.5 w-3.5 text-[#5C746A] shrink-0 mt-0.5"
                                          strokeWidth={2}
                                        />
                                      )}
                                      <div className="min-w-0">
                                        <div className="text-sm font-medium text-[#1B3B2E] break-words">
                                          {r.drug_name}
                                        </div>
                                        <div className="text-[11px] text-[#8A9C93] mt-0.5 font-mono-data">
                                          {r.time} · Rx {r.rx_id}
                                        </div>
                                      </div>
                                    </div>
                                    <div
                                      className={`font-mono-data text-sm font-medium shrink-0 ${
                                        r.fees === 0 ? "text-[#D36C56]" : "text-[#1B3B2E]"
                                      }`}
                                    >
                                      {formatCurrency(r.fees)}
                                    </div>
                                  </button>
                                  {rxOpen ? (
                                    <div
                                      data-testid={`drill-row-detail-${r.rx_id}`}
                                      className="px-4 pb-3 pt-1 bg-[#F7F9F8] border-t border-[#F0F4F2]"
                                    >
                                      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[11px]">
                                        <div className="flex items-center justify-between gap-2 border-b border-[#E6EBE8] pb-1.5">
                                          <dt className="text-[10px] uppercase tracking-[0.1em] text-[#5C746A] font-semibold">
                                            Sbmt. fee
                                          </dt>
                                          <dd
                                            className={`font-mono-data tabular-nums ${
                                              (r.fees || 0) === 0 ? "text-[#D36C56]" : "text-[#1B3B2E]"
                                            }`}
                                          >
                                            {formatCurrency(r.fees || 0)}
                                          </dd>
                                        </div>
                                        <div className="flex items-center justify-between gap-2 border-b border-[#E6EBE8] pb-1.5">
                                          <dt className="text-[10px] uppercase tracking-[0.1em] text-[#5C746A] font-semibold">
                                            Markup
                                          </dt>
                                          <dd className="font-mono-data tabular-nums text-[#1B3B2E]">
                                            {formatCurrency(r.markup || 0)}
                                          </dd>
                                        </div>
                                        <div className="flex items-center justify-between gap-2 border-b border-[#E6EBE8] pb-1.5">
                                          <dt className="text-[10px] uppercase tracking-[0.1em] text-[#5C746A] font-semibold">
                                            Plan 1 paid
                                          </dt>
                                          <dd className="font-mono-data tabular-nums text-[#1B3B2E]">
                                            {formatCurrency(r.plan1_paid || 0)}
                                          </dd>
                                        </div>
                                        <div className="flex items-center justify-between gap-2 border-b border-[#E6EBE8] pb-1.5">
                                          <dt className="text-[10px] uppercase tracking-[0.1em] text-[#5C746A] font-semibold">
                                            Plan 2 paid
                                          </dt>
                                          <dd className="font-mono-data tabular-nums text-[#1B3B2E]">
                                            {formatCurrency(r.plan2_paid || 0)}
                                          </dd>
                                        </div>
                                        <div className="col-span-2 flex items-center justify-between gap-2 pt-1">
                                          <dt className="text-[10px] uppercase tracking-[0.1em] text-[#2B5E4A] font-bold">
                                            Total billed
                                          </dt>
                                          <dd className="font-mono-data tabular-nums text-[#1B3B2E] font-semibold">
                                            {formatCurrency(totalPaid)}
                                          </dd>
                                        </div>
                                      </dl>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })
                          : null}
                      </div>
                    );
                  });
                })()
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Top Line Sales drill-down — lists every visible row contributing to Plan 1, Plan 2 or the Combined card so the user can verify the math. */}
      <Dialog
        open={!!topLineDrill}
        onOpenChange={(open) => {
          if (!open) {
            setTopLineDrill(null);
            setGpExpandedPatient(null);
            setTopLineExpandedPatient(null);
          }
        }}
      >
        <DialogContent
          data-testid="top-line-drill-dialog"
          className="max-w-2xl bg-white border-[#E6EBE8] max-h-[calc(100vh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-2rem)] overflow-y-auto p-6 sm:p-7"
        >
          {(() => {
            if (!topLineDrill) return null;

            // ===== Gross Profit drill — grouped by patient, expandable =====
            if (topLineDrill === "gross-profit") {
              const isHcv = viewMode === "hcv";
              // 1) Build per-row contributions
              const rows = visibleTransactionsForFees
                .map((t) => {
                  const paid = (t.fees || 0) > 0;
                  const feeNet = paid
                    ? Math.max(0, (t.fees || 0) - COPAY_WAIVED_PER_DISPENSING)
                    : 0;
                  const markup = t.markup || 0;
                  const rebate = isHcv ? hcvRebateForRow(t) : 0;
                  return {
                    ...t,
                    _feeNet: feeNet,
                    _markup: markup,
                    _rebate: rebate,
                    _line: +(feeNet + markup + rebate).toFixed(2),
                  };
                })
                .filter((r) => r._line > 0.005 || r._markup > 0.005);
              // 2) Group by patient
              const byPatient = new Map();
              for (const r of rows) {
                const entry = byPatient.get(r.patient) || {
                  patient: r.patient,
                  claims: [],
                  fees: 0,
                  markup: 0,
                  rebate: 0,
                  total: 0,
                  drugs: new Set(),
                };
                entry.claims.push(r);
                entry.fees += r._feeNet;
                entry.markup += r._markup;
                entry.rebate += r._rebate;
                entry.total += r._line;
                entry.drugs.add(canonicalDrugName(r.drug_name));
                byPatient.set(r.patient, entry);
              }
              const patientList = Array.from(byPatient.values())
                .map((p) => ({
                  ...p,
                  fees: +p.fees.toFixed(2),
                  markup: +p.markup.toFixed(2),
                  rebate: +p.rebate.toFixed(2),
                  total: +p.total.toFixed(2),
                  drugList: Array.from(p.drugs),
                }))
                .sort((a, b) => b.total - a.total);
              const lineSum = +patientList.reduce((s, p) => s + p.total, 0).toFixed(2);
              const nonHcvPoolRebate = !isHcv
                ? +(topLineSales.ourRevenue - lineSum).toFixed(2)
                : 0;
              const grandTotal = isHcv
                ? lineSum
                : +(lineSum + Math.max(0, nonHcvPoolRebate)).toFixed(2);

              return (
                <>
                  <DialogHeader>
                    <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                      Gross Profit · Audit by patient
                    </div>
                    <DialogTitle className="font-heading text-xl font-medium text-[#1B3B2E]">
                      Per-patient profit contribution
                    </DialogTitle>
                    <DialogDescription className="text-sm text-[#5C746A]">
                      Tap a patient to see every claim that contributed to gross profit. Line = fee (after $2 co-pay) + markup{isHcv ? " + auto-rebate" : ""}.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div className="rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] p-3 min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                        Patients
                      </div>
                      <div
                        data-testid="gp-drill-patient-count"
                        className="font-mono-data text-xl font-light text-[#1B3B2E] mt-1"
                      >
                        {patientList.length}
                      </div>
                    </div>
                    <div className="rounded-xl border border-[#1B3B2E] bg-[#1B3B2E] text-white p-3 min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3B8AD]">
                        Total
                      </div>
                      <div
                        data-testid="gp-drill-total"
                        className="font-mono-data text-base sm:text-xl font-light mt-1 tabular-nums whitespace-nowrap overflow-hidden text-ellipsis"
                      >
                        {formatCurrency(grandTotal)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 border border-[#E6EBE8] rounded-xl overflow-hidden divide-y divide-[#F0F4F2]">
                    {patientList.length === 0 ? (
                      <div className="p-6 text-center text-sm text-[#5C746A]">
                        No profit-contributing patients in this range.
                      </div>
                    ) : (
                      patientList.map((p) => {
                        const isOpen = gpExpandedPatient === p.patient;
                        return (
                          <div key={p.patient} data-testid={`gp-patient-${p.patient}`}>
                            <button
                              type="button"
                              onClick={() =>
                                setGpExpandedPatient(isOpen ? null : p.patient)
                              }
                              aria-expanded={isOpen}
                              data-testid={`gp-patient-toggle-${p.patient}`}
                              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors"
                            >
                              <div className="min-w-0 flex-1 flex items-center gap-2">
                                {isOpen ? (
                                  <ChevronDown className="h-4 w-4 text-[#5C746A] shrink-0" strokeWidth={2} />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-[#5C746A] shrink-0" strokeWidth={2} />
                                )}
                                <div className="min-w-0">
                                  <div className="font-semibold text-[#1B3B2E] truncate">
                                    {p.patient}
                                  </div>
                                  <div className="text-[10px] sm:text-[11px] text-[#8A9C93] tabular-nums truncate">
                                    {(p.claims.length / 4).toFixed(p.claims.length % 4 === 0 ? 0 : 2)} {p.claims.length / 4 === 1 ? "unit" : "units"} · {p.drugList.join(", ")}
                                  </div>
                                </div>
                              </div>
                              <div className="font-mono-data font-semibold text-[#1B3B2E] tabular-nums whitespace-nowrap text-sm sm:text-base shrink-0">
                                {formatCurrency(p.total)}
                              </div>
                            </button>
                            {isOpen ? (
                              <div className="bg-[#F7F9F8] border-t border-[#F0F4F2]">
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs">
                                    <thead className="text-[10px] uppercase tracking-[0.1em] text-[#5C746A] font-semibold">
                                      <tr>
                                        <th className="text-left px-3 py-2 font-semibold">Date</th>
                                        <th className="text-left px-3 py-2 font-semibold">Drug</th>
                                        <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Fee</th>
                                        <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Markup</th>
                                        {isHcv ? (
                                          <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Rebate</th>
                                        ) : null}
                                        <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Line</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {[...p.claims]
                                        .sort((a, b) =>
                                          a.service_date.localeCompare(b.service_date)
                                        )
                                        .map((r, idx) => (
                                          <tr
                                            key={`${r.rx_id}-${idx}`}
                                            className="border-t border-[#E6EBE8] text-[#3D4F46]"
                                          >
                                            <td className="px-3 py-2 font-mono-data whitespace-nowrap">
                                              {r.service_date}
                                            </td>
                                            <td className="px-3 py-2 truncate">
                                              {canonicalDrugName(r.drug_name)}
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap">
                                              {formatCurrency(r._feeNet)}
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap">
                                              {formatCurrency(r._markup)}
                                            </td>
                                            {isHcv ? (
                                              <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap text-[#2B5E4A]">
                                                {formatCurrency(r._rebate)}
                                              </td>
                                            ) : null}
                                            <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap font-semibold text-[#1B3B2E]">
                                              {formatCurrency(r._line)}
                                            </td>
                                          </tr>
                                        ))}
                                      <tr className="border-t border-[#E6EBE8] bg-white text-[#1B3B2E] font-semibold">
                                        <td className="px-3 py-2" colSpan={2}>
                                          Subtotal ({(p.claims.length / 4).toFixed(p.claims.length % 4 === 0 ? 0 : 2)} {p.claims.length / 4 === 1 ? "unit" : "units"})
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap">
                                          {formatCurrency(p.fees)}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap">
                                          {formatCurrency(p.markup)}
                                        </td>
                                        {isHcv ? (
                                          <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap">
                                            {formatCurrency(p.rebate)}
                                          </td>
                                        ) : null}
                                        <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap">
                                          {formatCurrency(p.total)}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      })
                    )}
                    {!isHcv && nonHcvPoolRebate > 0.005 ? (
                      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#F7F9F8] text-[#3D4F46]">
                        <div className="min-w-0">
                          <div className="font-semibold text-[#1B3B2E]">Pool rebate</div>
                          <div className="text-[10px] sm:text-[11px] text-[#8A9C93]">
                            From uploaded monthly rebate cheques (not allocated per patient)
                          </div>
                        </div>
                        <div className="font-mono-data font-semibold text-[#2B5E4A] tabular-nums whitespace-nowrap text-sm sm:text-base shrink-0">
                          {formatCurrency(nonHcvPoolRebate)}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </>
              );
            }

            const planLabel =
              topLineDrill === "plan1"
                ? "Plan 1"
                : topLineDrill === "plan2"
                  ? "Plan 2"
                  : "Combined";
            // Filter and sort underlying transactions
            const rows = visibleTransactions
              .map((t) => {
                const p1 = t.plan1_paid || 0;
                const p2 = t.plan2_paid || 0;
                // For Combined: HCV+PSP rows subtract Plan 2 (it's the
                // PSP-funded deductible, not new revenue). Non-HCV and
                // HCV rows without PSP stay at Plan 1 + Plan 2.
                const amount =
                  topLineDrill === "plan1"
                    ? p1
                    : topLineDrill === "plan2"
                      ? p2
                      : combinedAmountForRow(t);
                return { ...t, _p1: p1, _p2: p2, _amount: amount, _psp: isPspRow(t) };
              })
              .filter((r) => r._amount > 0.005)
              .sort((a, b) => {
                if (a.service_date !== b.service_date)
                  return a.service_date.localeCompare(b.service_date);
                return (b._amount || 0) - (a._amount || 0);
              });
            const total = rows.reduce((s, r) => s + r._amount, 0);
            return (
              <>
                <DialogHeader>
                  <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                    Top Line Sales · Validate data
                  </div>
                  <DialogTitle className="font-heading text-xl font-medium text-[#1B3B2E]">
                    {planLabel}
                  </DialogTitle>
                  <DialogDescription className="text-sm text-[#5C746A]">
                    Every dispensing in the selected range that contributed to
                    this total. Patients are collapsed — tap one to expand and
                    audit their individual dispensings.
                  </DialogDescription>
                </DialogHeader>

                {/* Top stats */}
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] p-3 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                      Rows
                    </div>
                    <div
                      data-testid="top-line-drill-count"
                      className="font-mono-data text-xl font-light text-[#1B3B2E] mt-1"
                    >
                      {rows.length}
                    </div>
                  </div>
                  <div className="rounded-xl border border-[#1B3B2E] bg-[#1B3B2E] text-white p-3 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3B8AD]">
                      Total
                    </div>
                    <div
                      data-testid="top-line-drill-total"
                      className="font-mono-data text-base sm:text-xl font-light mt-1 tabular-nums whitespace-nowrap overflow-hidden text-ellipsis"
                    >
                      {formatCurrency(total)}
                    </div>
                  </div>
                </div>

                {/* Patient list — collapsed by default so the user can scan
                    unique clients quickly, then expand one to audit its
                    individual dispensings. Mirrors the ODB drill pattern. */}
                <div className="mt-4 border border-[#E6EBE8] rounded-xl overflow-hidden divide-y divide-[#F0F4F2]">
                  {rows.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[#5C746A]">
                      No rows contributed to {planLabel} in this range.
                    </div>
                  ) : (
                    (() => {
                      // Bucket every row by patient. Keep their claims (sorted
                      // by service_date), drug names, and a running total so
                      // the collapsed header can render the at-a-glance line.
                      const byPatient = new Map();
                      for (const r of rows) {
                        const slot = byPatient.get(r.patient) || {
                          patient: r.patient,
                          claims: [],
                          drugs: new Set(),
                          total: 0,
                          p1Total: 0,
                          p2Total: 0,
                        };
                        slot.claims.push(r);
                        if (r.drug_name) slot.drugs.add(canonicalDrugName(r.drug_name));
                        slot.total += r._amount || 0;
                        slot.p1Total += r._p1 || 0;
                        slot.p2Total += r._p2 || 0;
                        byPatient.set(r.patient, slot);
                      }
                      const patientList = Array.from(byPatient.values())
                        .map((p) => ({
                          ...p,
                          total: +p.total.toFixed(2),
                          p1Total: +p.p1Total.toFixed(2),
                          p2Total: +p.p2Total.toFixed(2),
                          drugList: Array.from(p.drugs),
                        }))
                        .sort((a, b) => b.total - a.total);
                      return patientList.map((p) => {
                        const isOpen = topLineExpandedPatient === p.patient;
                        return (
                          <div
                            key={p.patient}
                            data-testid={`top-line-drill-patient-${p.patient}`}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setTopLineExpandedPatient(isOpen ? null : p.patient)
                              }
                              aria-expanded={isOpen}
                              data-testid={`top-line-drill-patient-toggle-${p.patient}`}
                              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors"
                            >
                              <div className="min-w-0 flex-1 flex items-center gap-2">
                                {isOpen ? (
                                  <ChevronDown className="h-4 w-4 text-[#5C746A] shrink-0" strokeWidth={2} />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-[#5C746A] shrink-0" strokeWidth={2} />
                                )}
                                <div className="min-w-0">
                                  <div className="font-semibold text-[#1B3B2E] truncate text-sm">
                                    {p.patient}
                                  </div>
                                  <div className="text-[10px] sm:text-[11px] text-[#8A9C93] tabular-nums truncate mt-0.5">
                                    {p.claims.length} disp.{p.drugList.length ? ` · ${p.drugList.join(", ")}` : ""}
                                  </div>
                                </div>
                              </div>
                              <div className="font-mono-data font-semibold text-[#1B3B2E] tabular-nums whitespace-nowrap text-sm sm:text-base shrink-0">
                                {formatCurrency(p.total)}
                              </div>
                            </button>
                            {isOpen ? (
                              <div className="bg-[#F7F9F8] border-t border-[#F0F4F2]">
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs">
                                    <thead className="text-[10px] uppercase tracking-[0.1em] text-[#5C746A] font-semibold">
                                      <tr>
                                        <th className="text-left px-3 py-2 font-semibold">Date</th>
                                        <th className="text-left px-3 py-2 font-semibold">Drug · Rx</th>
                                        {topLineDrill === "combined" ? (
                                          <>
                                            <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">P1</th>
                                            <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">P2</th>
                                          </>
                                        ) : null}
                                        <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {[...p.claims]
                                        .sort((a, b) => a.service_date.localeCompare(b.service_date))
                                        .map((r, idx) => (
                                          <tr
                                            key={`${r.rx_id || idx}-${r.service_date}`}
                                            data-testid={`top-line-drill-row-${p.patient}-${idx}`}
                                            className="border-t border-[#E6EBE8] text-[#3D4F46]"
                                          >
                                            <td className="px-3 py-2 font-mono-data whitespace-nowrap">
                                              {r.service_date}
                                            </td>
                                            <td className="px-3 py-2 min-w-[8rem]">
                                              <div className="text-[#1B3B2E] truncate">
                                                {canonicalDrugName(r.drug_name)}
                                              </div>
                                              <div className="text-[10px] text-[#8A9C93] font-mono-data">
                                                Rx {r.rx_id}
                                              </div>
                                            </td>
                                            {topLineDrill === "combined" ? (
                                              <>
                                                <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap text-[#5C746A]">
                                                  {formatCurrency(r._p1)}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap text-[#5C746A]">
                                                  {formatCurrency(r._p2)}
                                                </td>
                                              </>
                                            ) : null}
                                            <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap font-semibold text-[#1B3B2E]">
                                              {formatCurrency(r._amount)}
                                            </td>
                                          </tr>
                                        ))}
                                      <tr className="border-t border-[#E6EBE8] bg-white text-[#1B3B2E] font-semibold">
                                        <td
                                          className="px-3 py-2"
                                          colSpan={topLineDrill === "combined" ? 4 : 2}
                                        >
                                          Subtotal ({p.claims.length} disp.)
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap">
                                          {formatCurrency(p.total)}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      });
                    })()
                  )}
                </div>

                {/* Bottom verification strap */}
                <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-[#F7F9F8] border border-dashed border-[#E6EBE8] px-3.5 py-2.5">
                  <span className="text-[11px] sm:text-xs text-[#5C746A]">
                    Sum of {rows.length} row{rows.length === 1 ? "" : "s"} above
                  </span>
                  <span className="font-heading text-sm sm:text-base font-semibold text-[#1B3B2E] tabular-nums">
                    {formatCurrency(total)}
                  </span>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ODB Cycle drill-down — audit every dispensing that contributed to a
          specific reimbursement deposit. */}
      <Dialog
        open={!!odbDrill}
        onOpenChange={(open) => {
          if (!open) {
            setOdbDrill(null);
            setOdbExpandedPatient(null);
          }
        }}
      >
        <DialogContent
          data-testid="odb-drill-dialog"
          className="max-w-2xl bg-white border-[#E6EBE8] max-h-[calc(100vh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-2rem)] overflow-y-auto p-6 sm:p-7"
        >
          {(() => {
            if (!odbDrill) return null;
            // Audit dialog now serves both ODB and NIHB cycles — same UI, but
            // rows filtered by the scope chosen when the card was tapped.
            const drillScope = odbDrill._scope === "NIHB" ? "NIHB" : "ODB";
            // Pull every visible row inside this cycle's dispense window,
            // intersected with the active selection so it matches the deposit
            // amount shown in the section.
            let effStart = date_range_start;
            let effEnd = date_range_end;
            // When the user picked cycles via the ODB/NIHB multi-select
            // (including combined "both") OR selected the "Full Range"
            // preset, the customRange spans MORE than one cycle. Drilling
            // into ONE specific cycle card should show just that cycle —
            // not the entire multi-cycle span. Treat these cases as
            // "no explicit range" for drill purposes and let the clicked
            // cycle's window win.
            const cycleMultiSelect =
              customRange?.source === "odb" ||
              customRange?.source === "nihb" ||
              customRange?.source === "both" ||
              customRange?.source === "full";
            const hasExplicitSelection =
              (!!customRange && !cycleMultiSelect) ||
              selectedDate !== "all" ||
              selectedMonth !== "all";
            if (customRange && !cycleMultiSelect) {
              effStart = customRange.start;
              effEnd = customRange.end;
            } else if (selectedDate !== "all") {
              effStart = selectedDate;
              effEnd = selectedDate;
            } else if (selectedMonth !== "all") {
              effStart = `${selectedMonth}-01`;
              effEnd = lastDayOfMonth(selectedMonth);
            }
            // When the user has an explicit selection (custom range,
            // single day, or month), that range is the source of truth
            // — honor it verbatim and do NOT clip inward to the cycle's
            // dispense window. An auditor picking May 22 → Jun 8 while
            // opening the Cycle B card (May 23 → Jun 8) expects to see
            // EVERY row in their range, including any Cycle A tail on
            // May 22. Only fall back to the cycle window when no
            // explicit selection is active.
            const winStart = hasExplicitSelection
              ? effStart
              : odbDrill.dispenseStart;
            const winEnd = hasExplicitSelection
              ? effEnd
              : odbDrill.dispenseEnd;
            // True only when the resolved window exactly matches the
            // cycle's canonical dispense range — used below to decide
            // whether the subtitle should say "Cycle B" or spell out
            // the custom range instead.
            const isCycleWindow =
              winStart === odbDrill.dispenseStart &&
              winEnd === odbDrill.dispenseEnd;
            // Classify every patient in the visible window so we can isolate
            // ODB-only or NIHB-only rows for this drill.
            const winPatientRows = new Map();
            for (const t of visibleTransactions) {
              if (t.service_date < winStart || t.service_date > winEnd) continue;
              const arr = winPatientRows.get(t.patient) || [];
              arr.push(t);
              winPatientRows.set(t.patient, arr);
            }
            const winPatientPayer = new Map();
            for (const [pt, arr] of winPatientRows) {
              winPatientPayer.set(pt, classifyPayer(arr));
            }
            // Group NIHB rows into bottles (patient, drug, service_date)
            // — 1 bottle = every same-date row for the same patient+drug.
            // Used ONLY for the per-drug units chip. Every raw claim row
            // is shown in the audit list so the auditor can see the
            // individual transactions that make up each bottle.
            const rows = visibleTransactions
              .filter(
                (t) =>
                  t.service_date >= winStart &&
                  t.service_date <= winEnd &&
                  (t.plan1_paid || 0) > 0 &&
                  (winPatientPayer.get(t.patient) || "ODB") === drillScope
              )
              .map((t) => ({
                ...t,
                // Every row shows its RAW plan1_paid. For NIHB, the sum of
                // all rows sharing (patient, drug, service_date) equals
                // that bottle's deposit — per the user's accounting rule.
                _amount: t.plan1_paid || 0,
              }))
              .sort((a, b) => {
                if (a.service_date !== b.service_date)
                  return a.service_date.localeCompare(b.service_date);
                return b._amount - a._amount;
              });
            const total = rows.reduce((s, r) => s + r._amount, 0);
            const statusLabel =
              odbDrill.status === "in-progress"
                ? "In progress"
                : odbDrill.status === "upcoming"
                  ? "Upcoming"
                  : "Paid";
            return (
              <>
                <DialogHeader>
                  <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                    {drillScope === "NIHB" ? "NIHB Cycle · Audit" : "ODB Cycle · Audit"}
                  </div>
                  <DialogTitle className="font-heading text-xl font-medium text-[#1B3B2E]">
                    {drillScope === "NIHB"
                      ? odbDrill.shortLabel
                      : formatPayDate(odbDrill.payDate)}
                  </DialogTitle>
                  <DialogDescription className="text-sm text-[#5C746A]">
                    {drillScope === "NIHB" ? (
                      isCycleWindow ? (
                        <>NIHB receivable · same window as cycle {odbDrill.type}. Tap a patient to see every claim that contributed.</>
                      ) : (
                        <>
                          NIHB receivable ·{" "}
                          <span className="text-[#2B5E4A] font-medium">
                            {formatShortDate(winStart)} → {formatShortDate(winEnd)}
                          </span>
                          . Tap a patient to see every claim that contributed.
                        </>
                      )
                    ) : (
                      <>
                        {isCycleWindow ? (
                          <>Cycle {odbDrill.type} · {odbDrill.shortLabel}</>
                        ) : (
                          <>
                            Custom range ·{" "}
                            <span className="text-[#2B5E4A] font-medium">
                              {formatShortDate(winStart)} → {formatShortDate(winEnd)}
                            </span>
                          </>
                        )}{" "}
                        ·{" "}
                        <span className="text-[#2B5E4A] font-medium">{statusLabel}</span>
                        . Tap a patient to see every claim that contributed to this deposit.
                      </>
                    )}
                  </DialogDescription>
                </DialogHeader>

                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] p-3 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                      Dispensings
                    </div>
                    <div
                      data-testid="odb-drill-count"
                      className="font-mono-data text-xl font-light text-[#1B3B2E] mt-1"
                    >
                      {rows.length}
                    </div>
                  </div>
                  <div className="rounded-xl border border-[#1B3B2E] bg-[#1B3B2E] text-white p-3 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3B8AD]">
                      Plan 1 Total
                    </div>
                    <div
                      data-testid="odb-drill-total"
                      className="font-mono-data text-base sm:text-xl font-light mt-1 tabular-nums whitespace-nowrap overflow-hidden text-ellipsis"
                    >
                      {formatCurrency(total)}
                    </div>
                  </div>
                </div>

                {/* Total patient count + per-drug breakdown chip band.
                    Gives the auditor a one-glance view of how many
                    unique patients contributed to this cycle deposit
                    and which HCV drugs make up the mix. Total counts
                    use the same units-per-claim logic as the ledger
                    rows below (ODB: 4 claims = 1 bottle; NIHB:
                    1 patient × drug = 1 bottle). */}
                {(() => {
                  const uniquePatients = new Set(rows.map((r) => r.patient));
                  // Payer classification for each patient in the window
                  // — same as the audit row calculation below so the
                  // per-drug totals reconcile 1:1 with the list rows.
                  const rowsByPatient = new Map();
                  for (const r of rows) {
                    const arr = rowsByPatient.get(r.patient) || [];
                    arr.push(r);
                    rowsByPatient.set(r.patient, arr);
                  }
                  const perDrugUnits = new Map();
                  for (const [, arr] of rowsByPatient) {
                    const payer = classifyPayer(arr);
                    if (payer === "NIHB") {
                      // 1 bottle = 1 unique (drug, service_date) per patient.
                      // A patient with two dispense dates on the same drug
                      // registers as 2 bottles under the user's accounting rule.
                      const drugDatesSeen = new Set();
                      for (const c of arr) {
                        const k = hcvDrugKey(c.drug_name);
                        if (!k) continue;
                        const key = `${k}||${c.service_date}`;
                        if (drugDatesSeen.has(key)) continue;
                        drugDatesSeen.add(key);
                        perDrugUnits.set(k, (perDrugUnits.get(k) || 0) + 1);
                      }
                    } else {
                      const upc = unitsPerClaimForPayer(payer);
                      for (const c of arr) {
                        const k = hcvDrugKey(c.drug_name);
                        if (!k) continue;
                        perDrugUnits.set(k, (perDrugUnits.get(k) || 0) + upc);
                      }
                    }
                  }
                  const drugEntries = Array.from(perDrugUnits.entries())
                    .filter(([, u]) => u > 0)
                    .sort((a, b) => b[1] - a[1]);
                  const fmtU = (u) => {
                    const isWhole = Math.abs(u - Math.round(u)) < 0.001;
                    return u.toFixed(isWhole ? 0 : 2);
                  };
                  return (
                    <div
                      data-testid="odb-drill-patient-summary"
                      className="mt-3 rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] px-3 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px]"
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#5C746A]">
                          Total patients
                        </span>
                        <span
                          data-testid="odb-drill-total-patients"
                          className="font-mono-data tabular-nums font-semibold text-[#1B3B2E]"
                        >
                          {uniquePatients.size}
                        </span>
                      </div>
                      {drugEntries.length > 0 ? (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
                          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#5C746A]">
                            By drug
                          </span>
                          {drugEntries.map(([k, u]) => (
                            <span
                              key={k}
                              data-testid={`odb-drill-drug-${k}`}
                              className="inline-flex items-center gap-1 whitespace-nowrap"
                            >
                              <span className="text-[#3D4F46] font-medium">{k}</span>
                              <span className="font-mono-data tabular-nums text-[#5C746A]">
                                {fmtU(u)}
                              </span>
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })()}

                {/* Sort control for the patient list below. Placed just
                    above the first row so the auditor can flip between
                    "by deposit amount" (default) and "alphabetical" —
                    the latter is handy when cross-referencing a signed
                    patient roster during an audit. */}
                {rows.length > 0 ? (
                  <div
                    className="mt-4 flex items-center justify-end gap-2"
                    data-testid="odb-drill-sort-bar"
                  >
                    <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#5C746A]">
                      Sort
                    </span>
                    <div className="flex rounded-md border border-[#C7DBCF] overflow-hidden">
                      {[
                        { v: "total", l: "Amount" },
                        { v: "alpha", l: "Alphabetical" },
                      ].map((opt) => (
                        <button
                          key={opt.v}
                          type="button"
                          data-testid={`odb-drill-sort-${opt.v}`}
                          onClick={() => setOdbSortMode(opt.v)}
                          className={`text-[10px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 transition-colors ${
                            odbSortMode === opt.v
                              ? "bg-[#1B3B2E] text-white"
                              : "bg-white text-[#1B3B2E] hover:bg-[#F0F4F2]"
                          }`}
                        >
                          {opt.l}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="mt-2 border border-[#E6EBE8] rounded-xl overflow-hidden divide-y divide-[#F0F4F2]">
                  {rows.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[#5C746A]">
                      No Plan 1 dispensings in this cycle window.
                    </div>
                  ) : (
                    (() => {
                      // Group every claim in the cycle by patient. One card
                      // per patient with a running total of their Plan 1
                      // contribution; tap to expand and see each individual
                      // claim (date, drug, Rx, amount).
                      const byPatient = new Map();
                      for (const r of rows) {
                        const entry = byPatient.get(r.patient) || {
                          patient: r.patient,
                          claims: [],
                          total: 0,
                          drugs: new Set(),
                        };
                        entry.claims.push(r);
                        entry.total += r._amount;
                        entry.drugs.add(canonicalDrugName(r.drug_name));
                        byPatient.set(r.patient, entry);
                      }
                      const patientList = Array.from(byPatient.values())
                        .map((p) => {
                          const payer = classifyPayer(p.claims);
                          // NIHB: every patient = exactly 1 bottle (1-month
                          // billing rule). For multi-drug NIHB patients (rare),
                          // count 1 unit per distinct drug. ODB stays at the
                          // per-claim ratio (4 claims = 1 unit).
                          let units;
                          if (payer === "NIHB") {
                            // 1 bottle per unique (drug, service_date) group
                            // — matches the user's same-date accounting rule.
                            const bottleKeys = new Set();
                            for (const c of p.claims) {
                              const k = hcvDrugKey(c.drug_name);
                              if (!k) continue;
                              bottleKeys.add(`${k}||${c.service_date}`);
                            }
                            units = bottleKeys.size > 0 ? bottleKeys.size : 1;
                          } else {
                            const upc = unitsPerClaimForPayer(payer);
                            units = p.claims.length * upc;
                          }
                          return {
                            ...p,
                            payer,
                            units,
                            total: +p.total.toFixed(2),
                            drugList: Array.from(p.drugs),
                          };
                        })
                        .sort((a, b) => {
                          if (odbSortMode === "alpha") {
                            return a.patient.localeCompare(b.patient);
                          }
                          return b.total - a.total;
                        });
                      return patientList.map((p) => {
                        const isOpen = odbExpandedPatient === p.patient;
                        return (
                          <div key={p.patient} data-testid={`odb-patient-${p.patient}`}>
                            <button
                              type="button"
                              onClick={() =>
                                setOdbExpandedPatient(isOpen ? null : p.patient)
                              }
                              aria-expanded={isOpen}
                              data-testid={`odb-patient-toggle-${p.patient}`}
                              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors"
                            >
                              <div className="min-w-0 flex-1 flex items-center gap-2">
                                {isOpen ? (
                                  <ChevronDown className="h-4 w-4 text-[#5C746A] shrink-0" strokeWidth={2} />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-[#5C746A] shrink-0" strokeWidth={2} />
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="font-semibold text-[#1B3B2E] flex items-start gap-2 flex-wrap">
                                    <span className="whitespace-normal break-words">{p.patient}</span>
                                    {p.payer === "NIHB" ? (
                                      <span
                                        data-testid={`odb-patient-payer-${p.patient}`}
                                        className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md bg-[#F4E4C5] text-[#8A6B2B] text-[9px] font-bold uppercase tracking-[0.12em]"
                                      >
                                        NIHB
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className="text-[10px] sm:text-[11px] text-[#8A9C93] tabular-nums truncate">
                                    {(() => {
                                      const u = p.units;
                                      const isWhole = Math.abs(u - Math.round(u)) < 0.001;
                                      return `${u.toFixed(isWhole ? 0 : 2)} ${u === 1 ? "unit" : "units"} · ${p.drugList.join(", ")}`;
                                    })()}
                                  </div>
                                </div>
                              </div>
                              <div className="font-mono-data font-semibold text-[#1B3B2E] tabular-nums whitespace-nowrap text-sm sm:text-base shrink-0">
                                {formatCurrency(p.total)}
                              </div>
                            </button>
                            {/* Program override chip. Native <select> so it
                                plays well with iOS Safari (shadcn Select
                                portals sometimes collide with the modal on
                                mobile). Lives in its own subrow so tapping
                                the select doesn't toggle the patient claim
                                breakdown above. */}
                            <div
                              className="px-4 pb-2 -mt-1 flex flex-wrap sm:flex-nowrap sm:overflow-x-auto items-center gap-x-2.5 sm:gap-x-3 gap-y-2"
                              data-testid={`odb-patient-program-row-${p.patient}`}
                            >
                              <div className="flex items-center gap-1.5 shrink-0">
                                <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#5C746A]">
                                  Program
                                </span>
                                <select
                                  data-testid={`odb-patient-program-select-${p.patient}`}
                                  value={patientProgramMap[p.patient] || ""}
                                  onChange={(e) =>
                                    setPatientProgram(p.patient, e.target.value)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-[11px] font-mono-data px-1.5 py-1 rounded-md border border-[#C7DBCF] bg-white text-[#1B3B2E] focus:outline-none focus:ring-2 focus:ring-[#2B5E4A] focus:border-transparent"
                                >
                                  <option value="">Auto</option>
                                  <option value="420">George</option>
                                  <option value="HepCURE">Racquel</option>
                                  <option value="Malachi">Malachi</option>
                                  <option value="Maria">Maria</option>
                                  <option value="Excluded">Excluded</option>
                                </select>
                              </div>
                              {/* Type — Outreach / Pharmacy. Metadata only,
                                  persisted per (license, scope, cycle, patient)
                                  so each cycle card starts blank. */}
                              <div className="flex items-center gap-1.5 shrink-0">
                                <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#5C746A]">
                                  Type
                                </span>
                                <select
                                  data-testid={`odb-patient-type-select-${p.patient}`}
                                  value={cyclePatientTags[`${drillScope}||${odbDrill.key}||${p.patient}`]?.type || ""}
                                  onChange={(e) =>
                                    updateCyclePatientTag(drillScope, odbDrill.key, p.patient, "type", e.target.value)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-[11px] font-mono-data px-1.5 py-1 rounded-md border border-[#C7DBCF] bg-white text-[#1B3B2E] focus:outline-none focus:ring-2 focus:ring-[#2B5E4A] focus:border-transparent"
                                >
                                  <option value="">—</option>
                                  <option value="Outreach">Outreach</option>
                                  <option value="Pharmacy">Pharmacy</option>
                                </select>
                              </div>
                              {/* Bridging — Yes / No. Per cycle. */}
                              <div className="flex items-center gap-1.5 shrink-0">
                                <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#5C746A]">
                                  Bridging
                                </span>
                                <select
                                  data-testid={`odb-patient-bridging-select-${p.patient}`}
                                  value={cyclePatientTags[`${drillScope}||${odbDrill.key}||${p.patient}`]?.bridging || ""}
                                  onChange={(e) =>
                                    updateCyclePatientTag(drillScope, odbDrill.key, p.patient, "bridging", e.target.value)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-[11px] font-mono-data px-1.5 py-1 rounded-md border border-[#C7DBCF] bg-white text-[#1B3B2E] focus:outline-none focus:ring-2 focus:ring-[#2B5E4A] focus:border-transparent"
                                >
                                  <option value="">—</option>
                                  <option value="Yes">Yes</option>
                                  <option value="No">No</option>
                                </select>
                              </div>
                              {/* Recovered — Yes / Pending. Per cycle. */}
                              <div className="flex items-center gap-1.5 shrink-0">
                                <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#5C746A]">
                                  Recovered
                                </span>
                                <select
                                  data-testid={`odb-patient-recovered-select-${p.patient}`}
                                  value={cyclePatientTags[`${drillScope}||${odbDrill.key}||${p.patient}`]?.recovered || ""}
                                  onChange={(e) =>
                                    updateCyclePatientTag(drillScope, odbDrill.key, p.patient, "recovered", e.target.value)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-[11px] font-mono-data px-1.5 py-1 rounded-md border border-[#C7DBCF] bg-white text-[#1B3B2E] focus:outline-none focus:ring-2 focus:ring-[#2B5E4A] focus:border-transparent"
                                >
                                  <option value="">—</option>
                                  <option value="Yes">Yes</option>
                                  <option value="Pending">Pending</option>
                                </select>
                              </div>
                            </div>
                            {isOpen ? (
                              <div className="bg-[#F7F9F8] border-t border-[#F0F4F2]">
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs">
                                    <thead className="text-[10px] uppercase tracking-[0.1em] text-[#5C746A] font-semibold">
                                      <tr>
                                        <th className="text-left px-3 py-2 font-semibold">Date</th>
                                        <th className="text-left px-3 py-2 font-semibold">Drug · Rx</th>
                                        <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Plan 1</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {[...p.claims]
                                        .sort((a, b) =>
                                          a.service_date.localeCompare(b.service_date)
                                        )
                                        .map((r, idx) => (
                                          <tr
                                            key={`${r.rx_id || idx}-${r.service_date}`}
                                            className="border-t border-[#E6EBE8] text-[#3D4F46]"
                                          >
                                            <td className="px-3 py-2 font-mono-data whitespace-nowrap">
                                              {r.service_date}
                                            </td>
                                            <td className="px-3 py-2 min-w-[8rem]">
                                              <div className="text-[#1B3B2E] truncate">
                                                {canonicalDrugName(r.drug_name)}
                                              </div>
                                              <div className="text-[10px] text-[#8A9C93] font-mono-data">
                                                Rx {r.rx_id}
                                              </div>
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap font-semibold text-[#1B3B2E]">
                                              {formatCurrency(r._amount)}
                                            </td>
                                          </tr>
                                        ))}
                                      <tr className="border-t border-[#E6EBE8] bg-white text-[#1B3B2E] font-semibold">
                                        <td className="px-3 py-2" colSpan={2}>
                                          Subtotal ({(() => {
                                            const u = p.units;
                                            const isWhole = Math.abs(u - Math.round(u)) < 0.001;
                                            return `${u.toFixed(isWhole ? 0 : 2)} ${u === 1 ? "unit" : "units"}`;
                                          })()})
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono-data tabular-nums whitespace-nowrap">
                                          {formatCurrency(p.total)}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      });
                    })()
                  )}
                </div>

                <div className="mt-4 rounded-xl bg-[#F7F9F8] border border-dashed border-[#E6EBE8] divide-y divide-[#E6EBE8] overflow-hidden">
                  {(() => {
                    // Pull every row in the cycle window. We then bucket each
                    // patient (and their rows) by payer — ODB vs NIHB — so the
                    // summary keeps the ODB cycle deposit clean and shows the
                    // NIHB receivable separately (NIHB arrives on a different
                    // schedule from the manufacturer/federal plan).
                    const cycleAll = visibleTransactions.filter(
                      (t) => t.service_date >= winStart && t.service_date <= winEnd
                    );
                    const rowsByPatient = new Map();
                    for (const r of cycleAll) {
                      const arr = rowsByPatient.get(r.patient) || [];
                      arr.push(r);
                      rowsByPatient.set(r.patient, arr);
                    }
                    const payerByPatient = new Map();
                    for (const [pt, arr] of rowsByPatient) {
                      payerByPatient.set(pt, classifyPayer(arr));
                    }
                    const odbRows = drillScope === "NIHB" ? [] : cycleAll.filter(
                      (r) => payerByPatient.get(r.patient) !== "NIHB"
                    );
                    const nihbRows = drillScope === "ODB" ? [] : cycleAll.filter(
                      (r) => payerByPatient.get(r.patient) === "NIHB"
                    );

                    // Helper to compute the standard line bundle for a payer slice.
                    // Drug cost is anchored to the known $20,000/bottle wholesale
                    // price × units in this slice — NOT derived from PDF billing
                    // amounts — so the totals always reconcile to whole-bottle
                    // numbers (no $0.18 rounding noise).
                    const computeSlice = (slice, slicePayer) => {
                      const plan1 = slice.reduce((s, r) => s + (r.plan1_paid || 0), 0);
                      const plan2 = slice.reduce((s, r) => s + (r.plan2_paid || 0), 0);
                      const markup = slice.reduce((s, r) => s + (r.markup || 0), 0);
                      const fees = slice.reduce((s, r) => s + (r.fees || 0), 0);
                      const paidCount = slice.reduce(
                        (s, r) => s + ((r.fees || 0) > 0 ? 1 : 0),
                        0
                      );
                      // $2 co-pay is waived only on ODB-billed claims. NIHB
                      // (federal) clients are not subject to the ODB co-pay
                      // so net fees equal gross fees for that slice.
                      const isOdb = slicePayer !== "NIHB";
                      const copay = isOdb ? paidCount * COPAY_WAIVED_PER_DISPENSING : 0;
                      const netFees = Math.max(0, fees - copay);
                      // Anchor drug cost to the known $20K/bottle × units.
                      // For NIHB, units = unique (patient, drug) pairs because
                      // NIHB only permits 1-month-at-a-time billing → every
                      // patient = exactly 1 bottle per cycle regardless of
                      // how many claims were submitted. ODB stays at the
                      // per-claim ratio (4 claims = 1 unit).
                      const isNihbSlice = slicePayer === "NIHB";
                      const drugUnits = new Map();
                      if (isNihbSlice) {
                        const patientsByDrug = new Map();
                        for (const r of slice) {
                          const key = hcvDrugKey(r.drug_name);
                          if (!key) continue;
                          if (!patientsByDrug.has(key)) patientsByDrug.set(key, new Set());
                          patientsByDrug.get(key).add(r.patient);
                        }
                        for (const [k, pts] of patientsByDrug) {
                          drugUnits.set(k, pts.size);
                        }
                      } else {
                        const upc = unitsPerClaimForPayer(slicePayer);
                        for (const r of slice) {
                          const key = hcvDrugKey(r.drug_name);
                          if (!key) continue;
                          drugUnits.set(key, (drugUnits.get(key) || 0) + upc);
                        }
                      }
                      const totalUnits = Array.from(drugUnits.values()).reduce(
                        (s, u) => s + u,
                        0
                      );
                      const drugCost = totalUnits * HCV_COST_PER_UNIT;
                      // Plan-1 overage = anything the payer actually paid above
                      // (drug cost + markup + fees). Captures rounding noise or
                      // minor list-price drift from the payer's schedule.
                      const overage = Math.max(0, plan1 - (drugCost + markup + fees));
                      // Per-drug rebate based on units × $20K × rebate%.
                      const rebate = Array.from(drugUnits.entries()).reduce(
                        (s, [k, u]) => s + u * HCV_COST_PER_UNIT * (HCV_REBATE_PCT[k] || 0),
                        0
                      );
                      return {
                        plan1,
                        plan2,
                        markup,
                        fees,
                        paidCount,
                        copay,
                        netFees,
                        drugCost,
                        overage,
                        rebate,
                        totalUnits,
                        // Calculated receivable = drug cost + markup + net fees.
                        // Used in the summary rows so any sub-dollar PDF
                        // rounding overage doesn't inflate the deposit figure.
                        calcReceivable: drugCost + markup + netFees,
                      };
                    };
                    const odb = computeSlice(odbRows, "ODB");
                    const nihb = computeSlice(nihbRows, "NIHB");
                    const hasNihb = nihbRows.length > 0;

                    // Units per drug — payer-aware, and scope-aware so the
                    // drill shows only the units that belong to the payer
                    // being audited (ODB-scope drill = ODB units only,
                    // NIHB-scope drill = NIHB units only).
                    const scopeRows = drillScope === "NIHB" ? nihbRows : odbRows;
                    const unitsByDrug = new Map();
                    if (drillScope === "NIHB") {
                      // NIHB: 1 unique (patient, drug) pair = 1 bottle.
                      // 1-month-at-a-time billing → patient count, not claim count.
                      const patientsByDrug = new Map();
                      for (const r of scopeRows) {
                        const name = canonicalDrugName(r.drug_name) || r.drug_name || "Unknown";
                        if (!patientsByDrug.has(name)) patientsByDrug.set(name, new Set());
                        patientsByDrug.get(name).add(r.patient);
                        const cur = unitsByDrug.get(name) || { units: 0, claims: 0 };
                        cur.claims += 1;
                        unitsByDrug.set(name, cur);
                      }
                      for (const [name, pts] of patientsByDrug) {
                        const cur = unitsByDrug.get(name) || { units: 0, claims: 0 };
                        cur.units = pts.size;
                        unitsByDrug.set(name, cur);
                      }
                    } else {
                      const upc = unitsPerClaimForPayer(drillScope);
                      for (const r of scopeRows) {
                        const name = canonicalDrugName(r.drug_name) || r.drug_name || "Unknown";
                        const cur = unitsByDrug.get(name) || { units: 0, claims: 0 };
                        cur.units += upc;
                        cur.claims += 1;
                        unitsByDrug.set(name, cur);
                      }
                    }
                    const units = Array.from(unitsByDrug.entries())
                      .map(([name, v]) => ({ name, ...v }))
                      .sort((a, b) => b.claims - a.claims);
                    const totalUnits = units.reduce((s, u) => s + u.units, 0);

                    const fmtRow = (label, value, opts = {}) => (
                      <div
                        key={label}
                        className={`flex items-center justify-between gap-3 px-3.5 py-2.5 ${
                          opts.divider ? "bg-white border-y border-[#E6EBE8]" : ""
                        } ${opts.subtotal ? "border-t border-dashed border-[#E6EBE8]" : ""} ${
                          opts.section ? "bg-[#EFF2F0]" : ""
                        }`}
                        data-testid={`odb-drill-summary-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        <span
                          className={`text-[11px] sm:text-xs ${
                            opts.indent ? "pl-3 text-[#5C746A]" : ""
                          } ${
                            opts.strong || opts.subtotal || opts.section ? "text-[#1B3B2E] font-semibold" : "text-[#5C746A]"
                          } ${opts.section ? "uppercase tracking-[0.12em] text-[10px]" : ""}`}
                        >
                          {opts.indent && !opts.subtotal && !opts.negative ? "+ " : ""}{label}
                        </span>
                        <span
                          className={`font-mono-data tabular-nums text-sm sm:text-base ${
                            opts.strong || opts.subtotal
                              ? "font-semibold text-[#1B3B2E]"
                              : opts.negative
                                ? "text-[#B65A45]"
                                : opts.accent
                                  ? "font-semibold text-[#2B5E4A]"
                                  : opts.section
                                    ? "font-semibold text-[#1B3B2E]"
                                    : "text-[#1B3B2E]"
                          }`}
                        >
                          {opts.negative ? "−" : ""}{formatCurrency(Math.abs(value))}
                        </span>
                      </div>
                    );

                    return (
                      <>
                        {/* ODB section — hidden in NIHB-scope drill */}
                        {odbRows.length > 0 ? (
                          <>
                            {fmtRow("ODB clients", odb.plan1, { section: true })}
                            {fmtRow("Drug cost", odb.drugCost, { strong: true })}
                            {fmtRow("Submitted markup", odb.markup, { indent: true })}
                            {fmtRow("Dispensing fees (gross)", odb.fees, { indent: true })}
                            {odb.copay > 0.005
                              ? fmtRow(`Co-pay waived (−$2 × ${odb.paidCount})`, -odb.copay, { indent: true, negative: true })
                              : null}
                            {fmtRow("Net dispensing fees", odb.netFees, { indent: true, subtotal: true })}
                            {fmtRow("Plan 1 deposit (ODB)", odb.plan1, { strong: true, divider: true })}
                            {odb.rebate > 0.005
                              ? fmtRow("Expected rebate (manufacturer)", odb.rebate, { accent: true })
                              : null}
                          </>
                        ) : null}

                        {/* NIHB section — only shown when present.
                            Markup is derived as (receivable − drug cost − net fees)
                            so the rows reconcile exactly to the actual NIHB
                            receivable. Anchored drug cost ($20k/unit) keeps
                            the wholesale figure clean while the markup line
                            absorbs sub-dollar PDF rounding cleanly. */}
                        {hasNihb ? (() => {
                          const nihbMarkupDerived = +(nihb.plan1 - nihb.drugCost - nihb.netFees).toFixed(2);
                          return (
                          <>
                            {fmtRow("NIHB clients", nihb.plan1, { section: true })}
                            {fmtRow("Drug cost", nihb.drugCost, { strong: true })}
                            {fmtRow("Submitted markup", nihbMarkupDerived, { indent: true })}
                            {fmtRow("Dispensing fees (gross)", nihb.fees, { indent: true })}
                            {nihb.copay > 0.005
                              ? fmtRow(`Co-pay waived (−$2 × ${nihb.paidCount})`, -nihb.copay, { indent: true, negative: true })
                              : null}
                            {fmtRow("Net dispensing fees", nihb.netFees, { indent: true, subtotal: true })}
                            {fmtRow("NIHB receivable", nihb.plan1, { strong: true, divider: true })}
                            {nihb.rebate > 0.005
                              ? fmtRow("Expected rebate (manufacturer)", nihb.rebate, { accent: true })
                              : null}
                          </>
                          );
                        })() : null}

                        {/* Units by medication */}
                        <div className="px-3.5 py-2.5" data-testid="odb-drill-summary-units">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-[11px] sm:text-xs text-[#5C746A] font-semibold">
                              Total units dispensed
                            </span>
                            <span className="font-mono-data tabular-nums text-sm sm:text-base font-semibold text-[#1B3B2E]">
                              {(() => {
                                const isWhole = Math.abs(totalUnits - Math.round(totalUnits)) < 0.001;
                                return totalUnits.toFixed(isWhole ? 0 : 2);
                              })()}
                            </span>
                          </div>
                          <div className="mt-2 space-y-1">
                            {units.map((u) => {
                              const isWhole = Math.abs(u.units - Math.round(u.units)) < 0.001;
                              return (
                                <div
                                  key={u.name}
                                  className="flex items-center justify-between gap-3 text-[11px]"
                                  data-testid={`odb-drill-units-${u.name.toLowerCase()}`}
                                >
                                  <span className="text-[#5C746A] truncate">· {u.name}</span>
                                  <span className="font-mono-data tabular-nums text-[#1B3B2E] font-medium">
                                    {u.units.toFixed(isWhole ? 0 : 2)} {u.units === 1 ? "unit" : "units"}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* HCV per-patient cost drill-down — clicking the Operating cost or
          Pharmacy operations line in the HCV P&L opens this dialog. Mirrors
          the consultant audit template (CLIENTS / TOTAL FEE cards, mask
          patient names, PDF export, expandable per-patient rows). */}
      <Dialog
        open={!!costDrill}
        onOpenChange={(o) => {
          if (!o) {
            setCostDrill(null);
            setConsultantExpandedRow(null);
            setConsultantMaskNames(false);
          }
        }}
      >
        <DialogContent
          data-testid="cost-drill-dialog"
          className="w-[calc(100vw-1rem)] sm:w-full max-w-md max-h-[88vh] overflow-y-auto overscroll-contain p-4 sm:p-6"
        >
          {costDrill ? (() => {
            const isOpCost = costDrill.type === "op-cost";
            const isPharmOps = costDrill.type === "pharmacy-ops";
            const perPatient = isOpCost
              ? opCostHcv
              : isPharmOps
                ? HCV_PHARMACY_OPS_PER_PATIENT
                : HCV_STAFFING_PER_PATIENT;
            const title = isOpCost
              ? "Operating cost · audit"
              : isPharmOps
                ? "Pharmacy operations · audit"
                : "Staffing · audit";
            const subtitleRules = isOpCost
              ? [
                  `${formatCurrency(perPatient)} per HCV patient`,
                  "Editable monthly operating cost",
                  "Applied once per unique HCV patient in window",
                ]
              : isPharmOps
                ? [
                    `${formatCurrency(perPatient)} per HCV patient`,
                    "Pharmacy operations overhead",
                    "Applied once per unique HCV patient in window",
                  ]
                : [
                    `${formatCurrency(perPatient)} per HCV patient`,
                    "Pharmacy staffing cost",
                    "Applied once per unique HCV patient in window",
                  ];
            // Build per-patient rows — every distinct HCV patient in the
            // visible window, alongside the HCV claims that put them in
            // scope (so the audit row can expand to show evidence).
            const patientMap = new Map();
            for (const t of visibleTransactionsForFees) {
              if (!t.patient) continue;
              if (!hcvDrugKey(t.drug_name)) continue;
              const slot = patientMap.get(t.patient) || { patient: t.patient, claims: [] };
              slot.claims.push(t);
              patientMap.set(t.patient, slot);
            }
            const rows = Array.from(patientMap.values())
              .map((p) => ({
                ...p,
                fee: perPatient,
                drugs: Array.from(new Set(p.claims.map((c) => canonicalDrugName(c.drug_name)))),
              }))
              .sort((a, b) => a.patient.localeCompare(b.patient));
            const grandTotal = +(perPatient * rows.length).toFixed(2);
            // Visible window label (used in the period chip + PDF footer).
            const periodLabel = (() => {
              const dates = (visibleDays || [])
                .map((d) => d.service_date)
                .filter(Boolean)
                .sort();
              if (!dates.length) return "";
              return dates.length === 1
                ? dates[0]
                : `${dates[0]} → ${dates[dates.length - 1]}`;
            })();
            const exportPdf = async () => {
              const [{ default: jsPDF }, autoTable] = await Promise.all([
                import("jspdf"),
                import("jspdf-autotable"),
              ]);
              const doc = new jsPDF({ unit: "pt", format: "letter" });
              doc.setFont("helvetica", "bold");
              doc.setFontSize(14);
              doc.text(title.replace(" · audit", "").toUpperCase(), 40, 50);
              doc.setFont("helvetica", "normal");
              doc.setFontSize(10);
              doc.setTextColor(90);
              if (periodLabel) doc.text(`Period: ${periodLabel}`, 40, 68);
              doc.text(`Generated: ${new Date().toLocaleString("en-CA")}`, 40, 82);
              subtitleRules.forEach((rule, i) => {
                doc.text(`• ${rule}`, 40, 100 + i * 14);
              });
              let cursorY = 100 + subtitleRules.length * 14 + 16;
              autoTable.default(doc, {
                startY: cursorY,
                head: [["#", "Patient", "Drug(s)", "Charge"]],
                body: rows.map((r, idx) => [
                  String(idx + 1),
                  consultantMaskNames ? `Patient ${idx + 1}` : r.patient,
                  r.drugs.join(", ") || "—",
                  `-${formatCurrency(r.fee).replace(/[^\d.\-,]/g, "$")}`,
                ]),
                foot: [
                  [
                    { content: "", styles: { fillColor: [27, 59, 46] } },
                    {
                      content: `Total · ${rows.length} client${rows.length === 1 ? "" : "s"}`,
                      colSpan: 2,
                      styles: {
                        halign: "left",
                        fontStyle: "bold",
                        fillColor: [27, 59, 46],
                        textColor: 255,
                      },
                    },
                    {
                      content: `-${formatCurrency(grandTotal)}`,
                      styles: {
                        halign: "right",
                        fontStyle: "bold",
                        fillColor: [27, 59, 46],
                        textColor: 255,
                      },
                    },
                  ],
                ],
                theme: "grid",
                styles: {
                  fontSize: 10,
                  cellPadding: { top: 7, right: 8, bottom: 7, left: 8 },
                  lineColor: [230, 235, 232],
                  lineWidth: 0.5,
                },
                headStyles: {
                  fillColor: [27, 59, 46],
                  textColor: 255,
                  fontStyle: "bold",
                  halign: "center",
                },
                columnStyles: {
                  0: { halign: "center", cellWidth: 32 },
                  1: { halign: "left", fontStyle: "bold" },
                  2: { halign: "left" },
                  3: { halign: "right", cellWidth: 90, fontStyle: "bold" },
                },
                alternateRowStyles: { fillColor: [247, 249, 248] },
              });
              const safeTitle = title.replace(/[^a-z0-9]+/gi, "_").toLowerCase();
              const datePart = (periodLabel || "report")
                .replace(/[^0-9]+/g, "-")
                .replace(/^-|-$/g, "");
              doc.save(`${safeTitle}_${datePart || "export"}.pdf`);
            };
            return (
              <>
                <DialogHeader className="text-left">
                  <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-[#5C746A]">
                    {title}
                  </div>
                  <DialogTitle className="font-heading text-xl sm:text-2xl font-light text-[#1B3B2E] mt-1">
                    {rows.length} client{rows.length === 1 ? "" : "s"}
                  </DialogTitle>
                  <DialogDescription asChild>
                    <ul className="mt-2 space-y-0.5 text-[11px] sm:text-xs text-[#5C746A] leading-snug">
                      {subtitleRules.map((rule) => (
                        <li key={rule} className="flex items-baseline gap-1.5">
                          <span className="text-[#8A9C93] shrink-0">·</span>
                          <span>{rule}</span>
                        </li>
                      ))}
                    </ul>
                  </DialogDescription>
                </DialogHeader>

                {rows.length > 0 ? (
                  <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] px-3.5 py-2.5">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        data-testid="cost-drill-mask-toggle"
                        checked={consultantMaskNames}
                        onChange={(e) => setConsultantMaskNames(e.target.checked)}
                        className="h-4 w-4 rounded border-[#CFD9D4] accent-[#1B3B2E]"
                      />
                      <span className="text-[11px] sm:text-xs text-[#1B3B2E] font-medium">
                        Mask patient names
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={exportPdf}
                      data-testid="cost-drill-export-pdf"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1B3B2E] text-white text-[11px] sm:text-xs font-medium hover:bg-[#2B5E4A] active:bg-[#143226] transition-colors"
                    >
                      <Download className="h-3.5 w-3.5" strokeWidth={2} />
                      Download PDF
                    </button>
                  </div>
                ) : null}

                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] p-3 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                      Clients
                    </div>
                    <div
                      data-testid="cost-drill-count"
                      className="font-mono-data text-xl font-light text-[#1B3B2E] mt-1"
                    >
                      {rows.length}
                    </div>
                  </div>
                  <div className="rounded-xl border border-[#1B3B2E] bg-[#1B3B2E] text-white p-3 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3B8AD]">
                      Total fee
                    </div>
                    <div
                      data-testid="cost-drill-total"
                      className="font-mono-data text-base sm:text-xl font-light mt-1 tabular-nums whitespace-nowrap overflow-hidden text-ellipsis"
                    >
                      {formatCurrency(grandTotal)}
                    </div>
                  </div>
                </div>

                {periodLabel ? (
                  <div
                    className="mt-3 rounded-xl border border-[#1B3B2E] bg-white overflow-hidden"
                    data-testid="cost-drill-period"
                  >
                    <div className="px-3 py-2 bg-[#F0F4F2] border-b border-[#E6EBE8] flex items-center justify-between">
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#1B3B2E]">
                        Visible window
                      </div>
                      <div className="text-[10px] text-[#5C746A] uppercase tracking-[0.1em]">
                        Flat per-patient charge
                      </div>
                    </div>
                    <div className="px-3 py-2 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs sm:text-sm font-bold text-[#1B3B2E] tabular-nums">
                          {periodLabel}
                        </div>
                        <div className="text-[10px] sm:text-[11px] text-[#5C746A] mt-0.5">
                          {rows.length} client{rows.length === 1 ? "" : "s"} ·{" "}
                          {formatCurrency(perPatient)}/patient
                        </div>
                      </div>
                      <div className="font-mono-data text-sm sm:text-base font-semibold text-[#B65A45] tabular-nums whitespace-nowrap">
                        −{formatCurrency(grandTotal)}
                      </div>
                    </div>
                  </div>
                ) : null}


                <div className="mt-4 border border-[#E6EBE8] rounded-xl overflow-hidden divide-y divide-[#F0F4F2]">
                  {rows.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[#5C746A]">
                      No HCV patients in the visible period.
                    </div>
                  ) : (
                    rows.map((r, idx) => {
                      const rowKey = `cost||${r.patient}`;
                      const isExpanded = consultantExpandedRow === rowKey;
                      const displayName = consultantMaskNames
                        ? `Patient ${idx + 1}`
                        : r.patient;
                      return (
                        <div key={r.patient} data-testid={`cost-drill-row-${idx}`}>
                          <button
                            type="button"
                            data-testid={`cost-drill-row-toggle-${idx}`}
                            onClick={() =>
                              setConsultantExpandedRow(isExpanded ? null : rowKey)
                            }
                            aria-expanded={isExpanded}
                            className="w-full flex items-center justify-between gap-3 px-3 py-3 text-left hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors"
                          >
                            <div className="min-w-0 flex items-center gap-2">
                              {isExpanded ? (
                                <ChevronDown className="h-3.5 w-3.5 text-[#5C746A] shrink-0" strokeWidth={2} />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5 text-[#5C746A] shrink-0" strokeWidth={2} />
                              )}
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-[#1B3B2E] truncate">
                                  {displayName}
                                </div>
                                <div className="text-[10px] sm:text-[11px] text-[#8A9C93] truncate">
                                  {r.drugs.join(", ")} · {r.claims.length} claim
                                  {r.claims.length === 1 ? "" : "s"}
                                </div>
                              </div>
                            </div>
                            <div className="font-mono-data text-sm font-semibold text-[#B65A45] tabular-nums whitespace-nowrap shrink-0">
                              −{formatCurrency(r.fee)}
                            </div>
                          </button>
                          {isExpanded ? (
                            <div className="bg-[#F7F9F8] border-t border-[#F0F4F2] px-3 py-2">
                              {(() => {
                                const claimCount = r.claims.length || 1;
                                const perClaim = +(r.fee / claimCount).toFixed(2);
                                return (
                                  <table className="w-full text-[11px]">
                                    <thead className="text-[9px] uppercase tracking-[0.1em] text-[#5C746A] font-semibold">
                                      <tr>
                                        <th className="text-left py-1.5">Date</th>
                                        <th className="text-left py-1.5">Drug</th>
                                        <th className="text-right py-1.5 whitespace-nowrap">Cost / claim</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {[...r.claims]
                                        .sort((a, b) =>
                                          (a.service_date || "").localeCompare(
                                            b.service_date || ""
                                          )
                                        )
                                        .map((c, ci) => (
                                          <tr
                                            key={`${c.rx_id || c.drug_name}-${ci}`}
                                            className="border-t border-[#E6EBE8] text-[#3D4F46]"
                                          >
                                            <td className="py-1.5 font-mono-data tabular-nums whitespace-nowrap">
                                              {c.service_date}
                                            </td>
                                            <td className="py-1.5">
                                              {canonicalDrugName(c.drug_name)}
                                            </td>
                                            <td className="py-1.5 text-right font-mono-data tabular-nums whitespace-nowrap text-[#B65A45]">
                                              −{formatCurrency(perClaim)}
                                            </td>
                                          </tr>
                                        ))}
                                      <tr className="border-t border-[#E6EBE8] bg-white text-[#1B3B2E] font-semibold">
                                        <td className="py-1.5" colSpan={2}>
                                          Subtotal · {claimCount} claim{claimCount === 1 ? "" : "s"}
                                        </td>
                                        <td className="py-1.5 text-right font-mono-data tabular-nums whitespace-nowrap text-[#B65A45]">
                                          −{formatCurrency(r.fee)}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                );
                              })()}
                            </div>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            );
          })() : null}
        </DialogContent>
      </Dialog>

      {/* Consultant fee drill-down — clicking Medical/Sales consultant in the
          HCV P&L opens this dialog showing every client and the consultant
          fee attributed to them in the current visible period (audit view). */}
      <Dialog
        open={!!consultantDrill}
        onOpenChange={(o) => {
          if (!o) {
            setConsultantDrill(null);
            setConsultantExpandedRow(null);
            setConsultantMaskNames(false);
          }
        }}
      >
        <DialogContent className="w-[calc(100vw-1rem)] sm:w-full max-w-md max-h-[88vh] overflow-y-auto overscroll-contain p-4 sm:p-6">
          {(() => {
            if (!consultantDrill) return null;
            const type = consultantDrill.type;
            const title =
              type === "medical"
                ? "Medical consultant · audit"
                : type === "sales"
                  ? "Sales consultant · audit"
                  : type === "np"
                    ? "NP consultant · audit"
                    : "Milestone incentives · audit";
            const subtitleRules =
              type === "medical"
                ? [
                    "0.5% of HCV drug cost",
                    "ODB: per claim",
                    "NIHB: capped at $100 / bottle",
                  ]
                : type === "sales"
                  ? [
                      "0.5% of HCV drug cost",
                      "ODB: per claim",
                      "NIHB: capped at $100 / bottle",
                      "Not paid on Maviret",
                    ]
                  : type === "np"
                    ? [
                        "Capped at $50 / client (per bottle)",
                        "Applies to ODB and NIHB",
                        "Includes Maviret",
                      ]
                    : [
                        `$${HCV_MILESTONE_PER_CLIENT} per HCV client receiving treatment`,
                        "Paid once per unique client in the visible period",
                      ];
            // Build the per-row breakdown depending on the drill type.
            // Consultant drills (medical/sales/np) group by (patient × drug);
            // milestone drill groups by patient only (flat $240 per client).
            let rows;
            if (type === "milestone") {
              const ms = aggregateMilestoneIncentives(visibleTransactionsForFees);
              rows = ms.breakdown
                .map((b) => ({
                  patient: b.patient,
                  drug: b.drugs.join(" · "),
                  drugKey: b.drugs.length === 1 ? b.drugs[0] : null,
                  payer: b.payer,
                  claims: b.claims,
                  fee: b.amount,
                }))
                .sort((a, b) =>
                  (a.patient || "").localeCompare(b.patient || "")
                );
            } else {
              const agg = aggregateConsultantFeesPerCycle(visibleTransactionsForFees, visibleConsultantCycles, patientPayerMap);
              const feeField =
                type === "medical" ? "medical" : type === "sales" ? "sales" : "np";
              rows = agg.breakdown
                .map((b) => ({
                  patient: b.patient,
                  drug: b.drug,
                  drugKey: b.drug,
                  payer: b.payer,
                  claims: b.claims,
                  fee: b[feeField],
                  // Per-cycle attribution (NIHB cap applied per cycle).
                  perCycle: (b.perCycle || []).map((pc) => ({
                    cycleKey: pc.cycleKey,
                    cycleLabel: pc.cycleLabel,
                    payDate: pc.payDate,
                    claims: pc.claims,
                    fee: pc[feeField],
                  })),
                }))
                .filter((r) => r.fee > 0.005)
                .sort((a, b) => b.fee - a.fee);
            }
            const grandTotal = rows.reduce((s, r) => s + r.fee, 0);
            // For each row, figure out which ODB pay date(s) will cover its
            // claims so the user knows exactly when the consultant fee will
            // come due. A patient's claims may span multiple cycles within
            // the visible window — we list every distinct pay date.
            const visibleDates = visibleTransactionsForFees
              .map((t) => t.service_date)
              .filter(Boolean);
            const earliestVisible = visibleDates.length
              ? visibleDates.reduce((a, b) => (a < b ? a : b))
              : null;
            const latestVisible = visibleDates.length
              ? visibleDates.reduce((a, b) => (a > b ? a : b))
              : null;
            // Widen by ~30 days on either side so cycles that pay just
            // outside the visible window (but contain in-window dispensings)
            // are also caught.
            const widenIso = (iso, days) => {
              if (!iso) return iso;
              const d = new Date(`${iso}T00:00:00Z`);
              d.setUTCDate(d.getUTCDate() + days);
              return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
            };
            const cyclesCovering = earliestVisible && latestVisible
              ? odbCyclesInRange(widenIso(earliestVisible, -30), widenIso(latestVisible, 30))
              : [];
            const cycleFor = (iso) => {
              if (!iso) return null;
              for (const c of cyclesCovering) {
                if (iso >= c.dispenseStart && iso <= c.dispenseEnd) return c;
              }
              return null;
            };
            // Pay dates lookup: "patient||drugKey" → sorted unique pay dates
            // (formatted) + the cycle short labels for tooltip use.
            // For consultant rows (non-milestone) we already have perCycle
            // attribution from aggregateConsultantFeesPerCycle — use it
            // directly so this stays in sync with the per-cycle cap logic.
            // For milestone rows we still derive from transactions.
            const rowPayDates = new Map();
            for (const r of rows) {
              if (type !== "milestone" && Array.isArray(r.perCycle) && r.perCycle.length) {
                const dates = r.perCycle.map((pc) => pc.payDate).filter(Boolean);
                const labels = r.perCycle.map((pc) => pc.cycleLabel).filter(Boolean);
                rowPayDates.set(`${r.patient}||${r.drug}`, {
                  payDates: Array.from(new Set(dates)).sort(),
                  cycleLabels: Array.from(new Set(labels)).sort(),
                });
                continue;
              }
              const matches = visibleTransactionsForFees.filter((t) => {
                if (t.patient !== r.patient) return false;
                if (type === "milestone") return !!hcvDrugKey(t.drug_name);
                const k = hcvDrugKey(t.drug_name);
                return k && k === r.drugKey;
              });
              const dates = new Set();
              const labels = new Set();
              for (const t of matches) {
                const c = cycleFor(t.service_date);
                if (c) {
                  dates.add(c.payDate);
                  labels.add(c.shortLabel);
                }
              }
              rowPayDates.set(`${r.patient}||${r.drug}`, {
                payDates: Array.from(dates).sort(),
                cycleLabels: Array.from(labels).sort(),
              });
            }
            // Roll up totals per pay date for the prominent header banner.
            // For consultant rows (non-milestone) we use the EXACT per-cycle
            // fee from aggregateConsultantFeesPerCycle — no proportional
            // approximation needed, so NIHB-capped patients are attributed
            // $100 to EACH cycle their bottle touches (matching how each
            // cycle pays out individually).
            // Milestone rows still split proportionally by claim count.
            const payDateTotals = new Map();
            for (const r of rows) {
              if (type !== "milestone" && Array.isArray(r.perCycle) && r.perCycle.length) {
                for (const pc of r.perCycle) {
                  if (!pc.payDate || !(pc.fee > 0.005)) continue;
                  const slot = payDateTotals.get(pc.payDate) || {
                    fee: 0,
                    clients: new Set(),
                    cycleLabels: new Set(),
                  };
                  slot.fee += pc.fee;
                  slot.clients.add(r.patient);
                  if (pc.cycleLabel) slot.cycleLabels.add(pc.cycleLabel);
                  payDateTotals.set(pc.payDate, slot);
                }
                continue;
              }
              // Milestone branch — proportional split by claim count.
              const matches = visibleTransactionsForFees.filter((t) => {
                if (t.patient !== r.patient) return false;
                if (type === "milestone") return !!hcvDrugKey(t.drug_name);
                const k = hcvDrugKey(t.drug_name);
                return k && k === r.drugKey;
              });
              const claimsByPayDate = new Map();
              for (const t of matches) {
                const c = cycleFor(t.service_date);
                if (!c) continue;
                const slot = claimsByPayDate.get(c.payDate) || { count: 0, cycleLabel: c.shortLabel };
                slot.count += 1;
                claimsByPayDate.set(c.payDate, slot);
              }
              const totalClaimsInCycles = Array.from(claimsByPayDate.values()).reduce(
                (s, v) => s + v.count, 0
              );
              if (!totalClaimsInCycles) continue;
              for (const [payDate, info] of claimsByPayDate) {
                const share = r.fee * (info.count / totalClaimsInCycles);
                const slot = payDateTotals.get(payDate) || {
                  fee: 0,
                  clients: new Set(),
                  cycleLabels: new Set(),
                };
                slot.fee += share;
                slot.clients.add(r.patient);
                if (info.cycleLabel) slot.cycleLabels.add(info.cycleLabel);
                payDateTotals.set(payDate, slot);
              }
            }
            const payDateBreakdown = Array.from(payDateTotals.entries())
              .sort((a, b) => (a[0] < b[0] ? -1 : 1))
              .map(([payDate, v]) => ({
                payDate,
                fee: v.fee,
                clients: v.clients.size,
                cycleLabels: Array.from(v.cycleLabels).sort(),
              }));
            const fmtPayDate = (iso) =>
              iso ? formatPayDate(iso) : "—";
            // Build the visible-period label for the PDF footer.
            const periodLabel = (() => {
              const days = visibleDays && visibleDays.length ? visibleDays : [];
              if (!days.length) return "";
              const dates = days.map((d) => d.service_date).filter(Boolean).sort();
              if (!dates.length) return "";
              return dates.length === 1
                ? dates[0]
                : `${dates[0]} → ${dates[dates.length - 1]}`;
            })();
            const exportPdf = async () => {
              const [{ default: jsPDF }, autoTable] = await Promise.all([
                import("jspdf"),
                import("jspdf-autotable"),
              ]);
              const doc = new jsPDF({ unit: "pt", format: "letter" });
              doc.setFont("helvetica", "bold");
              doc.setFontSize(14);
              doc.text(title.replace(" · audit", "").toUpperCase(), 40, 50);
              doc.setFont("helvetica", "normal");
              doc.setFontSize(10);
              doc.setTextColor(90);
              if (periodLabel) doc.text(`Period: ${periodLabel}`, 40, 68);
              doc.text(`Generated: ${new Date().toLocaleString("en-CA")}`, 40, 82);
              subtitleRules.forEach((rule, i) => {
                doc.text(`• ${rule}`, 40, 100 + i * 14);
              });
              let cursorY = 100 + subtitleRules.length * 14 + 16;
              // Pays-on breakdown banner — printed near the top so users
              // can see at a glance which ODB deposit pays each amount.
              if (payDateBreakdown.length) {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(10);
                doc.setTextColor(27, 59, 46);
                doc.text("PAYS ON", 40, cursorY);
                cursorY += 6;
                autoTable.default(doc, {
                  startY: cursorY,
                  head: [["Deposit date", "Clients", "Cycles", "Fee"]],
                  body: payDateBreakdown.map((p) => [
                    fmtPayDate(p.payDate),
                    String(p.clients),
                    p.cycleLabels.join(", ") || "—",
                    formatCurrency(p.fee).replace(/[^\d.\-,]/g, "$"),
                  ]),
                  theme: "grid",
                  styles: {
                    fontSize: 10,
                    cellPadding: { top: 7, right: 8, bottom: 7, left: 8 },
                    lineColor: [230, 235, 232],
                    lineWidth: 0.5,
                  },
                  headStyles: {
                    fillColor: [27, 59, 46],
                    textColor: 255,
                    fontStyle: "bold",
                    halign: "center",
                  },
                  columnStyles: {
                    0: { halign: "left", fontStyle: "bold", cellWidth: 110 },
                    1: { halign: "center", cellWidth: 65 },
                    2: { halign: "left" },
                    3: { halign: "right", cellWidth: 90, fontStyle: "bold" },
                  },
                  alternateRowStyles: { fillColor: [247, 249, 248] },
                });
                cursorY = (doc.lastAutoTable?.finalY || cursorY) + 20;
              }
              const headStartY = cursorY;
              const body = rows.map((r, idx) => {
                const pd = rowPayDates.get(`${r.patient}||${r.drug}`);
                const payDateStr = pd && pd.payDates.length
                  ? pd.payDates.map(fmtPayDate).join(", ")
                  : "—";
                return [
                  consultantMaskNames ? `Patient ${idx + 1}` : r.patient,
                  r.drug || "—",
                  String(r.claims || 0),
                  r.payer || "ODB",
                  payDateStr,
                  formatCurrency(r.fee).replace(/[^\d.\-,]/g, "$"),
                ];
              });
              autoTable.default(doc, {
                startY: headStartY,
                head: [["Patient", "Drug", "Claims", "Payer", "ODB Pay Date", "Fee"]],
                body,
                theme: "grid",
                styles: {
                  fontSize: 9,
                  cellPadding: { top: 6, right: 6, bottom: 6, left: 6 },
                  lineColor: [230, 235, 232],
                  lineWidth: 0.5,
                  valign: "middle",
                },
                headStyles: {
                  fillColor: [27, 59, 46],
                  textColor: 255,
                  fontStyle: "bold",
                  halign: "center",
                },
                columnStyles: {
                  0: { halign: "left", fontStyle: "bold" },
                  1: { halign: "center" },
                  2: { halign: "center", cellWidth: 50 },
                  3: { halign: "center", cellWidth: 55 },
                  4: { halign: "center", fontSize: 8 },
                  5: { halign: "right", cellWidth: 75, fontStyle: "bold" },
                },
                alternateRowStyles: { fillColor: [247, 249, 248] },
                foot: [
                  [
                    {
                      content: `${rows.length} client${rows.length === 1 ? "" : "s"}`,
                      colSpan: 5,
                      styles: { halign: "right", fontStyle: "bold", fillColor: [240, 244, 242], textColor: [27, 59, 46] },
                    },
                    {
                      content: formatCurrency(grandTotal),
                      styles: { halign: "right", fontStyle: "bold", fillColor: [27, 59, 46], textColor: 255 },
                    },
                  ],
                ],
              });
              const safeTitle = title.replace(/[^a-z0-9]+/gi, "_").toLowerCase();
              const datePart = (periodLabel || "report").replace(/[^0-9]+/g, "-").replace(/^-|-$/g, "");
              doc.save(`${safeTitle}_${datePart || "export"}.pdf`);
            };
            return (
              <>
                <DialogHeader className="text-left">
                  <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-[#5C746A]">
                    {title}
                  </div>
                  <DialogTitle className="font-heading text-xl sm:text-2xl font-light text-[#1B3B2E] mt-1">
                    {rows.length} client{rows.length === 1 ? "" : "s"}
                  </DialogTitle>
                  <DialogDescription asChild>
                    <ul className="mt-2 space-y-0.5 text-[11px] sm:text-xs text-[#5C746A] leading-snug">
                      {subtitleRules.map((rule) => (
                        <li key={rule} className="flex items-baseline gap-1.5">
                          <span className="text-[#8A9C93] shrink-0">·</span>
                          <span>{rule}</span>
                        </li>
                      ))}
                    </ul>
                  </DialogDescription>
                </DialogHeader>

                {/* Export controls — mask toggle + PDF download. Mask
                    replaces patient names with "Patient 1, 2, 3 …" so the
                    statement can be sent outside the pharmacy without PHI. */}
                {rows.length > 0 ? (
                  <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] px-3.5 py-2.5">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        data-testid="consultant-mask-toggle"
                        checked={consultantMaskNames}
                        onChange={(e) => setConsultantMaskNames(e.target.checked)}
                        className="h-4 w-4 rounded border-[#CFD9D4] accent-[#1B3B2E]"
                      />
                      <span className="text-[11px] sm:text-xs text-[#1B3B2E] font-medium">
                        Mask patient names
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={exportPdf}
                      data-testid="consultant-export-pdf"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1B3B2E] text-white text-[11px] sm:text-xs font-medium hover:bg-[#2B5E4A] active:bg-[#143226] transition-colors"
                    >
                      <Download className="h-3.5 w-3.5" strokeWidth={2} />
                      Download PDF
                    </button>
                  </div>
                ) : null}

                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-[#E6EBE8] bg-[#F7F9F8] p-3 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                      Clients
                    </div>
                    <div
                      data-testid="consultant-drill-count"
                      className="font-mono-data text-xl font-light text-[#1B3B2E] mt-1"
                    >
                      {rows.length}
                    </div>
                  </div>
                  <div className="rounded-xl border border-[#1B3B2E] bg-[#1B3B2E] text-white p-3 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3B8AD]">
                      Total fee
                    </div>
                    <div
                      data-testid="consultant-drill-total"
                      className="font-mono-data text-base sm:text-xl font-light mt-1 tabular-nums whitespace-nowrap overflow-hidden text-ellipsis"
                    >
                      {formatCurrency(grandTotal)}
                    </div>
                  </div>
                </div>

                {payDateBreakdown.length ? (
                  <div
                    className="mt-3 rounded-xl border border-[#1B3B2E] bg-white overflow-hidden"
                    data-testid="consultant-drill-pay-dates"
                  >
                    <div className="px-3 py-2 bg-[#F0F4F2] border-b border-[#E6EBE8] flex items-center justify-between">
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#1B3B2E]">
                        Pays on
                      </div>
                      <div className="text-[10px] text-[#5C746A] uppercase tracking-[0.1em]">
                        {payDateBreakdown.length} deposit{payDateBreakdown.length === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div className="divide-y divide-[#F0F4F2]">
                      {payDateBreakdown.map((p) => (
                        <div
                          key={p.payDate}
                          className="px-3 py-2 flex items-center justify-between gap-3"
                          data-testid={`consultant-pay-date-${p.payDate}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-xs sm:text-sm font-bold text-[#1B3B2E] tabular-nums">
                              {fmtPayDate(p.payDate)}
                            </div>
                            <div className="text-[10px] sm:text-[11px] text-[#5C746A] mt-0.5">
                              {p.clients} client{p.clients === 1 ? "" : "s"}
                              {p.cycleLabels.length
                                ? ` · ${p.cycleLabels.join(", ")}`
                                : ""}
                            </div>
                          </div>
                          <div className="font-mono-data text-sm sm:text-base font-semibold text-[#B65A45] tabular-nums whitespace-nowrap">
                            −{formatCurrency(p.fee)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="mt-4 border border-[#E6EBE8] rounded-xl overflow-hidden divide-y divide-[#F0F4F2]">
                  {rows.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[#5C746A]">
                      No HCV claims in the visible period attract this consultant fee.
                    </div>
                  ) : (
                    rows.map((r, idx) => {
                      const rowKey = `${r.patient}||${r.drug}`;
                      const isExpanded = consultantExpandedRow === rowKey;
                      // Pull every visible claim in this period for this
                      // patient so the auditor sees exactly what fed into
                      // the fee. Consultant drills narrow to the matching
                      // drug; milestone drill shows every HCV claim for
                      // the patient since the $240 is per-client (not per-drug).
                      const claimRows = isExpanded
                        ? visibleTransactionsForFees
                            .filter((t) => {
                              if (t.patient !== r.patient) return false;
                              const k = hcvDrugKey(t.drug_name);
                              if (!k) return false;
                              if (type === "milestone") return true;
                              return k === r.drugKey;
                            })
                            .slice()
                            .sort((a, b) =>
                              (a.service_date || "").localeCompare(b.service_date || "")
                            )
                        : [];
                      return (
                        <div
                          key={`${r.patient}-${r.drug}-${idx}`}
                          data-testid={`consultant-drill-row-${idx}`}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              setConsultantExpandedRow(isExpanded ? null : rowKey)
                            }
                            className="w-full px-3.5 py-3 flex items-start justify-between gap-3 text-left hover:bg-[#F7F9F8] active:bg-[#F0F4F2] transition-colors"
                            aria-expanded={isExpanded}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-heading text-sm sm:text-base font-medium text-[#1B3B2E] flex items-center gap-1.5 flex-wrap">
                                <ChevronRight
                                  className={`h-3.5 w-3.5 text-[#8A9C93] shrink-0 transition-transform ${
                                    isExpanded ? "rotate-90" : ""
                                  }`}
                                  strokeWidth={2}
                                />
                                <span className="break-words">
                                  {consultantMaskNames ? `Patient ${idx + 1}` : r.patient}
                                </span>
                                {r.payer === "NIHB" ? (
                                  <span className="text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.1em] text-[#8A6B2B] bg-[#F4E4C5] px-1 py-0.5 rounded shrink-0">
                                    NIHB
                                  </span>
                                ) : null}
                              </div>
                              <div className="text-[10px] sm:text-[11px] text-[#8A9C93] mt-0.5 tabular-nums break-words pl-5">
                                {r.claims} claim{r.claims === 1 ? "" : "s"} · {r.drug}
                              </div>
                              {(() => {
                                const pd = rowPayDates.get(`${r.patient}||${r.drug}`);
                                if (!pd || !pd.payDates.length) return null;
                                return (
                                  <div
                                    className="text-[10px] sm:text-[11px] text-[#5C746A] mt-0.5 tabular-nums break-words pl-5"
                                    data-testid={`consultant-drill-row-${idx}-pay-dates`}
                                  >
                                    <span className="text-[#8A9C93] uppercase tracking-[0.1em] text-[9px] sm:text-[10px] mr-1.5">
                                      Pays
                                    </span>
                                    {pd.payDates.map(fmtPayDate).join(" · ")}
                                  </div>
                                );
                              })()}
                            </div>
                            <div className="text-right shrink-0">
                              <div className="font-mono-data text-sm sm:text-base font-semibold text-[#B65A45] tabular-nums whitespace-nowrap">
                                −{formatCurrency(r.fee)}
                              </div>
                              <div className="text-[9px] sm:text-[10px] text-[#8A9C93] uppercase tracking-[0.1em] mt-0.5">
                                {type === "milestone"
                                  ? "Per client"
                                  : type === "np"
                                    ? "Capped $50/bottle"
                                    : r.payer === "NIHB"
                                      ? "Capped $100/bottle"
                                      : "0.5%"}
                              </div>
                            </div>
                          </button>
                          {isExpanded ? (
                            <div
                              className="bg-[#F7F9F8] border-t border-[#F0F4F2] divide-y divide-[#F0F4F2]"
                              data-testid={`consultant-drill-row-${idx}-claims`}
                            >
                              {claimRows.length === 0 ? (
                                <div className="px-4 py-3 text-[11px] text-[#5C746A]">
                                  No matching claims in this period.
                                </div>
                              ) : (
                                claimRows.map((t, ci) => {
                                  const c = hcvConsultantsForRow(t);
                                  // Per-claim share — only meaningful for ODB
                                  // medical/sales (true per-row %). NIHB &
                                  // NP are capped, so individual claims don't
                                  // map cleanly to a fee share.
                                  const rowFee =
                                    type === "medical" ? c.medical : c.sales;
                                  let rightLabel;
                                  if (type === "milestone") {
                                    rightLabel = "—";
                                  } else if (type === "np") {
                                    rightLabel = "incl. in cap";
                                  } else if (r.payer === "NIHB") {
                                    rightLabel = "incl. in cap";
                                  } else {
                                    rightLabel = `−${formatCurrency(rowFee)}`;
                                  }
                                  return (
                                    <div
                                      key={`${t.patient}-${t.service_date}-${t.rx_id || ci}`}
                                      className="px-4 py-2 flex items-center justify-between gap-3"
                                    >
                                      <div className="min-w-0 flex-1">
                                        <div className="text-[11px] sm:text-xs text-[#1B3B2E] font-medium tabular-nums">
                                          {t.service_date || "—"}
                                          {t.rx_id ? (
                                            <span className="ml-2 text-[10px] text-[#8A9C93] font-normal">
                                              Rx {t.rx_id}
                                            </span>
                                          ) : null}
                                        </div>
                                        <div className="text-[10px] text-[#8A9C93] mt-0.5 truncate tabular-nums">
                                          Plan 1 {formatCurrency(t.plan1_paid || 0)}
                                        </div>
                                      </div>
                                      <div className="text-right shrink-0">
                                        <div className="font-mono-data text-[11px] sm:text-xs text-[#5C746A] tabular-nums whitespace-nowrap">
                                          {rightLabel}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* JARVIS — voice assistant. Hidden by default; revealed via 4 taps
          on the Charts section. Once revealed it auto-opens and the floating
          mic FAB stays visible for the session.
          getContext supplies a snapshot for intent parsing; onAction maps
          structured intents back to dashboard state updates. */}
      {jarvisRevealed && (
      <JarvisAssistant
        autoOpenSignal={jarvisAutoOpenSignal}
        getContext={() => ({
          report_loaded: num_days > 0,
          range_start: date_range_start,
          range_end: date_range_end,
          current_start: customRange?.start || date_range_start,
          current_end: customRange?.end || date_range_end,
          view_mode: viewMode, // 'hcv' or 'non-hcv'
          total_dispensings,
          total_patients: analysisUniquePatients,
          // First 250 patient names — enough for fuzzy/exact matching
          // without ballooning the prompt.
          patients: patientsBreakdown.slice(0, 250).map((p) => p.patient),
          has_dob_list: !!birthdayList,
        })}
        onBeforeIntent={() => {
          jarvisIntentRef.current = { start: null, end: null, viewMode: null };
        }}
        onAction={async (action, params) => {
          if (action === "set_date_range" && params.start && params.end) {
            setCustomRange({ start: params.start, end: params.end });
            setSelectedMonth("all");
            setSelectedDate("all");
            // Remember for any subsequent report_metric in the same dispatch.
            jarvisIntentRef.current.start = params.start;
            jarvisIntentRef.current.end = params.end;
            return null;
          }
          if (action === "set_view_mode" && params.mode) {
            const normalized = String(params.mode).toLowerCase().replace(/_/g, "-");
            if (normalized === "hcv" || normalized === "non-hcv") {
              setViewMode(normalized);
              jarvisIntentRef.current.viewMode = normalized;
              return null;
            }
            return null;
          }
          if (action === "show_patient" && params.patient) {
            // Case-insensitive search through visible patients.
            const target = params.patient.toUpperCase().trim();
            const match =
              patientsBreakdown.find((p) => p.patient.toUpperCase() === target) ||
              patientsBreakdown.find((p) =>
                p.patient.toUpperCase().includes(target)
              );
            if (match) {
              setDrillDown({
                patient: match.patient,
                service_date: null,
                pretty_date: "all visible dates",
              });
              return null;
            }
            return "I'm afraid that patient isn't on the loaded report, sir.";
          }
          if (action === "show_birthday_month" && params.month) {
            if (!birthdayList) {
              return "No birthday list is loaded, sir. Kindly upload one first.";
            }
            const m = String(params.month).padStart(2, "0");
            setBirthdayMonth(`2000-${m}`);
            return null;
          }
          if (action === "open_consultant_drill" && params.type) {
            const valid = ["medical", "sales", "np", "milestone"];
            if (valid.includes(params.type)) {
              // Switching to HCV makes consultant numbers actually populate.
              setViewMode("hcv");
              setConsultantDrill({ type: params.type });
              return null;
            }
          }
          if (action === "scroll_to" && params.section) {
            const map = {
              patient_search: "patient-search-collapse",
              birthday_lookup: "birthday-lookup-collapse",
              drug_payments: "odb-cycles-collapse",
              patient_volumes: "patient-volume-collapse",
              pl: "profitability-collapse",
              top_line_sales: "top-line-sales-collapse",
            };
            const id = map[params.section];
            const el = id ? document.querySelector(`[data-testid="${id}"]`) : null;
            if (el) {
              // Auto-expand the collapsed accordion so the user actually
              // sees the data, not just an empty section header.
              const toggle = el.querySelector(`[data-testid="${id}-toggle"]`);
              if (toggle && toggle.getAttribute("aria-expanded") === "false") {
                toggle.click();
              }
              // Give the DOM a frame to expand before scrolling so the
              // scrollIntoView lands on the correct (post-expansion) Y.
              requestAnimationFrame(() => {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
              });
              return null;
            }
            return "I couldn't locate that section, sir.";
          }
          if (action === "show_chart") {
            // ================================================================
            // 🔒 LOCKED REGION — JARVIS show_chart RENDERING
            // ----------------------------------------------------------------
            // The handler below pins the rendering of two locked report
            // families:
            //   1. sales_by_month  (pharmacy / hepatitis sales over time)
            //   2. odb_cycles      (ODB cycle deposits — bars match the
            //                       dashboard cycle cards 1:1)
            //
            // Before changing ANYTHING here, run:
            //   cd /app/backend && python -m pytest tests/test_cycle_reporting_lock.py -v
            // All 30 tests must pass. See /app/CYCLE_REPORTING_CONTRACT.md
            // for the full contract this region locks. In particular:
            //   • The odb_cycles branch MUST read from odbCycleDepositsRef
            //     (not the closure-captured odbCycleDeposits) — stale
            //     closure bugs have broken this twice already.
            //   • X-axis labels for odb_cycles MUST be formatted pay
            //     dates ("Jun 15"), never dispense-window shortLabels.
            //   • View_mode flow-through for HCV/non-HCV filters MUST
            //     apply to both report families.
            // ================================================================
            // Build an inline chart spec that JARVIS will render in his
            // own HUD (in place of the hologram). The spec shape is
            // documented in /app/frontend/src/components/JarvisChart.jsx.
            //
            // IMPORTANT: we read from `originalAnalysis?.transactions`
            // (the FULL unfiltered transaction list) rather than the
            // `transactions` array in scope — that one has already been
            // filtered by the dashboard's current viewMode, which would
            // make an HCV chart return $0 whenever the dashboard is in
            // Non-HCV view (and vice versa).
            const allTx = (originalAnalysis?.transactions) || transactions || [];
            const chartKey = String(params.chart || params.metric || "").toLowerCase();
            const start = params.start || customRange?.start || date_range_start;
            const end = params.end || customRange?.end || date_range_end;
            // ONLY use the LLM's explicit view_mode. Do NOT fall back to
            // the dashboard's current `viewMode` — that creates a sticky
            // filter where one HCV question silently scopes every future
            // chart to HCV, leaving Jan-Apr empty when the user actually
            // asked for total sales. If the LLM didn't pass view_mode,
            // show all sales.
            const requestedView = params.view_mode
              ? String(params.view_mode).toLowerCase().replace(/_/g, "-")
              : "";
            const limit = Number(params.limit) > 0 ? Math.min(10, Number(params.limit)) : 5;
            const inRange = allTx.filter(
              (t) => t.service_date >= start && t.service_date <= end,
            );
            // Build a patient → payer classification (ODB vs NIHB) lazily,
            // only computed when the request explicitly asks for one of
            // those views. Payer is determined per-PATIENT (not per
            // transaction) by classifyPayer in hcv.js.
            let payerByPatient = null;
            if (requestedView === "odb" || requestedView === "nihb") {
              payerByPatient = new Map();
              const byPatient = new Map();
              for (const t of inRange) {
                const arr = byPatient.get(t.patient_id) || [];
                arr.push(t);
                byPatient.set(t.patient_id, arr);
              }
              for (const [pid, rows] of byPatient.entries()) {
                payerByPatient.set(pid, classifyPayer(rows));
              }
            }
            const scoped = requestedView === "hcv"
              ? inRange.filter((t) => isHcvDrug(t.drug_name))
              : requestedView === "non-hcv"
                ? inRange.filter((t) => !isHcvDrug(t.drug_name))
                : requestedView === "odb"
                  ? inRange.filter((t) => payerByPatient.get(t.patient_id) === "ODB")
                  : requestedView === "nihb"
                    ? inRange.filter((t) => payerByPatient.get(t.patient_id) === "NIHB")
                    : inRange;
            const fmtMoneyShort = (v) => {
              const abs = Math.abs(v);
              if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} million dollars`;
              if (abs >= 1000) return `${Math.round(v / 1000)} thousand dollars`;
              return `${Math.round(v)} dollars`;
            };
            const prettyRange = `${start} to ${end}`;

            // ----- bar / line shared helpers -----
            const monthLabel = (ym) => {
              const [y, m] = ym.split("-");
              const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
              return `${names[+m - 1]} ${String(y).slice(2)}`;
            };
            const dayLabel = (d) => d.slice(5); // MM-DD

            // ----- monthly revenue bar / line (top-line combined) -----
            // Enumerates EVERY month between start and end inclusive — even
            // those with zero sales — so the chart always spans the full
            // requested period instead of collapsing to a single bar when
            // only one month happens to have data.
            const monthlyRevenue = () => {
              const byMonth = new Map();
              for (const t of scoped) {
                const ym = (t.service_date || "").slice(0, 7);
                if (!ym) continue;
                byMonth.set(ym, (byMonth.get(ym) || 0) + combinedAmountForRow(t));
              }
              if (!start || !end) {
                return [...byMonth.entries()].sort((a, b) => a[0].localeCompare(b[0]));
              }
              // Cap the end at the CURRENT month — months in the future
              // would just render as $0 (nothing has happened yet) and
              // pad the chart with flat lines / overlapping labels.
              const now = new Date();
              const todayYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
              const requestedEndYm = end.slice(0, 7);
              const effectiveEndYm = requestedEndYm > todayYm ? todayYm : requestedEndYm;
              const [sy, sm] = start.slice(0, 7).split("-").map(Number);
              const [ey, em] = effectiveEndYm.split("-").map(Number);
              const out = [];
              let y = sy, m = sm;
              // Hard safety: max 24 months so a stray bad range never
              // explodes the chart.
              let guard = 0;
              while ((y < ey || (y === ey && m <= em)) && guard < 24) {
                const ym = `${y}-${String(m).padStart(2, "0")}`;
                out.push([ym, byMonth.get(ym) || 0]);
                m += 1;
                if (m > 12) { m = 1; y += 1; }
                guard += 1;
              }
              return out;
            };

            // ----- monthly profit (revenue - operating cost share - HCV drug cost) -----
            const monthlyProfit = () => {
              const dispByMonth = new Map();
              const revByMonth = new Map();
              for (const t of scoped) {
                const ym = (t.service_date || "").slice(0, 7);
                if (!ym) continue;
                revByMonth.set(ym, (revByMonth.get(ym) || 0) + combinedAmountForRow(t));
                dispByMonth.set(ym, (dispByMonth.get(ym) || 0) + 1);
              }
              const OP_COST_PER_MONTH = 25000;
              // Re-use the full-range monthly list so missing months show
              // as just operating-cost losses (which is the truthful answer).
              const months = monthlyRevenue();
              return months.map(([ym, rev]) => {
                let hcvCost = 0;
                for (const t of scoped) {
                  if ((t.service_date || "").slice(0, 7) !== ym) continue;
                  if (!isHcvDrug(t.drug_name)) continue;
                  hcvCost += unitsPerClaimForPayer(classifyPayer(t)) * HCV_COST_PER_UNIT;
                }
                return [ym, rev - OP_COST_PER_MONTH - hcvCost];
              });
            };

            // ============================================================
            //   sales_by_month — combo bar (monthly) + cumulative line (YTD)
            // ============================================================
            if (chartKey === "sales_by_month" || chartKey === "revenue_by_month") {
              const rows = monthlyRevenue();
              if (!rows.length) return { spoken: "No sales data in that range, sir." };
              const top = rows.reduce((a, b) => (b[1] > a[1] ? b : a));
              let running = 0;
              const cum = rows.map(([ym, v]) => {
                running += v;
                return { label: monthLabel(ym), value: running };
              });
              const titleSuffix = requestedView === "hcv"
                ? " · HCV"
                : requestedView === "non-hcv"
                  ? " · Non-HCV"
                  : "";
              return {
                spoken: `${monthLabel(top[0])} leads at ${fmtMoneyShort(top[1])}; cumulative reaches ${fmtMoneyShort(running)}, sir.`,
                chart: {
                  type: "combo_bar_line",
                  title: params.title || `Sales by Month${titleSuffix}`,
                  unit: "$",
                  bars: rows.map(([ym, v]) => ({ label: monthLabel(ym), value: v })),
                  line: cum,
                  caption: prettyRange,
                },
              };
            }
            // ============================================================
            //   revenue_trend / profit_trend — line
            // ============================================================
            if (chartKey === "revenue_trend") {
              const rows = monthlyRevenue();
              if (!rows.length) return { spoken: "No revenue data in that range, sir." };
              return {
                spoken: `Revenue trend across ${rows.length} months, sir.`,
                chart: {
                  type: "line",
                  title: params.title || "Revenue Trend",
                  unit: "$",
                  data: rows.map(([ym, v]) => ({ label: monthLabel(ym), value: v })),
                  caption: prettyRange,
                },
              };
            }
            if (chartKey === "profit_trend") {
              const rows = monthlyProfit();
              if (!rows.length) return { spoken: "No profit data in that range, sir." };
              const top = rows.reduce((a, b) => (b[1] > a[1] ? b : a));
              return {
                spoken: `${monthLabel(top[0])} peaks at ${fmtMoneyShort(top[1])}, sir.`,
                chart: {
                  type: "line",
                  title: params.title || "Profit Trend",
                  unit: "$",
                  data: rows.map(([ym, v]) => ({ label: monthLabel(ym), value: v })),
                  caption: prettyRange,
                },
              };
            }
            // ============================================================
            //   dispensings_per_day — bar
            // ============================================================
            if (chartKey === "dispensings_per_day") {
              const byDay = new Map();
              for (const t of scoped) {
                const d = t.service_date || "";
                if (!d) continue;
                byDay.set(d, (byDay.get(d) || 0) + 1);
              }
              const rows = [...byDay.entries()].sort((a, b) => a[0].localeCompare(b[0]));
              if (!rows.length) return { spoken: "No dispensings in that range, sir." };
              const total = rows.reduce((s, [, v]) => s + v, 0);
              return {
                spoken: `${total.toLocaleString()} dispensings across ${rows.length} days, sir.`,
                chart: {
                  type: "bar",
                  title: params.title || "Dispensings per Day",
                  unit: "",
                  data: rows.map(([d, v]) => ({ label: dayLabel(d), value: v })),
                  caption: prettyRange,
                },
              };
            }
            // ============================================================
            //   odb_cycles — bar chart of the ODB CYCLE DEPOSITS that
            //   already power the cycle cards on the dashboard. We use
            //   the closure-captured `odbCycleDeposits` memo so the
            //   chart MATCHES the cards 1:1 (same dollar amounts, same
            //   pay dates) — anything else risks a drift between what
            //   the user sees in the cards and what JARVIS draws.
            //
            //   X-axis = pay date ("Jun 15", "Jun 30", ...) sorted
            //   chronologically left → right.
            //   Y-axis = Plan-1 deposit dollars on that cycle.
            //   Cumulative line = running YTD deposits.
            //
            //   view_mode handling — when the user asks "ODB payment
            //   cycles for pharmacy sales / hepatitis sales", we
            //   recompute each cycle's plan1 by filtering the cycle's
            //   in-window transactions by HCV/non-HCV. Without a filter,
            //   we use c.plan1 directly (the ODB-classified Plan-1
            //   total already on the cards).
            // ============================================================
            if (chartKey === "odb_cycles") {
              // CRITICAL: read from refs, NOT the closure. The closure
              // captures `odbCycleDeposits` at the moment the onAction
              // arrow function was defined (which can be BEFORE the
              // dashboard finished loading data → empty array → JARVIS
              // says "no cycles to chart" forever). The ref always
              // mirrors the latest computed value.
              const liveCycleDeposits = odbCycleDepositsRef.current || [];
              const liveAllTx = allTransactionsRef.current || [];
              if (!liveCycleDeposits.length) {
                // Helpful diagnostic — if you see this fire while the
                // dashboard cards clearly show cycle deposits, file a
                // bug. The refs should always mirror the memo.
                console.warn(
                  "[JARVIS odb_cycles] empty cycle deposits ref",
                  { transactions: liveAllTx.length },
                );
                return { spoken: "No O D B cycles to chart, sir." };
              }
              const cycleHcvMode = requestedView === "hcv"
                ? "hcv"
                : requestedView === "non-hcv"
                  ? "non_hcv"
                  : "all";
              // Source data: directly clone the live deposits so the
              // cards and chart never disagree.
              let cycleRows = liveCycleDeposits.map((c) => ({ ...c }));
              // If HCV / non-HCV filter is requested, recompute each
              // cycle's plan1 by iterating the FULL transaction list
              // so patient classification is identical to what the
              // cards used. The classifyPayer call needs all of a
              // patient's claims to be stable.
              if (cycleHcvMode !== "all") {
                const fullByPatient = new Map();
                for (const t of liveAllTx) {
                  const arr = fullByPatient.get(t.patient) || [];
                  arr.push(t);
                  fullByPatient.set(t.patient, arr);
                }
                const fullPayer = new Map();
                for (const [pt, rows] of fullByPatient.entries()) {
                  fullPayer.set(pt, classifyPayer(rows));
                }
                cycleRows = cycleRows.map((c) => {
                  let plan1 = 0;
                  for (const t of liveAllTx) {
                    const d = t.service_date;
                    if (!d) continue;
                    if (d < c.dispenseStart || d > c.dispenseEnd) continue;
                    if (fullPayer.get(t.patient) !== "ODB") continue;
                    if (cycleHcvMode === "hcv" && !isHcvDrug(t.drug_name)) continue;
                    if (cycleHcvMode === "non_hcv" && isHcvDrug(t.drug_name)) continue;
                    plan1 += t.plan1_paid || 0;
                  }
                  return { ...c, plan1: +plan1.toFixed(2) };
                });
              }
              // JARVIS DATE-RANGE FILTER. When the user EXPLICITLY asks
              // for a sub-range (e.g. "ODB cycles from January to June
              // 2026"), the LLM/regex sets params.start / params.end and
              // we filter cycles by PAY DATE.
              //
              // When the user does NOT specify a range (e.g. "show me
              // ODB payment cycles for hepatitis"), we leave the chart
              // unfiltered so PENDING/upcoming cycles (whose payDate is
              // beyond the report's date_range_end) still appear. The
              // user explicitly asked to see these: a cycle whose
              // dispensing window has activity but whose deposit hasn't
              // arrived yet is exactly the next paycheck — they want to
              // see it.
              const jarvisGaveRange = !!(params.start || params.end);
              if (jarvisGaveRange && start && end) {
                cycleRows = cycleRows.filter(
                  (c) => c.payDate >= start && c.payDate <= end,
                );
              }
              // Drop zero-deposit cycles so the chart focuses on real
              // paydays. (After an HCV filter this can prune a lot.)
              cycleRows = cycleRows.filter((c) => (c.plan1 || 0) > 0);
              if (!cycleRows.length) {
                return { spoken: "No matching O D B deposits in that range, sir." };
              }
              // Sort chronologically by pay date (ascending) so X-axis
              // reads left→right.
              cycleRows.sort((a, b) =>
                (a.payDate || "").localeCompare(b.payDate || ""),
              );
              // Format pay date as "MMM DD" (e.g. "Jun 15").
              const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
              const fmtPay = (iso) => {
                if (!iso) return "?";
                const [, mm, dd] = iso.split("-");
                return `${MONTHS[+mm - 1]} ${+dd}`;
              };
              const bars = cycleRows.map((c) => ({
                label: fmtPay(c.payDate),
                value: +c.plan1.toFixed(2),
              }));
              let cum = 0;
              const lineRows = cycleRows.map((c) => {
                cum += c.plan1;
                return { label: fmtPay(c.payDate), value: +cum.toFixed(2) };
              });
              const top = bars.reduce((a, b) => (b.value > a.value ? b : a), bars[0]);
              const totalDeposits = cycleRows.reduce((s, c) => s + c.plan1, 0);
              const titleSuffix = cycleHcvMode === "hcv"
                ? " · Hepatitis"
                : cycleHcvMode === "non_hcv"
                  ? " · Pharmacy"
                  : "";
              const spokenSuffix = cycleHcvMode === "hcv"
                ? " hepatitis"
                : cycleHcvMode === "non_hcv"
                  ? " pharmacy"
                  : "";
              return {
                spoken: `${cycleRows.length}${spokenSuffix} O D B cycles totaling ${fmtMoneyShort(totalDeposits)}, peaking ${top.label} at ${fmtMoneyShort(top.value)}, sir.`,
                chart: {
                  type: "combo_bar_line",
                  title: (params.title || "ODB Cycle Deposits") + titleSuffix,
                  unit: "$",
                  bars,
                  line: lineRows,
                  caption: prettyRange,
                },
              };
            }
            // ============================================================
            //   hcv_vs_non_hcv — donut (revenue share)
            // ============================================================
            if (chartKey === "hcv_vs_non_hcv" || chartKey === "payer_mix") {
              let hcvSum = 0, nonHcvSum = 0;
              for (const t of inRange) {
                const v = combinedAmountForRow(t);
                if (isHcvDrug(t.drug_name)) hcvSum += v;
                else nonHcvSum += v;
              }
              const total = hcvSum + nonHcvSum;
              if (total <= 0) return { spoken: "No revenue to break down, sir." };
              const hcvPct = (hcvSum / total) * 100;
              return {
                spoken: `H C V holds ${hcvPct.toFixed(0)} percent of revenue, sir.`,
                chart: {
                  type: "donut",
                  title: params.title || "HCV vs Non-HCV",
                  unit: "$",
                  data: [
                    { label: "HCV", value: hcvSum, color: "#00E5FF" },
                    { label: "Non-HCV", value: nonHcvSum, color: "#FF8AC0" },
                  ],
                  caption: prettyRange,
                },
              };
            }
            // ============================================================
            //   top_drugs — top_n_bar
            // ============================================================
            if (chartKey === "top_drugs" || chartKey === "best_sellers") {
              const byDrug = new Map();
              const unitsByDrug = new Map();
              for (const t of scoped) {
                const key = canonicalDrugName(t.drug_name || "");
                if (!key) continue;
                byDrug.set(key, (byDrug.get(key) || 0) + combinedAmountForRow(t));
                unitsByDrug.set(key, (unitsByDrug.get(key) || 0) + 1);
              }
              const rows = [...byDrug.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit);
              if (!rows.length) return { spoken: "No drug data in that range, sir." };
              return {
                spoken: `${rows[0][0]} leads at ${fmtMoneyShort(rows[0][1])}, sir.`,
                chart: {
                  type: "top_n_bar",
                  title: params.title || `Top ${rows.length} Drugs by Revenue`,
                  unit: "$",
                  data: rows.map(([name, v]) => ({
                    label: name,
                    value: v,
                    sublabel: `${unitsByDrug.get(name) || 0} dispensing${unitsByDrug.get(name) === 1 ? "" : "s"}`,
                  })),
                  caption: prettyRange,
                },
              };
            }
            // ============================================================
            //   top_patients — top_n_bar (by professional fees)
            // ============================================================
            if (chartKey === "top_patients" || chartKey === "top_clients") {
              const byPatient = new Map();
              const countByPatient = new Map();
              for (const t of scoped) {
                const p = t.patient || "";
                if (!p) continue;
                byPatient.set(p, (byPatient.get(p) || 0) + (t.fees || 0));
                countByPatient.set(p, (countByPatient.get(p) || 0) + 1);
              }
              const rows = [...byPatient.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit);
              if (!rows.length) return { spoken: "No patient data in that range, sir." };
              return {
                spoken: `${rows[0][0].split(",")[0]} tops the list at ${fmtMoneyShort(rows[0][1])} in fees, sir.`,
                chart: {
                  type: "top_n_bar",
                  title: params.title || `Top ${rows.length} Patients by Fees`,
                  unit: "$",
                  data: rows.map(([name, v]) => ({
                    label: name,
                    value: v,
                    sublabel: `${countByPatient.get(name) || 0} dispensings`,
                  })),
                  caption: prettyRange,
                },
              };
            }
            // ============================================================
            //   stat cards — revenue / profit / dispensings / patients
            // ============================================================
            const statSpec = (() => {
              if (chartKey === "revenue_stat") {
                const v = scoped.reduce((s, t) => s + combinedAmountForRow(t), 0);
                return { title: "Total Revenue", value: v, unit: "$", subtitle: prettyRange };
              }
              if (chartKey === "profit_stat") {
                const rev = scoped.reduce((s, t) => s + combinedAmountForRow(t), 0);
                let hcvCost = 0;
                for (const t of scoped) {
                  if (!isHcvDrug(t.drug_name)) continue;
                  hcvCost += unitsPerClaimForPayer(classifyPayer(t)) * HCV_COST_PER_UNIT;
                }
                // Operating cost proportional to date span.
                const days = Math.max(1, Math.round((new Date(end) - new Date(start)) / 86400000) + 1);
                const opCost = (25000 / 30) * days;
                const profit = rev - hcvCost - opCost;
                return { title: "Total Profit", value: profit, unit: "$", subtitle: prettyRange };
              }
              if (chartKey === "dispensings_stat") {
                return { title: "Dispensings", value: scoped.length, unit: "", subtitle: prettyRange };
              }
              if (chartKey === "patients_stat") {
                const set = new Set(scoped.map((t) => t.patient).filter(Boolean));
                return { title: "Unique Patients", value: set.size, unit: "", subtitle: prettyRange };
              }
              return null;
            })();
            if (statSpec) {
              // Delta vs the immediately-preceding period of the same length.
              const ms = new Date(end) - new Date(start);
              const prevEnd = new Date(new Date(start) - 86400000);
              const prevStart = new Date(prevEnd.getTime() - ms);
              const fmtISO = (d) => d.toISOString().slice(0, 10);
              const prev = allTx.filter(
                (t) => t.service_date >= fmtISO(prevStart) && t.service_date <= fmtISO(prevEnd),
              );
              const prevScoped = requestedView === "hcv"
                ? prev.filter((t) => isHcvDrug(t.drug_name))
                : requestedView === "non-hcv"
                  ? prev.filter((t) => !isHcvDrug(t.drug_name))
                  : prev;
              let prevValue = 0;
              if (chartKey === "revenue_stat") prevValue = prevScoped.reduce((s, t) => s + combinedAmountForRow(t), 0);
              else if (chartKey === "dispensings_stat") prevValue = prevScoped.length;
              else if (chartKey === "patients_stat") prevValue = new Set(prevScoped.map((t) => t.patient).filter(Boolean)).size;
              else if (chartKey === "profit_stat") {
                const rev = prevScoped.reduce((s, t) => s + combinedAmountForRow(t), 0);
                let hcvCost = 0;
                for (const t of prevScoped) {
                  if (!isHcvDrug(t.drug_name)) continue;
                  hcvCost += unitsPerClaimForPayer(classifyPayer(t)) * HCV_COST_PER_UNIT;
                }
                const days = Math.max(1, Math.round(ms / 86400000) + 1);
                prevValue = rev - hcvCost - (25000 / 30) * days;
              }
              const delta = prevValue ? ((statSpec.value - prevValue) / Math.abs(prevValue)) * 100 : null;
              const spokenVal = statSpec.unit === "$" ? fmtMoneyShort(statSpec.value) : statSpec.value.toLocaleString();
              return {
                spoken: `${statSpec.title}: ${spokenVal}, sir.`,
                chart: {
                  type: "stat",
                  title: statSpec.title,
                  value: statSpec.value,
                  unit: statSpec.unit,
                  delta: delta == null || !Number.isFinite(delta) ? undefined : delta,
                  subtitle: statSpec.subtitle,
                },
              };
            }
            return { spoken: "I'm not sure what to chart, sir." };
          }
          if (action === "report_metric" && params.metric) {
            // Helper: scroll to a section AND auto-expand its accordion so
            // the user sees the actual data, not just a collapsed header.
            const navigateTo = (testid) => {
              const el = document.querySelector(`[data-testid="${testid}"]`);
              if (!el) return;
              const toggle = el.querySelector(`[data-testid="${testid}-toggle"]`);
              if (toggle && toggle.getAttribute("aria-expanded") === "false") {
                toggle.click();
              }
              requestAnimationFrame(() => {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
              });
            };
            // Priority for range/view: explicit params → just-set-by-jarvis
            // ref (synchronous, beats stale React state) → customRange →
            // full report range.
            const start = params.start || jarvisIntentRef.current.start || customRange?.start || date_range_start;
            const end = params.end || jarvisIntentRef.current.end || customRange?.end || date_range_end;
            const requestedView = (params.view_mode || jarvisIntentRef.current.viewMode || "").toLowerCase().replace(/_/g, "-");
            const isConsultant = String(params.metric).startsWith("consultant_");
            const effectiveView = isConsultant
              ? "hcv"
              : (requestedView === "hcv" || requestedView === "non-hcv" ? requestedView : viewMode);
            const inRange = (transactions || []).filter((t) => {
              return t.service_date >= start && t.service_date <= end;
            });
            let rowsForFees = inRange;
            if (effectiveView === "hcv") {
              // Mirror visibleTransactionsForFees: keep one fee per patient,
              // the largest, zero out the rest.
              const maxFee = new Map();
              for (const t of inRange) {
                const cur = maxFee.get(t.patient) || 0;
                if ((t.fees || 0) > cur) maxFee.set(t.patient, t.fees || 0);
              }
              const used = new Set();
              rowsForFees = inRange.map((t) => {
                const top = maxFee.get(t.patient) || 0;
                if (top === 0) return t;
                if (!used.has(t.patient) && (t.fees || 0) === top) {
                  used.add(t.patient);
                  return t;
                }
                return { ...t, fees: 0 };
              });
            }
            // Auto-align dashboard state so the user visually sees the range
            // the metric was computed against.
            if (params.start && params.end) {
              setCustomRange({ start: params.start, end: params.end });
              setSelectedMonth("all");
              setSelectedDate("all");
            }
            if (effectiveView === "hcv" && viewMode !== "hcv") {
              setViewMode("hcv");
            }
            // Compute ODB cycles spanning this metric request's range so the
            // per-cycle NIHB cap math is applied (matches dashboard semantics).
            const widenForCycles = (iso, days) => {
              if (!iso) return iso;
              const d = new Date(`${iso}T00:00:00Z`);
              d.setUTCDate(d.getUTCDate() + days);
              return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
            };
            const rangeCycles = start && end
              ? odbCyclesInRange(widenForCycles(start, -30), widenForCycles(end, 30))
              : [];
            const fmt = (n) => `$${(Math.round((n || 0) * 100) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            const prettyRange = `${start} to ${end}`;
            const metric = String(params.metric);
            if (metric === "consultant_medical") {
              const v = aggregateConsultantFeesPerCycle(rowsForFees, rangeCycles, patientPayerMap).medical;
              setConsultantDrill({ type: "medical" });
              return `Medical consultant payout for ${prettyRange}: ${fmt(v)}, sir.`;
            }
            if (metric === "consultant_sales") {
              const v = aggregateConsultantFeesPerCycle(rowsForFees, rangeCycles, patientPayerMap).sales;
              setConsultantDrill({ type: "sales" });
              return `Sales consultant payout for ${prettyRange}: ${fmt(v)}, sir.`;
            }
            if (metric === "consultant_np") {
              const v = aggregateConsultantFeesPerCycle(rowsForFees, rangeCycles, patientPayerMap).np;
              setConsultantDrill({ type: "np" });
              return `NP consultant payout for ${prettyRange}: ${fmt(v)}, sir.`;
            }
            if (metric === "consultant_milestone") {
              const v = aggregateMilestoneIncentives(rowsForFees).total;
              setConsultantDrill({ type: "milestone" });
              return `Milestone incentives for ${prettyRange}: ${fmt(v)}, sir.`;
            }
            if (metric === "consultant_total") {
              const c = aggregateConsultantFeesPerCycle(rowsForFees, rangeCycles, patientPayerMap);
              const m = aggregateMilestoneIncentives(rowsForFees).total;
              const total = (c.medical || 0) + (c.sales || 0) + (c.np || 0) + (m || 0);
              return `Total consultant + milestone payouts for ${prettyRange}: ${fmt(total)}, sir.`;
            }
            if (metric === "top_line_paid") {
              const plan1 = inRange.reduce((s, t) => s + (t.plan1_paid || 0), 0);
              const plan2 = inRange.reduce((s, t) => s + (t.plan2_paid || 0), 0);
              const total = plan1 + plan2;
              navigateTo("top-line-sales-collapse");
              return `Top-line billed for ${prettyRange}: ${fmt(total)} (Plan 1 ${fmt(plan1)} + Plan 2 ${fmt(plan2)}), sir.`;
            }
            if (metric === "odb_deposit" || metric === "nihb_deposit") {
              // Find every ODB cycle whose payDate falls inside the user's
              // window (pay_date for a single deposit, or start/end for a
              // multi-cycle range).
              const wantStart = params.pay_date || start;
              const wantEnd = params.pay_date || end;
              // Look back/forward enough to catch every cycle that could pay
              // inside [wantStart, wantEnd]. A cycle's dispensings finish up
              // to ~22 days before its pay date, so widen by ~60 days on
              // either side to be safe.
              const widen = (iso, days) => {
                const d = new Date(`${iso}T00:00:00Z`);
                d.setUTCDate(d.getUTCDate() + days);
                return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
              };
              const cycles = odbCyclesInRange(widen(wantStart, -60), widen(wantEnd, 60));
              const matching = cycles.filter((c) =>
                c.payDate >= wantStart && c.payDate <= wantEnd
              );
              if (!matching.length) {
                return `No ODB cycle deposits fall on ${prettyRange}, sir.`;
              }
              // Bucket every in-range transaction by patient → classifier so
              // we can split each cycle's Plan 1 paid into ODB vs NIHB.
              const allTx = transactions || [];
              const txByPatient = new Map();
              for (const t of allTx) {
                const arr = txByPatient.get(t.patient) || [];
                arr.push(t);
                txByPatient.set(t.patient, arr);
              }
              const payerByPatient = new Map();
              for (const [pt, arr] of txByPatient) {
                payerByPatient.set(pt, classifyPayer(arr));
              }
              let odbSum = 0;
              let nihbSum = 0;
              const cycleLines = [];
              for (const c of matching) {
                let cOdb = 0;
                let cNihb = 0;
                for (const t of allTx) {
                  if (t.service_date < c.dispenseStart) continue;
                  if (t.service_date > c.dispenseEnd) continue;
                  const isNihb = payerByPatient.get(t.patient) === "NIHB";
                  const v = t.plan1_paid || 0;
                  if (isNihb) cNihb += v; else cOdb += v;
                }
                odbSum += cOdb;
                nihbSum += cNihb;
                cycleLines.push(`${c.shortLabel} pays ${formatPayDate(c.payDate)}`);
              }
              // Open the drug payments / ODB deposits section so the user
              // sees the cycle cards.
              navigateTo("odb-cycles-collapse");
              if (metric === "odb_deposit") {
                const labelDate = params.pay_date ? formatPayDate(params.pay_date) : prettyRange;
                if (matching.length === 1) {
                  return `ODB deposit for ${labelDate}: ${fmt(odbSum)}, sir. ${cycleLines[0]}.`;
                }
                return `ODB deposits totalling ${fmt(odbSum)} across ${matching.length} cycle${matching.length === 1 ? "" : "s"} in ${labelDate}, sir.`;
              }
              // nihb_deposit
              const labelDate = params.pay_date ? formatPayDate(params.pay_date) : prettyRange;
              if (matching.length === 1) {
                return `NIHB deposit for ${labelDate}: ${fmt(nihbSum)}, sir. ${cycleLines[0]}.`;
              }
              return `NIHB deposits totalling ${fmt(nihbSum)} across ${matching.length} cycle${matching.length === 1 ? "" : "s"} in ${labelDate}, sir.`;
            }
            if (metric === "total_revenue") {
              const fees = rowsForFees.reduce((s, t) => s + (t.fees || 0), 0);
              navigateTo("profitability-collapse");
              return `Dispensing-fee revenue for ${prettyRange}: ${fmt(fees)}, sir.`;
            }
            if (metric === "total_profit") {
              // Approximate profit = visible fees minus pro-rated operating
              // cost for the window. For HCV, this is a rough figure since
              // full P&L logic involves rebates/cost spreads — but it's a
              // useful directional answer.
              const fees = rowsForFees.reduce((s, t) => s + (t.fees || 0), 0);
              navigateTo("profitability-collapse");
              return `Visible fee revenue for ${prettyRange}: ${fmt(fees)} (see P&L for full profit breakdown), sir.`;
            }
            if (metric === "patients") {
              const uniq = new Set(rowsForFees.map((t) => t.patient)).size;
              return `${uniq} unique clients in ${prettyRange}, sir.`;
            }
            if (metric === "dispensings") {
              return `${rowsForFees.length.toLocaleString()} dispensings in ${prettyRange}, sir.`;
            }
            if (metric === "zero_fee_count") {
              const n = inRange.reduce((s, t) => s + ((t.fees || 0) === 0 ? 1 : 0), 0);
              return `${n.toLocaleString()} zero-fee dispensings in ${prettyRange}, sir.`;
            }
            if (metric === "monthly_projection") {
              const fees = (transactions || []).reduce((s, t) => s + (t.fees || 0), 0);
              const days = Math.max(num_days, 1);
              const monthly = (fees / days) * 30;
              return `Projected monthly fee revenue: ${fmt(monthly)} based on the current daily average, sir.`;
            }
            if (metric === "annual_projection") {
              const fees = (transactions || []).reduce((s, t) => s + (t.fees || 0), 0);
              const days = Math.max(num_days, 1);
              const annual = (fees / days) * 365;
              return `Projected annual fee revenue: ${fmt(annual)} based on the current daily average, sir.`;
            }
            // Drug-specific and HCV-economics metrics. Normalize the drug
            // hint so 'maviret', 'Maviret', 'MAVIRET' all match.
            const drugHint = (params.drug || "").toString().toUpperCase().trim();
            const matchesDrug = (t) => {
              if (!drugHint) return true;
              const dn = (t.drug_name || "").toUpperCase();
              return dn.includes(drugHint);
            };
            const drugFilteredAll = inRange.filter(matchesDrug);
            const drugFilteredFees = rowsForFees.filter(matchesDrug);
            if (metric === "drug_units") {
              if (!drugHint) return "Which drug, sir? Please name it.";
              const hcvKey = HCV_DRUG_KEYWORDS.find((k) => drugHint.includes(k));
              if (hcvKey) {
                const txByPatient = new Map();
                for (const t of drugFilteredAll) {
                  const arr = txByPatient.get(t.patient) || [];
                  arr.push(t);
                  txByPatient.set(t.patient, arr);
                }
                let units = 0;
                for (const [, arr] of txByPatient) {
                  const payer = classifyPayer(arr);
                  if (payer === "NIHB") {
                    units += 1;
                  } else {
                    units += arr.length * unitsPerClaimForPayer("ODB");
                  }
                }
                return `${(+units.toFixed(2)).toLocaleString()} bottle${units === 1 ? "" : "s"} of ${hcvKey} in ${prettyRange}, sir.`;
              }
              return `${drugFilteredAll.length.toLocaleString()} dispensings of ${drugHint} in ${prettyRange}, sir.`;
            }
            if (metric === "drug_revenue") {
              if (!drugHint) return "Which drug, sir? Please name it.";
              const r = drugFilteredFees.reduce((s, t) => s + (t.fees || 0), 0);
              return `Dispensing-fee revenue from ${drugHint} in ${prettyRange}: ${fmt(r)}, sir.`;
            }
            if (metric === "drug_top_line") {
              if (!drugHint) return "Which drug, sir? Please name it.";
              const p1 = drugFilteredAll.reduce((s, t) => s + (t.plan1_paid || 0), 0);
              const p2 = drugFilteredAll.reduce((s, t) => s + (t.plan2_paid || 0), 0);
              const combined = drugFilteredAll.reduce((s, t) => s + combinedAmountForRow(t), 0);
              const pspOffset = drugFilteredAll.reduce((s, t) => s + (isPspRow(t) ? pspAmountForRow(t) : 0), 0);
              navigateTo("top-line-sales-collapse");
              if (pspOffset > 0) {
                return `Top-line billed for ${drugHint} in ${prettyRange}: ${fmt(combined)} (Plan 1 ${fmt(p1)} less ${fmt(pspOffset)} PSP), sir.`;
              }
              return `Top-line billed for ${drugHint} in ${prettyRange}: ${fmt(combined)} (Plan 1 ${fmt(p1)} + Plan 2 ${fmt(p2)}), sir.`;
            }
            if (metric === "drug_patients") {
              if (!drugHint) return "Which drug, sir? Please name it.";
              const uniq = new Set(drugFilteredAll.map((t) => t.patient)).size;
              return `${uniq} unique patients on ${drugHint} in ${prettyRange}, sir.`;
            }
            if (metric === "hcv_rebate") {
              const src = drugHint ? drugFilteredFees : rowsForFees;
              const r = aggregateHcvRebate(src);
              navigateTo("profitability-collapse");
              const drugLabel = drugHint ? ` from ${drugHint}` : "";
              return `Manufacturer rebate${drugLabel} for ${prettyRange}: ${fmt(r)}, sir.`;
            }
            if (metric === "hcv_drug_cost") {
              const src = drugHint ? drugFilteredFees : rowsForFees;
              const txByPatient = new Map();
              for (const t of src) {
                if (!hcvDrugKey(t.drug_name)) continue;
                const arr = txByPatient.get(t.patient) || [];
                arr.push(t);
                txByPatient.set(t.patient, arr);
              }
              let cost = 0;
              for (const [, arr] of txByPatient) {
                const payer = classifyPayer(arr);
                const byDrug = new Map();
                for (const t of arr) {
                  const k = hcvDrugKey(t.drug_name);
                  byDrug.set(k, (byDrug.get(k) || []).concat([t]));
                }
                for (const [, drows] of byDrug) {
                  if (payer === "NIHB") {
                    cost += HCV_COST_PER_UNIT;
                  } else {
                    cost += drows.length * unitsPerClaimForPayer("ODB") * HCV_COST_PER_UNIT;
                  }
                }
              }
              navigateTo("profitability-collapse");
              const drugLabel = drugHint ? ` for ${drugHint}` : "";
              return `HCV drug cost${drugLabel} in ${prettyRange}: ${fmt(cost)} (at $20,000/bottle), sir.`;
            }
            return null;
          }
          if (action === "unsupported") {
            return null; // Let backend's spoken reply through.
          }
          // General-assistant actions — work even with no PDF loaded.
          if (action === "chat") {
            // The LLM already wrote its witty reply in the `spoken` field;
            // returning null lets that flow through to TTS unchanged.
            return null;
          }
          if (action === "get_time") {
            // Device-local time, formatted as "Monday, 14:32" + timezone name.
            const now = new Date();
            const day = now.toLocaleDateString(undefined, { weekday: "long" });
            const time = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "your timezone";
            return `It is ${time} on ${day}, sir — ${tz}.`;
          }
          if (action === "get_location") {
            try {
              const res = await fetch("https://ipapi.co/json/");
              const d = await res.json();
              const city = d.city || d.region || "an unknown location";
              const region = d.region || "";
              const country = d.country_name || d.country || "";
              const where = [city, region, country].filter(Boolean).join(", ");
              return `You are in ${where}, sir. Pleasant spot, I trust.`;
            } catch {
              return "I'm afraid I couldn't reach the location service, sir.";
            }
          }
          if (action === "get_weather") {
            try {
              // 1) Resolve location (IP-based)
              const locRes = await fetch("https://ipapi.co/json/");
              const loc = await locRes.json();
              const lat = loc.latitude;
              const lon = loc.longitude;
              const city = loc.city || "your location";
              if (lat == null || lon == null) {
                return "I'm afraid I couldn't pinpoint you on the map, sir.";
              }
              // 2) Current weather from Open-Meteo
              const wxUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code,wind_speed_10m,relative_humidity_2m&temperature_unit=celsius&wind_speed_unit=kmh`;
              const wxRes = await fetch(wxUrl);
              const wx = await wxRes.json();
              const c = wx?.current || {};
              const temp = c.temperature_2m;
              const wind = c.wind_speed_10m;
              const humidity = c.relative_humidity_2m;
              // Map WMO weather code → human description.
              const wmoMap = {
                0: "clear", 1: "mostly clear", 2: "partly cloudy", 3: "overcast",
                45: "foggy", 48: "rimy fog",
                51: "drizzling", 53: "drizzling", 55: "drizzling heavily",
                61: "light rain", 63: "rain", 65: "heavy rain",
                71: "light snow", 73: "snow", 75: "heavy snow",
                77: "snow grains", 80: "rain showers", 81: "rain showers",
                82: "heavy rain showers", 85: "snow showers", 86: "snow showers",
                95: "a thunderstorm", 96: "thunderstorm with hail", 99: "thunderstorm with heavy hail",
              };
              const cond = wmoMap[c.weather_code] || "indeterminate conditions";
              if (temp == null) return `Weather data unavailable for ${city}, sir.`;
              return `In ${city} it is ${Math.round(temp)}°C with ${cond}, ${humidity}% humidity, wind ${Math.round(wind)} km/h, sir.`;
            } catch {
              return "Weather service is unreachable at the moment, sir.";
            }
          }
          return null;
        }}
      />
      )}
    </div>
  );
}
